import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Collapse,
  Card,
  CardHeader,
  CardContent,
  ListItemSecondaryAction,
  Select,
  MenuItem,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Radio,
  RadioGroup,
  Grid
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  AccessTime,
  ExpandMore,
  ExpandLess,
  Assignment,
  AssignmentTurnedIn,
  Download,
  Delete,
  Schedule,
  Upload,
  Add,
  Edit,
  AttachMoney,
  Email,
  OpenInNew as OpenInNewIcon,
  Visibility,
  PictureAsPdf,
  Close as CloseIcon
} from '@mui/icons-material';
import { SupervisorAccount, CloudUpload } from '@mui/icons-material';
import StageFiles from './StageFiles';
import WorkflowStageComments from './WorkflowStageComments';
import WorkflowTaskComments from './WorkflowTaskComments';
import TaskReminderDialog from './TaskReminderDialog';
import OpenScheduleCard from './OpenScheduleCard';
import MarketAppraisalConfirmDialog from './MarketAppraisalConfirmDialog';
import type { MarketAppraisalInputs } from '../types/marketAppraisal';
import { prepareAppraisalInputs } from '../utils/prepareAppraisalInputs';
import { NumericFormat } from 'react-number-format';
import Autocomplete from '@mui/material/Autocomplete';
import calendarService from '../services/calendarService';
import { getContractors, type Contractor } from '../services/contractorService';
import { getTaskReminders, deleteTaskReminder } from '../services/notificationService';
import type { TaskReminder } from '../types/notification';
import { assignContractor, getPropertyJobs, cancelJob } from '../services/contractorJobService';
import type { ContractorJob } from '../types/contractorJob';
import { skipStage as skipWorkflowStage } from '../services/workflowService';
import { acceptContractorQuote, declineContractorQuote } from '../services/quoteManagementService';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { updateProperty, uploadAgencyAgreement, uploadRentalManagementAgreement } from '../services/propertyService';
import { listStageFiles, downloadStageFile, downloadFileAsBlob } from '../services/fileService';
import openScheduleService, { type OpenSchedule } from '../services/openScheduleService';
import socketService from '../services/socketService';
import type { Property } from '../types/property';
import { getListingValidationErrorMessage } from '../utils/listingValidation';
import { getErrorMessage, getOperationErrorMessage } from '../utils/errorMessages';
import { updateSoldDetails, updateAuctionOutcome } from '../services/propertyService';
import { AgencyAgreementTermsDialog } from './AgencyAgreementTermsDialog';
import { hasAdminAccess } from '../utils/roleUtils';

type MissingAgreementPrerequisite = {
  field: 'agencyAbn' | 'agencyLicenseNumber' | 'agentLicenseNumber' | 'agentSignature';
  label: string;
  location: 'settings' | 'profile';
};

interface Task {
  name: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed';
  completedAt?: string;
  estimatedDuration: number;
  actualDuration?: number;
  taskType?: string;
  assignedTo?: string;
  dueDate?: string;
  requiredFields?: Array<{
    name: string;
    type: string;
    label: string;
    required: boolean;
  }>;
  fieldValues?: Record<string, any>;
}

interface Stage {
  _id?: string;
  stageId: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed';
  startedAt?: string;
  completedAt?: string;
  skippable?: boolean;
  tasks: Task[];
}

interface PropertyWorkflowProps {
  stages: Stage[];
  currentStage: number;
  onTaskStatusChange?: (stageIndex: number, taskIndex: number, newStatus: Task['status'], workflowId?: string, fieldValues?: any) => void;
  agencySlug: string;
  propertyId: string;
  workflowId?: string;
  onTaskPatched?: (options?: { skipFiles?: boolean; skipWorkflows?: boolean; skipProperty?: boolean }) => void;
  priceRange?: { min: number; max: number };
  editable?: boolean;
  propertyStylingCompanyName?: string;
  propertyStatus?: string; // Add property status prop
  propertySaleType?: string; // Property sale type (sale, auction, rental)
  consultationReportExists?: boolean; // Check if report was already generated
  consultationReportUrl?: string; // URL to existing report
  consultationReportSentAt?: string | Date | null; // When the report was sent to vendor
  consultationReportPresentFindingsCompleted?: boolean; // Whether "Present Findings" task is completed
  refreshKey?: number; // external signal to refresh stage file lists
  ownerEmail?: string; // Property owner email for pre-populating vendor email
  consultationVendorResponse?: string; // Fallback from property.consultationReport
  consultationVendorResponseDate?: string; // Fallback date
  propertyListingUrls?: { realestateUrl?: string; domainUrl?: string }; // Property listing URLs for display
  property?: Property; // Full property object for validation
}

const PropertyWorkflow: React.FC<PropertyWorkflowProps> = ({
  stages,
  currentStage,
  onTaskStatusChange,
  agencySlug,
  propertyId,
  workflowId,
  onTaskPatched,
  priceRange,
  editable = true,
  propertyStylingCompanyName,
  propertyStatus,
  propertySaleType,
  consultationReportExists,
  consultationReportUrl,
  consultationReportSentAt,
  consultationReportPresentFindingsCompleted,
  refreshKey,
  ownerEmail,
  consultationVendorResponse,
  consultationVendorResponseDate,
  propertyListingUrls,
  property
}) => {
  const { agency, user } = useAuth();
  const navigate = useNavigate();
  
  // Check if contractor job feature is available (not on Free plan)
  // Use subscription object if available, fallback to legacy field
  const currentPlan = agency?.subscription?.planName || agency?.subscriptionPlan;
  const canUseContractorJobs = currentPlan !== 'trial' && !['Free', 'Agency Free'].includes(currentPlan ?? '');
  
  // Check if realestate.com.au and domain.com.au configs exist (for subscribed plans)
  const [hasRealestateConfig, setHasRealestateConfig] = React.useState<boolean | null>(null);
  const [hasDomainConfig, setHasDomainConfig] = React.useState<boolean | null>(null);
  const [realestateConfigWarning, setRealestateConfigWarning] = React.useState<string | null>(null);
  React.useEffect(() => {
    const fetchConfigs = async () => {
      if (!agency?.slug) return;
      const isFreePlan = ['Free', 'Agency Free', 'trial', 'Trial'].includes(currentPlan ?? '');
      if (isFreePlan) {
        setHasRealestateConfig(false); // Not needed for free plan
        setHasDomainConfig(false);
        setRealestateConfigWarning(null);
        return;
      }
      const token = localStorage.getItem('token');
      
      // Check realestate config
      try {
        const realestateResponse = await fetch(`/api/agencies/${agency.slug}/realestate-config`, {
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });
        if (realestateResponse.ok) {
          const data = await realestateResponse.json();
          const config = data.config;
          // Config exists and has required agentId
          setHasRealestateConfig(!!(config && config.agentId));
          setRealestateConfigWarning(data.warning || null);
        } else {
          setHasRealestateConfig(false);
          setRealestateConfigWarning(null);
        }
      } catch (err) {
        console.error('Error checking realestate config:', err);
        setHasRealestateConfig(false);
        setRealestateConfigWarning(null);
      }
      
      // Check domain config - use domain-office-details to check connection status
      try {
        const domainResponse = await fetch(`/api/agencies/${agency.slug}/domain-office-details`, {
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });
        if (domainResponse.ok) {
          const data = await domainResponse.json();
          // Config exists and is connected (has accessToken and not disconnected)
          setHasDomainConfig(!!(data.connected && !data.needsReconnection));
        } else {
          setHasDomainConfig(false);
        }
      } catch (err) {
        console.error('Error checking domain config:', err);
        setHasDomainConfig(false);
      }
    };
    fetchConfigs();
  }, [agency?.slug, currentPlan]);
  
  // Initialize all stages as collapsed except the first
  const [expandedStages, setExpandedStages] = React.useState<Record<number, boolean>>({ 0: true });
  const [openTaskComments, setOpenTaskComments] = React.useState<Record<string, boolean>>({});
  const [currentTaskIndex, setCurrentTaskIndex] = React.useState<number | null>(null);
  const [currentStageIndex, setCurrentStageIndex] = React.useState<number | null>(null);
  const [openReportDialog, setOpenReportDialog] = React.useState(false);
  const [reportContent, setReportContent] = React.useState('');
  const [reportLoading, setReportLoading] = React.useState(false);
  const [reportPdfLoading, setReportPdfLoading] = React.useState(false);
  const [rpDataFileTypes, setRpDataFileTypes] = React.useState<Record<string, string>>({});
  const [reportError, setReportError] = React.useState<string | null>(null);
  const [reportSuccess, setReportSuccess] = React.useState<string | null>(null);
  const [reportStageIndex, setReportStageIndex] = React.useState<number | null>(null);
  const [generatedReportUrl, setGeneratedReportUrl] = React.useState<string | null>(null);
  const [consultationReportStatus, setConsultationReportStatus] = React.useState<'draft' | 'finalized' | null>(null);
  const [consultationReportContent, setConsultationReportContent] = React.useState<string>('');
  const [finalizingReport, setFinalizingReport] = React.useState(false);
  const [nextStepValue, setNextStepValue] = React.useState('');
  const [agentAgreementFile, setAgentAgreementFile] = React.useState<File | null>(null);
  const [agentAgreementUploading, setAgentAgreementUploading] = React.useState(false);
  const [agentAgreementFileId, setAgentAgreementFileId] = React.useState<string | null>(null);
  const [agentAgreementMethod, setAgentAgreementMethod] = React.useState<'generate' | 'upload'>('generate');
  const [rentalAgreementMethod, setRentalAgreementMethod] = React.useState<'generate' | 'upload'>('generate');
  const [rentalAgreementFile, setRentalAgreementFile] = React.useState<File | null>(null);
  const [rentalAgreementFileId, setRentalAgreementFileId] = React.useState<string | null>(null);
  const [generatedRentalAgreementFileId, setGeneratedRentalAgreementFileId] = React.useState<string | null>(null);
  const [rentalAgreementUploading, setRentalAgreementUploading] = React.useState(false);
  const [supportedStates, setSupportedStates] = React.useState<string[]>(['NSW']);

  // Fetch supported states for agency agreement generation
  React.useEffect(() => {
    const fetchSupportedStates = async () => {
      try {
        const response = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/agency-agreement/supported-states`);
        if (response.data?.supportedStates) {
          setSupportedStates(response.data.supportedStates);
        }
      } catch (err) {
        console.warn('Failed to fetch supported states for agency agreement:', err);
        // Fallback to NSW if fetch fails
        setSupportedStates(['NSW']);
      }
    };
    
    if (agencySlug && propertyId) {
      fetchSupportedStates();
    }
  }, [agencySlug, propertyId]);

  // Check if state-compliant agency agreement is supported for this property
  const isAgentAgreementSupported = React.useMemo(() => {
    const raw = (property?.address?.state || '').trim().toUpperCase();
    const stateAliases: Record<string, string> = {
      'NEW SOUTH WALES': 'NSW',
      NSW: 'NSW',
      VICTORIA: 'VIC',
      VIC: 'VIC',
    };
    const state = stateAliases[raw] || raw;
    return supportedStates.includes(state);
  }, [property?.address?.state, supportedStates]);

  // Adjust default method based on support
  useEffect(() => {
    if (!isAgentAgreementSupported && agentAgreementMethod === 'generate') {
      setAgentAgreementMethod('upload');
    }
  }, [isAgentAgreementSupported, agentAgreementMethod]);
  const [generatingAgreement, setGeneratingAgreement] = React.useState(false);
  const [generatedAgreementFileId, setGeneratedAgreementFileId] = React.useState<string | null>(null);
  const [generatingFinalAgreement, setGeneratingFinalAgreement] = React.useState(false);
  const [generatingFinalRentalAgreement, setGeneratingFinalRentalAgreement] = React.useState(false);
  const [sendingAgreementEmail, setSendingAgreementEmail] = React.useState(false);
  const [agreementEmail, setAgreementEmail] = React.useState('');
  const [selectedVendorEmails, setSelectedVendorEmails] = React.useState<Set<string>>(new Set());
  const [showSendAgreementDialog, setShowSendAgreementDialog] = React.useState(false);
  const [sendAgreementType, setSendAgreementType] = React.useState<'agency' | 'rental_management' | null>(null);
  const [showLicenseNumberDialog, setShowLicenseNumberDialog] = React.useState(false);
  const [missingAgreementPrerequisites, setMissingAgreementPrerequisites] = React.useState<MissingAgreementPrerequisite[]>([]);
  const [showAgreementReviewDialog, setShowAgreementReviewDialog] = React.useState(false);
  const [rentalAgreementPreviewHtml, setRentalAgreementPreviewHtml] = React.useState<string | null>(null);
  const [showRentalAgreementPreviewDialog, setShowRentalAgreementPreviewDialog] = React.useState(false);
  const [rentalAgreementPdfUrl, setRentalAgreementPdfUrl] = React.useState<string | null>(null);
  const [showRentalAgreementPdfDialog, setShowRentalAgreementPdfDialog] = React.useState(false);
  const [generatingRentalAgreementPdf, setGeneratingRentalAgreementPdf] = React.useState(false);
  const [loadingRentalAgreementPreview, setLoadingRentalAgreementPreview] = React.useState(false);
  const [lastRentalAgreementPdfBlob, setLastRentalAgreementPdfBlob] = React.useState<Blob | null>(null);
  const [savingRentalAgreement, setSavingRentalAgreement] = React.useState(false);
  const [rentalAgreementEligibility, setRentalAgreementEligibility] = React.useState<{
    canGenerate: boolean;
    hasFreeGeneration: boolean;
    balanceCents: number;
    costCents: number;
    message?: string;
  } | null>(null);
  const [showRentalAgreementTermsDialog, setShowRentalAgreementTermsDialog] = React.useState(false);
  const [showRentalAgreementCreditDialog, setShowRentalAgreementCreditDialog] = React.useState(false);
  const [pendingRentalAfterTermsAccept, setPendingRentalAfterTermsAccept] = React.useState<(() => void) | null>(null);
  const [pendingRentalAgreementAction, setPendingRentalAgreementAction] = React.useState<(() => Promise<void>) | null>(null);
  const [uploadingRentalAgreement, setUploadingRentalAgreement] = React.useState(false);
  const [showRentalAgreementConfigDialog, setShowRentalAgreementConfigDialog] = React.useState(false);
  const [rentalAgreementConfig, setRentalAgreementConfig] = React.useState<{
    advertisingPortals: boolean;
    advertisingPhotography: boolean;
    advertisingSignboard: boolean;
    advertisingOther?: string;
    repairAuthLimit: number;
    statementsFrequency: 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'other';
    disbursementsCouncil: boolean;
    disbursementsWater: boolean;
    disbursementsStrata: boolean;
    disbursementsMaintenance: boolean;
    disbursementsOther: boolean;
    terminationNoticeDays: number;
  }>({
    advertisingPortals: true,
    advertisingPhotography: true,
    advertisingSignboard: true,
    advertisingOther: '',
    repairAuthLimit: 500,
    statementsFrequency: 'monthly',
    disbursementsCouncil: true,
    disbursementsWater: true,
    disbursementsStrata: true,
    disbursementsMaintenance: true,
    disbursementsOther: true,
    terminationNoticeDays: 30,
  });
  const [showMissingFilesDialog, setShowMissingFilesDialog] = React.useState(false);
  const [missingFilesList, setMissingFilesList] = React.useState<string[]>([]);
  const [showAppraisalConfirmDialog, setShowAppraisalConfirmDialog] = React.useState(false);
  const [appraisalInputs, setAppraisalInputs] = React.useState<MarketAppraisalInputs | null>(null);
  const [loadingAppraisalInputs, setLoadingAppraisalInputs] = React.useState(false);
  const [hasStructuredInputs, setHasStructuredInputs] = React.useState<boolean | null>(null);
  const [showAgreementGenerationDialog, setShowAgreementGenerationDialog] = React.useState(false);
  const [agreementStageIndex, setAgreementStageIndex] = React.useState<number | null>(null);
  const agreementStageIndexRef = useRef<number | null>(null);
  const generateAgreementRef = useRef<() => Promise<void>>(async () => {});
  const generateRentalAgreementRef = useRef<() => Promise<void>>(async () => {});
  const pendingAfterTermsAcceptRef = useRef<(() => void) | null>(null);
  const pendingRentalAfterTermsAcceptRef = useRef<(() => void) | null>(null);
  const [agreementFormData, setAgreementFormData] = React.useState<{
    marketingCosts: Array<{ item: string; supplier?: string; cost: number; payable: 'upfront' | 'settlement' }>;
    introducedPurchasersListDays: number;
  }>({
    marketingCosts: [],
    introducedPurchasersListDays: 14,
  });
  const [agreementReviewUrl, setAgreementReviewUrl] = React.useState<string | null>(null);
  const [agreementEligibility, setAgreementEligibility] = React.useState<{
    canGenerate: boolean;
    hasFreeGeneration: boolean;
    balanceCents: number;
    costCents: number;
    message?: string;
    missingPrerequisites?: MissingAgreementPrerequisite[];
  } | null>(null);
  const [showCreditConfirmationDialog, setShowCreditConfirmationDialog] = React.useState(false);
  const [showAgencyAgreementTermsDialog, setShowAgencyAgreementTermsDialog] = React.useState(false);
  const [pendingAfterTermsAccept, setPendingAfterTermsAccept] = React.useState<(() => void) | null>(null);
  const [showNoResponseConfirmationDialog, setShowNoResponseConfirmationDialog] = React.useState(false);
  const [pendingNextStepTask, setPendingNextStepTask] = React.useState<{ stageIndex: number; taskIndex: number } | null>(null);
  const [pendingAgreementAction, setPendingAgreementAction] = React.useState<(() => void) | null>(null);
  const [informationSheetFile, setInformationSheetFile] = React.useState<File | null>(null);
  const [informationSheetUploading, setInformationSheetUploading] = React.useState(false);
  const [informationSheetFileId, setInformationSheetFileId] = React.useState<string | null>(null);
  const [auctionContractFile, setAuctionContractFile] = React.useState<File | null>(null);
  const [auctionContractUploading, setAuctionContractUploading] = React.useState(false);
  const [auctionContractFileId, setAuctionContractFileId] = React.useState<string | null>(null);
  const [contractFileByStage, setContractFileByStage] = React.useState<Record<number, { id: string; name: string } | null>>({});
  const [contractUploadFile, setContractUploadFile] = React.useState<File | null>(null);
  const [contractUploading, setContractUploading] = React.useState(false);
  const [contractUploadByTask, setContractUploadByTask] = React.useState<Record<string, { fileName: string; key: string } | null>>({});
  const [loading, setLoading] = React.useState(false);
  const [auctionOutcome, setAuctionOutcome] = React.useState<string>('');
  const [auctionFinalPrice, setAuctionFinalPrice] = React.useState<string>('');
  const [auctionNotes, setAuctionNotes] = React.useState<string>('');
  const [auctionMaxBid, setAuctionMaxBid] = React.useState<string>('');
  const [auctionOutcomeDate, setAuctionOutcomeDate] = React.useState<string>('');
  const [showSoldPrice, setShowSoldPrice] = React.useState<boolean>(true);
  const [auctionDecision, setAuctionDecision] = React.useState<string>('');
  const [dialogPriceRange, setDialogPriceRange] = React.useState({ minPrice: '', maxPrice: '' });
  const [dialogRentRange, setDialogRentRange] = React.useState({ minWeeklyRent: '', maxWeeklyRent: '' });
  const [dialogWeeklyRent, setDialogWeeklyRent] = React.useState('');
  const [dialogDateAvailable, setDialogDateAvailable] = React.useState('');
  const [dialogManagementFees, setDialogManagementFees] = React.useState({
    managementFeePercent: '',
    lettingFeeWeeks: '',
    leaseRenewalFeePercent: '',
    inspectionFeeNote: '',
  });
  const [saleTypeValue, setSaleTypeValue] = React.useState('');
  const [listingStates, setListingStates] = React.useState<Record<string, {
    realestateUrl: string;
    domainUrl: string;
    isPosting: boolean;
    listingPriceDisplay?: 'exact' | 'range' | 'contact';
    listingPriceDisplayText?: string;
    listingAuthority?: 'auction' | 'sale' | 'setsale' | 'exclusive' | 'multilist' | 'conjunctional' | 'open';
    listingStatus?: 'active' | 'under_offer' | 'sold' | 'off_market' | 'deleted';
    listingHeadline?: string;
    setSaleDate?: string;
    videoLink?: string;
    descriptionDraft?: string | null;
    descriptionSaving?: boolean;
    generatingAIDescription?: boolean;
  }>>({});

// Derive a concise listing headline from a longer description (used in listing task)
const deriveHeadlineFromDescription = (description: string): string => {
  const clean = (description || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';

  const sentenceEndMatch = clean.match(/[.!?]\s/);
  let base = sentenceEndMatch ? clean.slice(0, sentenceEndMatch.index! + 1) : clean;

  const MAX_LENGTH = 120;
  if (base.length > MAX_LENGTH) {
    base = base.slice(0, MAX_LENGTH);
    const lastSpace = base.lastIndexOf(' ');
    if (lastSpace > 40) {
      base = base.slice(0, lastSpace);
    }
  }

  return base.trim();
};

  // Prefetch contract files per stage so we can show status and download
  React.useEffect(() => {
    const fetchContracts = async () => {
      if (!workflowId) return;
      const map: Record<number, { id: string; name: string } | null> = {};
      for (let sIdx = 0; sIdx < stages.length; sIdx++) {
        try {
          const data = await listStageFiles(agencySlug, propertyId, sIdx, workflowId);
          const list = Array.isArray(data) ? data : (data.files || []);
          const found = list.find((f: any) => f.type === 'auction_contract' || f.type === 'pre_auction_contract');
          map[sIdx] = found ? { id: found._id, name: found.fileName } : null;
        } catch {}
      }
      setContractFileByStage(map);
    };
    fetchContracts();
  }, [workflowId, stages.length, agencySlug, propertyId, refreshKey]);

  // Refresh contract file map when posting status polling is active
  // (but avoid extra workflow/property refreshes)
  
  // Sync listing input states with URLs from task.fieldValues or property.listingUrls
  React.useEffect(() => {
    if (!stages || !Array.isArray(stages)) return;
    
    setListingStates(prev => {
      const updated: typeof prev = { ...prev };
      
      // Find all creating_listing tasks and sync their URLs
      stages.forEach((stage, stageIndex) => {
        if (!stage.tasks || !Array.isArray(stage.tasks)) return;
        
        stage.tasks.forEach((task, taskIndex) => {
          if (task.taskType === 'creating_listing') {
            const taskKey = `${stageIndex}-${taskIndex}`;
            const listingStateKey = `listing_${taskKey}`;
            const taskFieldValues = task.fieldValues || {};
            
            // Get URLs from task fieldValues or property listing URLs
            const realestateUrl = taskFieldValues.realestateUrl || propertyListingUrls?.realestateUrl || '';
            const domainUrl = taskFieldValues.domainUrl || propertyListingUrls?.domainUrl || '';
            
            // Only update if state doesn't exist or if URLs are empty and we have new URLs
            // This prevents overwriting user input
            const currentState = prev[listingStateKey];
            const propertyListingPriceDisplay = property?.listingPriceDisplay || 'contact';
            const propertyListingPriceDisplayText = property?.listingPriceDisplayText || '';
            const propertyListingAuthority = property?.listingAuthority || 
              (property?.saleType === 'auction' ? 'auction' : (property?.saleType === 'for sale' ? 'sale' : undefined));
            const propertySetSaleDate = property?.setSaleDate ? new Date(property.setSaleDate).toISOString().split('T')[0] : undefined;
            const propertyVideoLink = property?.videoLink || '';
            
            if (!currentState || 
                (currentState.realestateUrl === '' && realestateUrl) ||
                (currentState.domainUrl === '' && domainUrl)) {
              updated[listingStateKey] = {
                realestateUrl: currentState?.realestateUrl || realestateUrl,
                domainUrl: currentState?.domainUrl || domainUrl,
                isPosting: currentState?.isPosting || false,
                listingPriceDisplay: currentState?.listingPriceDisplay || propertyListingPriceDisplay,
                listingPriceDisplayText: currentState?.listingPriceDisplayText !== undefined ? currentState.listingPriceDisplayText : propertyListingPriceDisplayText,
                listingAuthority: currentState?.listingAuthority || propertyListingAuthority,
                listingStatus: currentState?.listingStatus || 'active', // Always default to 'active' for new listings
                listingHeadline: currentState?.listingHeadline !== undefined ? currentState.listingHeadline : property?.listingHeadline,
                setSaleDate: currentState?.setSaleDate || propertySetSaleDate,
                videoLink: currentState?.videoLink !== undefined ? currentState.videoLink : propertyVideoLink
              };
            } else if (currentState) {
              // Sync price display, authority, setSaleDate, and videoLink from property if not explicitly set by user (align with Property Listings)
              // Only update if property has a different value than current state to avoid unnecessary updates
              const needsUpdate = property && (
                currentState.listingPriceDisplay !== propertyListingPriceDisplay ||
                (currentState.listingPriceDisplayText === undefined && propertyListingPriceDisplayText !== '') ||
                (currentState.listingAuthority === undefined && propertyListingAuthority !== undefined) ||
                (currentState.setSaleDate === undefined && propertySetSaleDate !== undefined) ||
                (currentState.videoLink === undefined && propertyVideoLink !== '')
              );
              
              if (needsUpdate) {
                updated[listingStateKey] = {
                  ...currentState,
                  listingPriceDisplay: propertyListingPriceDisplay,
                  listingPriceDisplayText: currentState.listingPriceDisplayText !== undefined ? currentState.listingPriceDisplayText : propertyListingPriceDisplayText,
                  listingAuthority: currentState.listingAuthority !== undefined ? currentState.listingAuthority : propertyListingAuthority,
                  listingStatus: currentState.listingStatus || 'active', // Ensure status is always set
                  setSaleDate: currentState.setSaleDate !== undefined ? currentState.setSaleDate : propertySetSaleDate,
                  videoLink: currentState.videoLink !== undefined ? currentState.videoLink : propertyVideoLink
                };
              } else {
                // Keep existing state, but ensure listingStatus is set
                updated[listingStateKey] = {
                  ...currentState,
                  listingStatus: currentState.listingStatus || 'active'
                };
              }
            }
          }
        });
      });
      
      return updated;
    });
  }, [stages, propertyListingUrls, property?.listingPriceDisplay, property?.listingPriceDisplayText, property?.listingAuthority, property?.saleType]);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [fileToDelete, setFileToDelete] = React.useState<{fileName: string, deleteUrl: string} | null>(null);
  const [optimisticTaskUpdates, setOptimisticTaskUpdates] = React.useState<Record<string, { status: string; fieldValues: any }>>({});
  const [vendorEmail, setVendorEmail] = React.useState<string>(ownerEmail || '');
  const [selectedReportVendorEmails, setSelectedReportVendorEmails] = React.useState<Set<string>>(new Set());
  const [sendingEmail, setSendingEmail] = React.useState(false);
  const [emailError, setEmailError] = React.useState<string | null>(null);
  
  // Open schedule state for validation
  const [openSchedule, setOpenSchedule] = React.useState<OpenSchedule | null>(null);
  const [scheduleLoading, setScheduleLoading] = React.useState(false);
  
  // Load schedule when open_scheduling or creating_listing task is in progress
  React.useEffect(() => {
    // Check if any task needs schedule data and is in_progress
    const hasScheduleRelevantTask = stages.some((stage, sIdx) => 
      stage.tasks.some((task, tIdx) => {
        const taskStatus = optimisticTaskUpdates[`${sIdx}-${tIdx}`]?.status || task.status;
        return (task.taskType === 'open_scheduling' || task.taskType === 'creating_listing') && taskStatus === 'in_progress';
      })
    );
    
    if (hasScheduleRelevantTask) {
      const loadSchedule = async () => {
        try {
          setScheduleLoading(true);
          const scheduleData = await openScheduleService.getOpenSchedule(propertyId, agencySlug);
          setOpenSchedule(scheduleData);
        } catch (err: any) {
          if (err.response?.status !== 404) {
            console.error('Error loading schedule:', err);
          }
          setOpenSchedule(null);
        } finally {
          setScheduleLoading(false);
        }
      };
      loadSchedule();
    }
  }, [propertyId, agencySlug, refreshKey, stages, optimisticTaskUpdates]);

  // Refresh schedule when window regains focus (user might have scheduled auction in another tab)
  React.useEffect(() => {
    const hasScheduleRelevantTask = stages.some((stage, sIdx) => 
      stage.tasks.some((task, tIdx) => {
        const taskStatus = optimisticTaskUpdates[`${sIdx}-${tIdx}`]?.status || task.status;
        return (task.taskType === 'open_scheduling' || task.taskType === 'creating_listing') && taskStatus === 'in_progress';
      })
    );

    if (!hasScheduleRelevantTask) return;

    const handleFocus = () => {
      // Refresh schedule when window regains focus
      const loadSchedule = async () => {
        try {
          const scheduleData = await openScheduleService.getOpenSchedule(propertyId, agencySlug);
          setOpenSchedule(scheduleData);
        } catch (err: any) {
          if (err.response?.status !== 404) {
            console.error('Error loading schedule:', err);
          }
        }
      };
      loadSchedule();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [propertyId, agencySlug, stages, optimisticTaskUpdates]);
  
  // Poll for listing posting progress while job is queued or in progress
  React.useEffect(() => {
    if (!onTaskPatched) return;
    const hasActivePosting = stages.some((stage, sIdx) =>
      stage.tasks.some((task, tIdx) => {
        const taskKey = `${sIdx}-${tIdx}`;
        const postingStatus =
          optimisticTaskUpdates[taskKey]?.fieldValues?.postingStatus ||
          task.fieldValues?.postingStatus;
        return task.taskType === 'creating_listing' &&
          (postingStatus === 'queued' || postingStatus === 'in_progress');
      })
    );
    
    if (!hasActivePosting) return;
    
    let cancelled = false;
    const refresh = () => {
      if (!cancelled) onTaskPatched({ skipFiles: true, skipWorkflows: true, skipProperty: true });
    };
    
    refresh();
    const intervalId = window.setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [stages, optimisticTaskUpdates, onTaskPatched]);
  
  // Update vendor email when ownerEmail prop changes
  React.useEffect(() => {
    if (ownerEmail) {
      setVendorEmail(ownerEmail);
    }
  }, [ownerEmail]);

  // Initialize selected report vendor emails when property owner changes
  React.useEffect(() => {
    if (property?.owner) {
      const normalizeOwners = (owner: any): Array<{ name: string; email?: string }> => {
        if (!owner) return [];
        if (Array.isArray(owner)) {
          return owner.filter(o => o && o.name).map(o => ({ name: o.name, email: o.email }));
        }
        if (owner.name) {
          return [{ name: owner.name, email: owner.email }];
        }
        return [];
      };
      
      const allVendors = normalizeOwners(property.owner);
      const emailsWithEmail = allVendors
        .filter(v => v.email && v.email.includes('@'))
        .map(v => v.email!);
      
      if (emailsWithEmail.length > 0) {
        setSelectedReportVendorEmails(new Set(emailsWithEmail));
        // Also update vendorEmail for backward compatibility
        if (emailsWithEmail.length === 1) {
          setVendorEmail(emailsWithEmail[0]);
        } else if (ownerEmail) {
          setVendorEmail(ownerEmail);
        }
      } else if (ownerEmail) {
        setSelectedReportVendorEmails(new Set([ownerEmail]));
        setVendorEmail(ownerEmail);
      }
    }
  }, [property?.owner, ownerEmail]);

  // Initialize selected vendor emails when send agreement dialog opens
  React.useEffect(() => {
    if (showSendAgreementDialog && property?.owner) {
      const normalizeOwners = (owner: any): Array<{ name: string; email?: string }> => {
        if (!owner) return [];
        if (Array.isArray(owner)) {
          return owner.filter(o => o && o.name).map(o => ({ name: o.name, email: o.email }));
        }
        if (owner.name) {
          return [{ name: owner.name, email: owner.email }];
        }
        return [];
      };
      
      const allVendors = normalizeOwners(property.owner);
      const emailsWithEmail = allVendors
        .filter(v => v.email && v.email.includes('@'))
        .map(v => v.email!);
      
      if (emailsWithEmail.length > 0) {
        setSelectedVendorEmails(new Set(emailsWithEmail));
      } else if (ownerEmail) {
        // Fallback to ownerEmail prop if no vendor emails found
        setSelectedVendorEmails(new Set([ownerEmail]));
      }
    } else if (!showSendAgreementDialog) {
      // Clear selection when dialog closes
      setSelectedVendorEmails(new Set());
    }
  }, [showSendAgreementDialog, property?.owner, ownerEmail]);
  
  // Property Fixup: assign contractor jobs directly from the task
  const [fixupDialogOpen, setFixupDialogOpen] = React.useState(false);
  const [fixupContractorType, setFixupContractorType] = React.useState<string>('');
  const [fixupContractorId, setFixupContractorId] = React.useState<string>('');
  const [fixupDueDate, setFixupDueDate] = React.useState<string>('');
  const [fixupNotes, setFixupNotes] = React.useState<string>('');
  const [fixupLoading, setFixupLoading] = React.useState(false);
  const [fixupError, setFixupError] = React.useState<string | null>(null);
  const [allContractors, setAllContractors] = React.useState<Contractor[]>([]);
  const [fixupTypes, setFixupTypes] = React.useState<string[]>([]);
  const [fixupJobs, setFixupJobs] = React.useState<any[]>([]);
  
  // Load contractor types directly from API to ensure full list is available
  React.useEffect(() => {
    const loadContractorTypes = async () => {
      if (!agencySlug) return;
      try {
        const res = await api.get(`/agencies/${agencySlug}/contractor-types`);
        const types: Array<{ slug: string; isActive: boolean }> = Array.isArray(res.data) ? res.data : [];
        const activeTypeSlugs = types.filter(t => t.isActive).map(t => t.slug);
        const sortedSlugs = [...activeTypeSlugs].sort((a, b) => a.localeCompare(b));
        setFixupTypes(sortedSlugs);
        // Prefer handyman as default if available
        const preferred = sortedSlugs.find(s => s.toLowerCase().includes('handyman')) || (sortedSlugs[0] || '');
        if (!fixupContractorType || !sortedSlugs.includes(fixupContractorType)) {
          setFixupContractorType(preferred);
        }
      } catch (e) {
        // If types fail to load, leave existing behavior (derive from contractors) as fallback
      }
    };
    loadContractorTypes();
  }, [agencySlug]);

  const loadFixupJobs = React.useCallback(async () => {
    if (!agencySlug || !propertyId) return;
    try {
      const res = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        const all = Array.isArray(data) ? data : [];
        setFixupJobs(all.filter((j: any) => j.createdFrom === 'property_fixup'));
      }
    } catch {}
  }, [agencySlug, propertyId]);
  
  React.useEffect(() => {
    loadFixupJobs();
  }, [loadFixupJobs]);
  
  // Check if structured inputs exist for consultation report
  React.useEffect(() => {
    if (propertyId && agencySlug) {
      const checkStructuredInputs = async () => {
        try {
          const inputsResponse = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/inputs`);
          setHasStructuredInputs(!!inputsResponse.data.savedStructuredInputs);
        } catch (err) {
          // If check fails, assume no structured inputs
          setHasStructuredInputs(false);
        }
      };
      checkStructuredInputs();
    }
  }, [propertyId, agencySlug]);
  
  React.useEffect(() => {
    const handleCreated = (e: any) => {
      const job = e?.detail?.job;
      const pid = e?.detail?.propertyId;
      if (!job || pid !== propertyId) return;
      setFixupJobs(prev => (prev.some(j => j._id === job._id) ? prev : [job, ...prev]));
    };
    window.addEventListener('contractorJob:created', handleCreated as EventListener);
    return () => window.removeEventListener('contractorJob:created', handleCreated as EventListener);
  }, [propertyId]);
  
  const cancelFixupJob = async (jobId: string) => {
    if (!agencySlug) return;
    try {
      await api.delete(`/agencies/${agencySlug}/jobs/${jobId}/cancel`);
      // Refresh jobs
      await loadFixupJobs();
      showSuccess('Contractor job cancelled');
    } catch (err: any) {
      showError(err?.message || 'Failed to cancel job');
    }
  };
  
  // New state variables for styling arrangement and open scheduling tasks
  const [stylingCompany, setStylingCompany] = React.useState('');
  const [stylingScheduled, setStylingScheduled] = React.useState('');
  const [stylingNotes, setStylingNotes] = React.useState('');
  const [photographer, setPhotographer] = React.useState('');
  const [customPhotographer, setCustomPhotographer] = React.useState('');
  const [photographyScheduled, setPhotographyScheduled] = React.useState('');
  const [photographyNotes, setPhotographyNotes] = React.useState('');

  // Offers state
  const [offers, setOffers] = React.useState<Array<{
    buyerName: string;
    offerAmount: number;
    deposit?: number;
    settlement?: string;
    notes?: string;
    status: 'pending' | 'under_review' | 'accepted' | 'declined';
  }>>([]);
  const [newOffer, setNewOffer] = React.useState<{
    buyerName: string;
    offerAmount: string;
    deposit: string;
    settlement: string;
    notes: string;
    status: 'pending' | 'under_review' | 'accepted' | 'declined';
  }>({
    buyerName: '',
    offerAmount: '',
    deposit: '',
    settlement: '',
    notes: '',
    status: 'pending'
  });

  // Reminder state
  const [reminders, setReminders] = React.useState<TaskReminder[]>([]);
  const [reminderDialogOpen, setReminderDialogOpen] = React.useState(false);
  const [currentTaskForReminder, setCurrentTaskForReminder] = React.useState<{
    stageIndex: number;
    taskIndex: number;
    taskName: string;
    taskDescription: string;
  } | null>(null);
  
  // Load offers from any receive_offers task that's in_progress
  React.useEffect(() => {
    if (stages && stages.length > 0) {
      for (let stageIndex = 0; stageIndex < stages.length; stageIndex++) {
        const stage = stages[stageIndex];
        if (stage.tasks) {
          for (let taskIndex = 0; taskIndex < stage.tasks.length; taskIndex++) {
            const task = stage.tasks[taskIndex];
            if (task.taskType === 'receive_offers' && task.status === 'in_progress' && task.fieldValues?.offers) {
              const existingOffers = task.fieldValues.offers;
              if (Array.isArray(existingOffers) && existingOffers.length > 0) {
                setOffers(existingOffers);
                break;
              }
            }
          }
        }
      }
    }
  }, [stages]);

  // Check for existing agency agreement files when component loads or stages change
  React.useEffect(() => {
    // Check task fieldValues first (most reliable)
    if (stages && Array.isArray(stages)) {
      for (const stage of stages) {
        if (stage.tasks && Array.isArray(stage.tasks)) {
          for (const task of stage.tasks) {
            // Check both old location (next_steps_determination) and new location (agent_agreement or "Prepare Agency Agreement")
            const isAgentAgreementTask = task.taskType === 'agent_agreement' || 
                                         task.name === 'Prepare Agency Agreement' ||
                                         (task.taskType === 'next_steps_determination' && task.fieldValues?.agentAgreementFileId);
            
            if (isAgentAgreementTask && task.fieldValues?.agentAgreementFileId) {
              const fileId = task.fieldValues.agentAgreementFileId;
              if (fileId && (!agentAgreementFileId || agentAgreementFileId !== fileId)) {
                setAgentAgreementFileId(fileId);
                if (task.fieldValues.agentAgreementMethod === 'generate') {
                  setGeneratedAgreementFileId(fileId);
                } else {
                  setGeneratedAgreementFileId(null);
                }
              }
            }
            
            // Check for rental management agreement task
            const isRentalAgreementTask = task.taskType === 'rental_management_agreement' || 
                                         task.name === 'Prepare Rental Management Agreement';
            
            if (isRentalAgreementTask && task.fieldValues?.rentalAgreementFileId) {
              const fileId = task.fieldValues.rentalAgreementFileId;
              if (fileId && (!rentalAgreementFileId || rentalAgreementFileId !== fileId)) {
                setRentalAgreementFileId(fileId);
                if (task.fieldValues.rentalAgreementMethod === 'generate') {
                  setGeneratedRentalAgreementFileId(fileId);
                } else {
                  setGeneratedRentalAgreementFileId(null);
                }
              }
            }
          }
        }
      }
    }
    
    // If not found in fieldValues, check property.agencyAgreement
    if (property?.agencyAgreement?.key) {
      const fileId = property.agencyAgreement.key;
      if (!agentAgreementFileId || agentAgreementFileId !== fileId) {
        setAgentAgreementFileId(fileId);
        // Assume it's generated if it exists in property.agencyAgreement
        setGeneratedAgreementFileId(fileId);
      }
    }
  }, [stages, property?.agencyAgreement]);
  
  // Refs for task elements to enable auto-scrolling
  const taskRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});
  const [highlightedTask, setHighlightedTask] = React.useState<string | null>(null);
  const [openHouseDates, setOpenHouseDates] = React.useState('');
  const [openHouseTimes, setOpenHouseTimes] = React.useState('');
  const [openHouseNotes, setOpenHouseNotes] = React.useState('');
  const [contractors, setContractors] = React.useState<Contractor[]>([]);
  const [loadingContractors, setLoadingContractors] = React.useState(false);
  const [contractorJobs, setContractorJobs] = React.useState<ContractorJob[]>([]);
  const [loadingJobs, setLoadingJobs] = React.useState(false);
  const [agencyUsers, setAgencyUsers] = useState<any[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignStageIdx, setAssignStageIdx] = useState<number | null>(null);
  const [assignTaskIdx, setAssignTaskIdx] = useState<number | null>(null);
  const [assignAssignee, setAssignAssignee] = useState<string>('');
  const [assignDueDate, setAssignDueDate] = useState<string>('');
  const [assignDueTime, setAssignDueTime] = useState<string>('');
  const [assignTaskName, setAssignTaskName] = useState<string>('');
  
  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    message: string;
    confirmText: string;
    confirmColor: 'success' | 'error' | 'primary' | 'warning';
    onConfirm: () => void;
  } | null>(null);
  
  // Error notification state
  const [errorSnackbar, setErrorSnackbar] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: '' });
  
  // Success notification state
  const [successSnackbar, setSuccessSnackbar] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: '' });

  // Workflow version removed

  // Commission rate temporary state for commission_rate task
  const [commissionRateValue, setCommissionRateValue] = useState<string>('');
  const [commissionGstInclusive, setCommissionGstInclusive] = useState<boolean>(true);

  // Marketing and staging fees temporary state
  const [marketingFeeValue, setMarketingFeeValue] = useState<string>('');
  const [stagingFeeValue, setStagingFeeValue] = useState<string>('');

  // Reserve price temporary state for set_reserve_price task
  const [reservePriceValue, setReservePriceValue] = useState<string>('');

  // Fetch all contractors on component mount
  React.useEffect(() => {
    const fetchContractors = async () => {
      try {
        setLoadingContractors(true);
        const contractorsList = await getContractors(agencySlug);
        // Ensure contractorsList is always an array
        setContractors(Array.isArray(contractorsList) ? contractorsList : []);
      } catch (error) {
        console.error('Error fetching contractors:', error);
        setContractors([]); // Set empty array on error
      } finally {
        setLoadingContractors(false);
      }
    };
    fetchContractors();
  }, [agencySlug]);

  // Fetch contractor jobs
  React.useEffect(() => {
    const fetchContractorJobs = async () => {
      try {
        setLoadingJobs(true);
        const jobs = await getPropertyJobs(agencySlug, propertyId);
        setContractorJobs(Array.isArray(jobs) ? jobs : []);
      } catch (error) {
        console.error('Error fetching contractor jobs:', error);
        setContractorJobs([]);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchContractorJobs();
  }, [agencySlug, propertyId]);

  // Listen for quote response notifications and refresh contractor jobs
  useEffect(() => {
    if (!user || !agencySlug || !propertyId) return;

    const handleNotification = (notification: any) => {
      // Check if notification is about quote response
      if (notification && notification.relatedEntity?.propertyId === propertyId) {
        const title = notification.title || '';
        
        // Check if notification is about quote response
        if (title.includes('Quote response received')) {
          console.log('📋 Quote response notification received, refreshing contractor jobs...');
          // Refresh contractor jobs to show updated quote responses
          const fetchContractorJobs = async () => {
            try {
              const jobs = await getPropertyJobs(agencySlug, propertyId);
              setContractorJobs(Array.isArray(jobs) ? jobs : []);
            } catch (error) {
              console.error('Error fetching contractor jobs:', error);
            }
          };
          fetchContractorJobs();
        }
      }
    };

    // Subscribe to notifications
    const unsubscribe = socketService.onNotification(handleNotification);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user, agencySlug, propertyId]);

  // Load agency users for task assignment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await calendarService.getAgencyUsers(agencySlug);
        setAgencyUsers(Array.isArray(users) ? users : []);
      } catch (e) {
        setAgencyUsers([]);
      }
    };
    if (agencySlug) loadUsers();
  }, [agencySlug]);

  const patchTaskAssignment = async (stageIndex: number, taskIndex: number, body: { assignedTo?: string; dueDate?: string; name?: string }) => {
    if (!agencySlug) {
      showError('Missing tenant context');
      return;
    }
    try {
      await api.patch(
        `/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${taskIndex}`,
        body
      );
      if (onTaskPatched) onTaskPatched();
    } catch (e: any) {
      console.error('Failed to update task', e);
      showError(e?.response?.data?.message || e?.message || 'Failed to update task assignment');
    }
  };

  // Fetch reminders on component mount
  React.useEffect(() => {
    if (workflowId) {
      const fetchReminders = async () => {
        try {
          const taskReminders = await getTaskReminders(agencySlug, workflowId, propertyId, true);
          // Ensure taskReminders is always an array
          setReminders(Array.isArray(taskReminders) ? taskReminders : []);
        } catch (error) {
          console.error('Error fetching reminders:', error);
          setReminders([]); // Set empty array on error
        }
      };
      fetchReminders();
    }
  }, [agencySlug, workflowId, propertyId]);

  const handleReminderClick = (stageIndex: number, taskIndex: number, taskName: string, taskDescription: string) => {
    setCurrentTaskForReminder({ stageIndex, taskIndex, taskName, taskDescription });
    setReminderDialogOpen(true);
  };

  const handleReminderSuccess = () => {
    if (workflowId) {
      const fetchReminders = async () => {
        try {
          const taskReminders = await getTaskReminders(agencySlug, workflowId, propertyId, true);
          // Ensure taskReminders is always an array
          setReminders(Array.isArray(taskReminders) ? taskReminders : []);
        } catch (error) {
          console.error('Error fetching reminders:', error);
          setReminders([]); // Set empty array on error
        }
      };
      fetchReminders();
    }
  };

  const getTaskReminder = (stageIndex: number, taskIndex: number) => {
    // Ensure reminders is an array before calling find
    if (!Array.isArray(reminders)) {
      return undefined;
    }
    return reminders.find(r => r.stageIndex === stageIndex && r.taskIndex === taskIndex);
  };

  // Helper to get contractor type for a task type
  const getContractorTypeForTask = (taskType: string): string | null => {
    const mapping: Record<string, string> = {
      'property_photography': 'photographer',
      'styling_arrangement': 'stylist',
      'sign_board_order': 'property-sign-board-maker',
      'auction_booking': 'auctioner'
    };
    return mapping[taskType] || null;
  };

  // Helper to get job for a task
  const getJobForTask = (taskType: string): ContractorJob | undefined => {
    const contractorType = getContractorTypeForTask(taskType);
    if (!contractorType) return undefined;
    return contractorJobs.find(j => j.contractorType === contractorType);
  };

  // Helper to get display status for a job - prioritize job status, use quoteStatus only for draft/pending jobs
  const getJobDisplayStatus = (job: ContractorJob): { status: string; color: 'primary' | 'success' | 'error' | 'warning' } => {
    // Always prioritize job status if it's not draft or pending
    if (job.status === 'completed') {
      return { status: 'Completed', color: 'success' };
    }
    if (job.status === 'cancelled') {
      return { status: 'Cancelled', color: 'error' };
    }
    if (job.status === 'in_progress') {
      return { status: 'In Progress', color: 'primary' };
    }
    
    // For draft/pending jobs, show quote status if available
    const quoteStatus = (job as any)?.quoteStatus;
    if (quoteStatus && (job.status === 'draft' || job.status === 'pending')) {
      switch (quoteStatus) {
        case 'accepted':
          return { status: 'Quote Approved', color: 'success' };
        case 'declined':
          return { status: 'Quote Declined', color: 'error' };
        case 'responded':
          return { status: 'Waiting for Approval', color: 'warning' };
        case 'sent':
          return { status: 'Quotes Sent', color: 'primary' };
        case 'counter':
          return { status: 'Counter Offer', color: 'warning' };
        default:
          return { status: quoteStatus, color: 'primary' };
      }
    }
    
    // Fallback to job status
    return {
      status: job.status.replace('_', ' '),
      color: 'primary'
    };
  };

  // Handle accepting a quote from a task
  const handleAcceptQuote = async (jobId: string, contractorId: string) => {
    try {
      // Optimistically update the UI
      setContractorJobs(prevJobs => {
        return prevJobs.map(job => {
          if (job._id === jobId && (job as any)?.agencyQuote?.responses) {
            const updatedJob = { ...job };
            (updatedJob as any).agencyQuote = {
              ...(job as any).agencyQuote,
              responses: (job as any).agencyQuote.responses.map((response: any) => {
                // Convert both to strings for comparison
                const responseId = String(response.contractorId || response.contractorId?._id || '');
                const targetId = String(contractorId);
                if (responseId === targetId) {
                  return { ...response, status: 'accepted' };
                }
                // Mark other pending responses as declined
                if (response.status === 'pending') {
                  return { ...response, status: 'declined' };
                }
                return response;
              })
            };
            return updatedJob;
          }
          return job;
        });
      });
      
      await acceptContractorQuote(agencySlug, jobId, contractorId);
      // Refresh jobs to ensure we have the latest data
      const jobs = await getPropertyJobs(agencySlug, propertyId);
      setContractorJobs(Array.isArray(jobs) ? jobs : []);
      showSuccess('Quote approved successfully');
    } catch (error) {
      console.error('Error accepting quote:', error);
      // Revert optimistic update on error by refreshing
      const jobs = await getPropertyJobs(agencySlug, propertyId);
      setContractorJobs(Array.isArray(jobs) ? jobs : []);
      showError('Failed to approve quote');
    }
  };

  // Handle declining a quote from a task
  const handleDeclineQuote = async (jobId: string, contractorId: string) => {
    try {
      // Optimistically update the UI
      setContractorJobs(prevJobs => {
        return prevJobs.map(job => {
          if (job._id === jobId && (job as any)?.agencyQuote?.responses) {
            const updatedJob = { ...job };
            (updatedJob as any).agencyQuote = {
              ...(job as any).agencyQuote,
              responses: (job as any).agencyQuote.responses.map((response: any) => {
                // Convert both to strings for comparison
                const responseId = String(response.contractorId || response.contractorId?._id || '');
                const targetId = String(contractorId);
                if (responseId === targetId) {
                  return { ...response, status: 'declined' };
                }
                return response;
              })
            };
            return updatedJob;
          }
          return job;
        });
      });
      
      await declineContractorQuote(agencySlug, jobId, contractorId);
      // Refresh jobs to ensure we have the latest data
      const jobs = await getPropertyJobs(agencySlug, propertyId);
      setContractorJobs(Array.isArray(jobs) ? jobs : []);
      showSuccess('Quote declined successfully');
    } catch (error) {
      console.error('Error declining quote:', error);
      // Revert optimistic update on error by refreshing
      const jobs = await getPropertyJobs(agencySlug, propertyId);
      setContractorJobs(Array.isArray(jobs) ? jobs : []);
      showError('Failed to decline quote');
    }
  };

  const handleStageExpand = (stageIndex: number) => {
    setExpandedStages(prev => ({
      ...prev,
      [stageIndex]: !prev[stageIndex]
    }));
  };

  const handleTaskStatusChange = (stageIndex: number, taskIndex: number, newStatus: Task['status']) => {

    console.log('Stage Index:', stageIndex);
    console.log('Task Index:', taskIndex);
    console.log('New Status:', newStatus);
    console.log('Current Stage:', currentStage);
    console.log('Workflow ID:', workflowId);
    console.log('onTaskStatusChange exists:', !!onTaskStatusChange);
    console.log('Task name:', stages[stageIndex]?.tasks[taskIndex]?.name);
    
    if (onTaskStatusChange) {
      // Temporarily allow all task updates to debug Property Valuation issue
      // TODO: Re-implement proper stage progression logic after fixing the issue
      console.log('Calling onTaskStatusChange with workflowId:', workflowId);
      onTaskStatusChange(stageIndex, taskIndex, newStatus, workflowId);
      
      // Auto-expand next task when current task is completed
      if (newStatus === 'completed') {
        setTimeout(() => {
          // Find the next task
          let nextStageIndex = stageIndex;
          let nextTaskIndex = taskIndex + 1;
          
          // If we're at the end of the current stage, move to next stage
          if (nextTaskIndex >= stages[stageIndex].tasks.length) {
            nextStageIndex = stageIndex + 1;
            nextTaskIndex = 0;
          }
          
          // If there's a next task, expand its stage and scroll to it
          if (nextStageIndex < stages.length && nextTaskIndex < stages[nextStageIndex].tasks.length) {
            // Expand the stage containing the next task
            setExpandedStages(prev => ({
              ...prev,
              [nextStageIndex]: true
            }));
            
            // Scroll to the next task after a short delay to allow expansion
            setTimeout(() => {
              const nextTaskKey = `${nextStageIndex}-${nextTaskIndex}`;
              const nextTaskElement = taskRefs.current[nextTaskKey];
              if (nextTaskElement) {
                nextTaskElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
                
                // Highlight the next task briefly
                setHighlightedTask(nextTaskKey);
                setTimeout(() => {
                  setHighlightedTask(null);
                }, 2000); // Remove highlight after 2 seconds
              }
            }, 300);
          }
        }, 500); // Wait for backend response
      }
      
      // Clear optimistic update after a short delay to allow backend to respond
      setTimeout(() => {
        const taskKey = `${stageIndex}-${taskIndex}`;
        setOptimisticTaskUpdates(prev => {
          const newUpdates = { ...prev };
          delete newUpdates[taskKey];
          return newUpdates;
        });
      }, 1000);
    } else {
      console.warn('onTaskStatusChange is not provided');
    }
  };

  const getStageStatusColor = (status: Stage['status']) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      default:
        return 'info';
    }
  };

  const getTaskStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'in_progress':
        return <AccessTime color="primary" />;
      default:
        return <RadioButtonUnchecked color="disabled" />;
    }
  };

  // Fallback: infer skippable for legacy workflows created before the skippable flag existed
  const isStageSkippableByName = (stageName: string): boolean => {
    const skippableNames = new Set([
      'Client Meeting',
      'Property Assessment',
      'Requirements Gathering',
      'Market Analysis'
    ]);
    return skippableNames.has(stageName);
  };

  // Helper to filter visible tasks for Post-Auction stage
  const getVisibleTasksForStage = (stage: Stage): any[] => {
    if (stage.name === 'Post-Auction' || stage.name?.toLowerCase().includes('post-auction')) {
      const recordResultsTask = stage.tasks.find((t: any) => 
        t.name === 'Record Results' || t.taskType === 'auction_results'
      );
      const auctionOutcome = recordResultsTask?.fieldValues?.auctionOutcome;
      
      return stage.tasks.filter((task: any) => {
        // Always show "Record Results" and "Update Property Listing" tasks
        if (task.name === 'Record Results' || task.name === 'Update Property Listing') {
          return true;
        }
        
        // Show "Follow Up (If Sold)" only if outcome is a sold type
        if (task.name === 'Follow Up (If Sold)' || task.name?.includes('If Sold')) {
          const isSoldOutcome = 
            auctionOutcome === 'sold' || 
            auctionOutcome === 'sold-prior-to-auction' || 
            auctionOutcome === 'sold-at-auction' || 
            auctionOutcome === 'sold-after-auction' ||
            property?.auctionOutcome?.result === 'sold-prior-to-auction' ||
            property?.auctionOutcome?.result === 'sold-at-auction' ||
            property?.auctionOutcome?.result === 'sold-after-auction';
          return isSoldOutcome;
        }
        
        // Show tasks with "(If Passed In)" only if outcome is 'passed_in' or 'passed-in' or 'passed-in-vendor-bid'
        if (task.name?.includes('If Passed In')) {
          return auctionOutcome === 'passed_in' || 
                 auctionOutcome === 'passed-in' || 
                 auctionOutcome === 'passed-in-vendor-bid';
        }
        
        // Show tasks with "(If Withdrawn)" only if outcome is 'withdrawn'
        if (task.name?.includes('If Withdrawn')) {
          return auctionOutcome === 'withdrawn';
        }
        
        // If no outcome recorded yet, hide conditional tasks
        if (!auctionOutcome && !property?.auctionOutcome?.result) {
          return task.name === 'Record Results' || task.name === 'Update Property Listing';
        }
        
        return true;
      });
    }
    
    return Array.isArray(stage.tasks) ? stage.tasks : [];
  };

  const calculateStageProgress = (stage: Stage) => {
    // For Post-Auction stage, only count visible tasks
    const tasksToCount = getVisibleTasksForStage(stage);
    // Ensure tasks array is valid before processing
    if (!tasksToCount || tasksToCount.length === 0) {
      return 0;
    }
    const completedTasks = tasksToCount.filter(task => task.status === 'completed').length;
    return (completedTasks / tasksToCount.length) * 100;
  };

  const handleDeleteFile = async () => {
    if (!fileToDelete || currentStageIndex === null || currentTaskIndex === null) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // For agency agreement files, we need to clear the fieldValues and let the backend handle file cleanup
      // Since we don't have direct access to the file ID, we'll clear the fieldValues which should trigger cleanup
      const updatedFieldValues = { ...(optimisticTaskUpdates[`${currentStageIndex}-${currentTaskIndex}`]?.fieldValues || {}) };
      delete updatedFieldValues.agentAgreement; // legacy
      delete updatedFieldValues.agentAgreementFileId; // new
      delete updatedFieldValues.agentAgreementFileName;
      delete updatedFieldValues.agentAgreementUrl;
      
      // Send PATCH request to update the task and clear the agency agreement
      const updateUrl = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${currentStageIndex}/${currentTaskIndex}`;
      const res = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fieldValues: updatedFieldValues }),
      });
      
      if (res.ok) {
        // Clear the agency agreement fields
        setAgentAgreementFile(null);
        setAgentAgreementFileId(null);
        
        // Close dialog and trigger refresh
        setDeleteDialogOpen(false);
        setFileToDelete(null);
        if (onTaskPatched) onTaskPatched();
      } else {
        console.error('Failed to delete file');
        showError('Failed to delete file. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting agency agreement:', error);
      showError('Error deleting file. Please try again.');
    }
  };

  const handlePriceRangeSubmit = async (stageIndex: number, taskIndex: number) => {
    const fieldValues = {
      minPrice: Number(dialogPriceRange.minPrice),
      maxPrice: Number(dialogPriceRange.maxPrice)
    };
    const token = localStorage.getItem('token');
    try {
      const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${taskIndex}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          status: 'completed',
          fieldValues
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to update task');
      }
      
      // Get the updated workflow from the response and update the workflow state
      const updatedWorkflow = await response.json();
      if (onTaskStatusChange && workflowId) {
        // Update the workflow state with the response data
        onTaskStatusChange(stageIndex, taskIndex, 'completed', workflowId);
      }
      
      // Optimistically update UI
      setOptimisticTaskUpdates(prev => ({
        ...prev,
        [`${stageIndex}-${taskIndex}`]: { status: 'completed', fieldValues }
      }));
      if (onTaskPatched) onTaskPatched();
      setDialogPriceRange({ minPrice: '', maxPrice: '' });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleRentRangeSubmit = async (stageIndex: number, taskIndex: number) => {
    const fieldValues = {
      minWeeklyRent: Number(dialogRentRange.minWeeklyRent),
      maxWeeklyRent: Number(dialogRentRange.maxWeeklyRent)
    };
    const token = localStorage.getItem('token');
    try {
      const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${taskIndex}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          status: 'completed',
          fieldValues
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to update task');
      }
      if (onTaskStatusChange && workflowId) {
        onTaskStatusChange(stageIndex, taskIndex, 'completed', workflowId);
      }
      setOptimisticTaskUpdates(prev => ({
        ...prev,
        [`${stageIndex}-${taskIndex}`]: { status: 'completed', fieldValues }
      }));
      if (onTaskPatched) onTaskPatched();
      setDialogRentRange({ minWeeklyRent: '', maxWeeklyRent: '' });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleManagementFeesSubmit = async (stageIndex: number, taskIndex: number) => {
    const fieldValues: Record<string, unknown> = {};
    if (dialogManagementFees.managementFeePercent !== '') fieldValues.managementFeePercent = Number(dialogManagementFees.managementFeePercent);
    if (dialogManagementFees.lettingFeeWeeks !== '') fieldValues.lettingFeeWeeks = Number(dialogManagementFees.lettingFeeWeeks);
    if (dialogManagementFees.leaseRenewalFeePercent !== '') fieldValues.leaseRenewalFeePercent = Number(dialogManagementFees.leaseRenewalFeePercent);
    if (dialogManagementFees.inspectionFeeNote.trim() !== '') fieldValues.inspectionFeeNote = dialogManagementFees.inspectionFeeNote.trim();
    const token = localStorage.getItem('token');
    try {
      const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${taskIndex}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          status: 'completed',
          fieldValues
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to update task');
      }
      if (onTaskStatusChange && workflowId) {
        onTaskStatusChange(stageIndex, taskIndex, 'completed', workflowId);
      }
      setOptimisticTaskUpdates(prev => ({
        ...prev,
        [`${stageIndex}-${taskIndex}`]: { status: 'completed', fieldValues }
      }));
      if (onTaskPatched) onTaskPatched();
      setDialogManagementFees({ managementFeePercent: '', lettingFeeWeeks: '', leaseRenewalFeePercent: '', inspectionFeeNote: '' });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleConfirmNextStep = async () => {
    if (!pendingNextStepTask) return;
    const { stageIndex, taskIndex } = pendingNextStepTask;
    
    try {
      setLoading(true);
      const fieldValues: any = { nextStep: nextStepValue };
      
      if (onTaskStatusChange && workflowId) {
        onTaskStatusChange(stageIndex, taskIndex, 'completed', workflowId, fieldValues);
      }
      
      setNextStepValue('');
      showSuccess('Next step saved (manually confirmed)');
      setOptimisticTaskUpdates(prev => ({ 
        ...prev, 
        [`${stageIndex}-${taskIndex}`]: { status: 'completed', fieldValues } 
      }));
      
      setShowNoResponseConfirmationDialog(false);
      setPendingNextStepTask(null);
    } catch (e) {
      console.error('Error saving next step:', e);
      showError('Failed to save next step');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskComplete = (stageIndex: number, taskIndex: number) => {

    console.log('Stage Index:', stageIndex);
    console.log('Task Index:', taskIndex);
    
    // Ensure stages and tasks arrays exist
    if (!Array.isArray(stages) || !stages[stageIndex] || !Array.isArray(stages[stageIndex].tasks) || !stages[stageIndex].tasks[taskIndex]) {
      console.error('Invalid stage or task index');
      return;
    }
    
    const task = stages[stageIndex].tasks[taskIndex];
    console.log('Current task:', task);
    console.log('Current task status:', task.status);

    let newStatus: Task['status'];
    if (task.status === 'pending') {
      newStatus = 'in_progress';
    } else if (task.status === 'in_progress') {
      newStatus = 'completed';
    } else if (task.status === 'completed') {
      // Allow completed tasks to go back to in_progress for editing
      newStatus = 'in_progress';
    } else {
      newStatus = 'pending';
    }
    
    console.log('New status will be:', newStatus);
    
    // Initialize offers from fieldValues when task becomes in_progress
    if (newStatus === 'in_progress' && task.taskType === 'receive_offers' && task.fieldValues?.offers) {
      const existingOffers = task.fieldValues.offers;
      if (Array.isArray(existingOffers) && existingOffers.length > 0) {
        setOffers(existingOffers);
      }
    }
    
    // Initialize sale type from fieldValues when task becomes in_progress
    if (newStatus === 'in_progress' && task.taskType === 'sale_type_determination' && task.fieldValues?.saleType) {
      setSaleTypeValue(task.fieldValues.saleType);
    }

    // Initialize rent range from fieldValues when task becomes in_progress
    if (newStatus === 'in_progress' && task.taskType === 'rent_range_determination' && (task.fieldValues?.minWeeklyRent != null || task.fieldValues?.maxWeeklyRent != null)) {
      setDialogRentRange({
        minWeeklyRent: String(task.fieldValues.minWeeklyRent ?? ''),
        maxWeeklyRent: String(task.fieldValues.maxWeeklyRent ?? '')
      });
    }

    if (newStatus === 'in_progress' && (task.taskType === 'set_weekly_rent' || task.name === 'Set Weekly Rent') && task.fieldValues?.weeklyRent != null) {
      setDialogWeeklyRent(String(task.fieldValues.weeklyRent));
    }

    if (newStatus === 'in_progress' && (task.taskType === 'set_date_available' || task.name === 'Set Date Available') && task.fieldValues?.dateAvailable) {
      const d = task.fieldValues.dateAvailable;
      const dateStr = typeof d === 'string' ? d.split('T')[0] : (d instanceof Date ? d.toISOString().split('T')[0] : '');
      setDialogDateAvailable(dateStr || '');
    }

    // Initialize management fees from fieldValues when task becomes in_progress
    if (newStatus === 'in_progress' && task.taskType === 'management_fees_determination' && task.fieldValues) {
      const fv = task.fieldValues;
      setDialogManagementFees({
        managementFeePercent: fv.managementFeePercent != null ? String(fv.managementFeePercent) : '',
        lettingFeeWeeks: fv.lettingFeeWeeks != null ? String(fv.lettingFeeWeeks) : '',
        leaseRenewalFeePercent: fv.leaseRenewalFeePercent != null ? String(fv.leaseRenewalFeePercent) : '',
        inspectionFeeNote: fv.inspectionFeeNote != null ? String(fv.inspectionFeeNote) : '',
      });
    }
    
    // Initialize next step from fieldValues when task becomes in_progress
    if (newStatus === 'in_progress' && task.taskType === 'next_steps_determination' && task.fieldValues?.nextStep) {
      setNextStepValue(task.fieldValues.nextStep);
    }
    
    // Initialize agency agreement file from "Prepare Agency Agreement" task when it becomes in_progress
    if (newStatus === 'in_progress' && (task.taskType === 'agent_agreement' || task.name === 'Prepare Agency Agreement')) {
      if (task.fieldValues?.agentAgreementMethod) {
        setAgentAgreementMethod(task.fieldValues.agentAgreementMethod);
      }
      if (task.fieldValues?.agentAgreementFileId) {
        setAgentAgreementFileId(task.fieldValues.agentAgreementFileId);
        if (task.fieldValues.agentAgreementMethod === 'generate') {
          setGeneratedAgreementFileId(task.fieldValues.agentAgreementFileId);
        }
      }
    }
    
    // Initialize rental agreement file from "Prepare Rental Management Agreement" task when it becomes in_progress
    if (newStatus === 'in_progress' && (task.taskType === 'rental_management_agreement' || task.name === 'Prepare Rental Management Agreement')) {
      if (task.fieldValues?.rentalAgreementMethod) {
        setRentalAgreementMethod(task.fieldValues.rentalAgreementMethod);
      }
      if (task.fieldValues?.rentalAgreementFileId) {
        setRentalAgreementFileId(task.fieldValues.rentalAgreementFileId);
        if (task.fieldValues.rentalAgreementMethod === 'generate') {
          setGeneratedRentalAgreementFileId(task.fieldValues.rentalAgreementFileId);
        }
      }
      // Also check property.rentalManagementAgreement.key if fileId not in task
      if (!task.fieldValues?.rentalAgreementFileId && property?.rentalManagementAgreement?.key) {
        setRentalAgreementFileId(property.rentalManagementAgreement.key);
      }
    }

    // Initialize marketing fee from fieldValues when task becomes in_progress
    if (newStatus === 'in_progress' && task.taskType === 'marketing_fee' && task.fieldValues?.marketingFee) {
      setMarketingFeeValue(String(task.fieldValues.marketingFee));
    }

    // Initialize staging fee from fieldValues when task becomes in_progress
    if (newStatus === 'in_progress' && task.taskType === 'staging_fee' && task.fieldValues?.stagingFee) {
      setStagingFeeValue(String(task.fieldValues.stagingFee));
    }

    // Initialize reserve price from fieldValues when task becomes in_progress
    if (newStatus === 'in_progress' && (task.taskType === 'set_reserve_price' || (task.name?.toLowerCase().includes('reserve price') && !task.taskType)) && task.fieldValues?.reservePrice) {
      setReservePriceValue(String(task.fieldValues.reservePrice));
    }

    // Initialize auction results from fieldValues when task becomes in_progress
    if (newStatus === 'in_progress' && task.taskType === 'auction_results' && task.fieldValues) {
      // Handle both legacy values and new REAXML values
      const legacyOutcome = task.fieldValues.auctionOutcome;
      if (legacyOutcome) {
        // Map legacy values to REAXML values (default to sold-at-auction for 'sold', passed-in for 'passed_in')
        if (legacyOutcome === 'sold') {
          setAuctionOutcome('sold-at-auction'); // Default to 'sold-at-auction' for legacy 'sold'
        } else if (legacyOutcome === 'passed_in') {
          setAuctionOutcome('passed-in'); // Default to 'passed-in' for legacy 'passed_in'
        } else if (['sold-prior-to-auction', 'sold-at-auction', 'sold-after-auction', 'passed-in', 'passed-in-vendor-bid', 'withdrawn'].includes(legacyOutcome)) {
          // Already a REAXML value
          setAuctionOutcome(legacyOutcome);
        } else {
          setAuctionOutcome(legacyOutcome);
        }
      }
      
      // Initialize from property.auctionOutcome if available (more accurate)
      if (property?.auctionOutcome?.result) {
        setAuctionOutcome(property.auctionOutcome.result);
        if (property.auctionOutcome.date) {
          const dateStr = typeof property.auctionOutcome.date === 'string' 
            ? property.auctionOutcome.date.split('T')[0]
            : new Date(property.auctionOutcome.date).toISOString().split('T')[0];
          setAuctionOutcomeDate(dateStr);
        } else {
          // Default to today's date if no date is set
          setAuctionOutcomeDate(new Date().toISOString().split('T')[0]);
        }
        if (property.auctionOutcome.maxBid !== undefined) {
          setAuctionMaxBid(String(property.auctionOutcome.maxBid));
        }
        if (property.auctionOutcome.notes) {
          setAuctionNotes(property.auctionOutcome.notes);
        }
      } else if (!auctionOutcomeDate) {
        // If no property.auctionOutcome exists, default to today's date
        setAuctionOutcomeDate(new Date().toISOString().split('T')[0]);
      }
      
      // Initialize showSoldPrice from soldDetails
      if (property?.soldDetails?.showSoldPrice !== undefined) {
        setShowSoldPrice(property.soldDetails.showSoldPrice);
      } else {
        setShowSoldPrice(true); // Default to showing price
      }
      
      // Only initialize final price if outcome is sold or passed_in (not withdrawn)
      if (task.fieldValues.finalPrice && (legacyOutcome === 'sold' || legacyOutcome === 'passed_in')) {
        setAuctionFinalPrice(String(task.fieldValues.finalPrice));
      } else if (property?.soldDetails?.soldPrice) {
        setAuctionFinalPrice(String(property.soldDetails.soldPrice));
      } else {
        setAuctionFinalPrice('');
      }
      
      if (task.fieldValues.notes && !property?.auctionOutcome?.notes) {
        setAuctionNotes(task.fieldValues.notes);
      }
    }

    // Set optimistic update immediately for better UX
    const taskKey = `${stageIndex}-${taskIndex}`;
    setOptimisticTaskUpdates(prev => ({
      ...prev,
      [taskKey]: { status: newStatus, fieldValues: task.fieldValues || {} }
    }));

    console.log('Calling handleTaskStatusChange...');
    handleTaskStatusChange(stageIndex, taskIndex, newStatus);
  };

  // Fetch the report from backend
  const fetchReport = async (stageIndex: number) => {
    setReportLoading(true);
    setReportError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/stages/${stageIndex}/consultation-report`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) throw new Error('Failed to fetch report');
      const data = await response.json();
      setReportContent(data.content || '');
      setReportStageIndex(stageIndex);
      setOpenReportDialog(true);
    } catch (err: any) {
      setReportError('Failed to fetch report');
    } finally {
      setReportLoading(false);
    }
  };

  // Save the report
  const saveReport = async () => {
    if (reportStageIndex === null) return;
    setReportLoading(true);
    setReportError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/stages/${reportStageIndex}/consultation-report`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ content: reportContent })
      });
      if (!response.ok) throw new Error('Failed to save report');
      setReportSuccess('Report saved');
      if (onTaskPatched) onTaskPatched();
    } catch (err: any) {
      setReportError('Failed to save report');
    } finally {
      setReportLoading(false);
    }
  };

  // Send the report
  const sendReport = async () => {
    if (reportStageIndex === null) return;
    setReportLoading(true);
    setReportError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/stages/${reportStageIndex}/consultation-report/send`, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) throw new Error('Failed to send report');
      setReportSuccess('Report sent to owner');
      setOpenReportDialog(false);
      if (onTaskPatched) onTaskPatched();
    } catch (err: any) {
      setReportError('Failed to send report');
    } finally {
      setReportLoading(false);
    }
  };

  // Helper function to show confirmation dialog
  const showConfirmDialog = (
    title: string,
    message: string,
    confirmText: string,
    confirmColor: 'success' | 'error' | 'primary' | 'warning',
    onConfirm: () => void
  ) => {
    setConfirmDialogConfig({
      title,
      message,
      confirmText,
      confirmColor,
      onConfirm
    });
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
    setConfirmDialogConfig(null);
  };

  const handleConfirmDialogConfirm = () => {
    if (confirmDialogConfig?.onConfirm) {
      confirmDialogConfig.onConfirm();
    }
    handleConfirmDialogClose();
  };

  const checkAgreementEligibility = async () => {
    try {
      const response = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/agency-agreement/eligibility`);
      setAgreementEligibility(response.data);
      return response.data as typeof agreementEligibility;
    } catch (err: any) {
      console.error('Error checking agreement eligibility:', err);
      const fallback = {
        canGenerate: true,
        hasFreeGeneration: false,
        balanceCents: 0,
        costCents: 998,
        message: 'Unable to check eligibility',
        missingPrerequisites: [] as MissingAgreementPrerequisite[],
      };
      setAgreementEligibility(fallback);
      return fallback;
    }
  };

  const checkRentalAgreementEligibility = async () => {
    try {
      const response = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/rental-management-agreement/eligibility`);
      setRentalAgreementEligibility(response.data);
      return response.data as typeof rentalAgreementEligibility;
    } catch (err: any) {
      console.error('Error checking rental agreement eligibility:', err);
      const fallback = {
        canGenerate: true,
        hasFreeGeneration: false,
        balanceCents: 0,
        costCents: 998,
        message: 'Unable to check eligibility',
      };
      setRentalAgreementEligibility(fallback);
      return fallback;
    }
  };

  // Function to generate rental agreement PDF
  const generateRentalAgreementPdf = async () => {
    try {
      setGeneratingRentalAgreementPdf(true);
      const res = await api.post(
        `/agencies/${agencySlug}/properties/${propertyId}/rental-management-agreement/generate`,
        rentalAgreementConfig,
        { responseType: 'blob' }
      );
      
      // Save the generated PDF to the property
      const file = new File([res.data], 'rental-management-agreement.pdf', { type: 'application/pdf' });
      await uploadRentalManagementAgreement(agencySlug, propertyId, file);
      // Refresh property data so Files section and task UI show the new agreement
      if (onTaskPatched) {
        await onTaskPatched();
      }
      window.dispatchEvent(new CustomEvent('fileListRefresh'));
      setLastRentalAgreementPdfBlob(res.data);
      const contentType = res.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const err = JSON.parse(text);
        throw new Error(err.error || err.message || 'Failed to generate PDF');
      }
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (rentalAgreementPdfUrl) URL.revokeObjectURL(rentalAgreementPdfUrl);
      setRentalAgreementPdfUrl(url);
      setLastRentalAgreementPdfBlob(blob);
      setShowRentalAgreementPdfDialog(true);
    } catch (err: any) {
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          showError(j?.error || 'Failed to generate PDF');
        } catch {
          showError('Failed to generate PDF');
        }
      } else {
        showError(err?.message || err?.response?.data?.error || 'Failed to generate PDF');
      }
    } finally {
      setGeneratingRentalAgreementPdf(false);
    }
  };

  // Function to generate agreement
  const generateAgreement = async () => {
    try {
      setGeneratingAgreement(true);
      setShowAgreementGenerationDialog(false);
      
      const response = await api.post(
        `/agencies/${agencySlug}/properties/${propertyId}/agency-agreement/generate`,
        {
          marketingCosts: agreementFormData.marketingCosts,
          introducedPurchasersListDays: agreementFormData.introducedPurchasersListDays,
        },
        {
          responseType: 'blob', // Important: get PDF as blob
        }
      );
      
      // Check if response is actually an error (JSON error returned as blob)
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || errorData.message || 'Failed to generate agreement');
      }
      
      await handleAgreementGeneration(response);
    } catch (err: any) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const errorData = JSON.parse(text);
          err.response.data = errorData;
          if (!err.message || err.message === 'Request failed with status code 400' || err.message.startsWith('Request failed')) {
            err.message = errorData.error || errorData.message || err.message;
          }
        } catch {
          // not JSON
        }
      }
      handleAgreementError(err);
    } finally {
      setGeneratingAgreement(false);
    }
  };

  generateAgreementRef.current = generateAgreement;
  generateRentalAgreementRef.current = generateRentalAgreementPdf;

  // Helper function to handle agreement generation
  const handleAgreementGeneration = async (response: any) => {
    // Create a blob URL for preview
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    setAgreementReviewUrl(blobUrl);
    
    // Automatically download the PDF
    const fileName = `agent-agreement-${propertyId}-${Date.now()}.pdf`;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
    
    // Show review dialog
    setShowAgreementReviewDialog(true);

    // Upload the generated PDF to the workflow stage
    // Use the stored stageIndex from when the dialog was opened
    const targetStageIndex = agreementStageIndexRef.current ?? agreementStageIndex;
    if (targetStageIndex === null || targetStageIndex === undefined) {
      throw new Error('Stage index not available for file upload');
    }
    
    const formData = new FormData();
    formData.append('file', blob, fileName);
    formData.append('type', 'agent_agreement');
    
    const uploadResponse = await api.post(
      `/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${targetStageIndex}/files`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    
    console.log('[AgreementUpload] Upload response:', uploadResponse.data);
    const { key, fileName: uploadedFileName, fileId } = uploadResponse.data;
    setGeneratedAgreementFileId(key);
    setAgentAgreementFileId(key);
    
    // Note: The upload to workflow stage files endpoint already saves to property.agencyAgreement
    // and creates a notification, so we don't need to call uploadAgencyAgreement separately
    // However, we should refresh property data to ensure UI updates
    if (onTaskPatched) {
      await onTaskPatched();
    }
    
    // Trigger file list refresh by calling onTaskPatched (same as other file uploads)
    // This will cause StageFiles component to refetch the file list
    // Also manually trigger a file list refresh by dispatching a custom event
    // This ensures the file list updates even if refreshKey doesn't change immediately
    console.log('[AgreementUpload] Dispatching fileListRefresh event for stage', targetStageIndex, 'workflow', workflowId);
    window.dispatchEvent(new CustomEvent('fileListRefresh', { 
      detail: { stageIndex: targetStageIndex, workflowId } 
    }));
    
    // Add a small delay and trigger another refresh to ensure it's picked up
    setTimeout(() => {
      console.log('[AgreementUpload] Triggering delayed file list refresh');
      window.dispatchEvent(new CustomEvent('fileListRefresh', { 
        detail: { stageIndex: targetStageIndex, workflowId } 
      }));
      // Also refresh property data again to ensure UI updates
      if (onTaskPatched) {
        onTaskPatched();
      }
    }, 1000);
    
    showSuccess('Agency agreement generated, downloaded, and uploaded successfully. Check your notifications.');
    // Refresh eligibility after successful generation
    await checkAgreementEligibility();
  };

  // Helper function to handle agreement errors
  const handleAgreementError = (err: any) => {
    console.error('Error generating agency agreement:', err);
    const errorData = err?.response?.data;
    const missing = errorData?.missingPrerequisites as MissingAgreementPrerequisite[] | undefined;
    if (errorData?.code === 'MISSING_AGREEMENT_PREREQUISITES' || (missing && missing.length > 0)) {
      setMissingAgreementPrerequisites(missing && missing.length > 0 ? missing : []);
      setShowLicenseNumberDialog(true);
      if (!missing || missing.length === 0) {
        showError(errorData?.error || errorData?.message || 'Required information is missing.');
      }
      return;
    }
    if (typeof errorData?.error === 'string' && (errorData.error.includes('license number') || errorData.error.includes('signature') || errorData.error.includes('ABN'))) {
      setMissingAgreementPrerequisites([]);
      setShowLicenseNumberDialog(true);
      showError(errorData.error);
      return;
    }
    const serverMessage = errorData?.error || errorData?.message;
    showError(serverMessage || getOperationErrorMessage(err, 'generateAgreement'));
  };

  // Check eligibility when dialog opens
  React.useEffect(() => {
    if (showAgreementGenerationDialog) {
      checkAgreementEligibility();
    }
  }, [showAgreementGenerationDialog]);

  // Check rental agreement eligibility when config dialog opens (same pattern as sales)
  React.useEffect(() => {
    if (showRentalAgreementConfigDialog) {
      checkRentalAgreementEligibility();
    }
  }, [showRentalAgreementConfigDialog]);

  const showError = (message: string) => {
    setErrorSnackbar({ open: true, message });
  };

  const handleErrorSnackbarClose = () => {
    setErrorSnackbar({ open: false, message: '' });
  };
  
  const showSuccess = (message: string) => {
    setSuccessSnackbar({ open: true, message });
  };

  const handleSuccessSnackbarClose = () => {
    setSuccessSnackbar({ open: false, message: '' });
  };

  // Workflow version removed

  return (
    <Paper sx={{ p: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '16px' }}>
        <span style={{ fontSize: '1.25rem', fontWeight: 600 }}>Workflow Progress</span>
      </div>
      
      <Stepper orientation="vertical">
        {Array.isArray(stages) && stages.map((stage, stageIndex) => (
          <Step key={stage._id || stage.stageId || `stage-${stageIndex}`} completed={stage.status === 'completed'}>
            <StepLabel
              data-stage-header={stageIndex}
              StepIconProps={{
                icon: stage.status === 'completed' ? <AssignmentTurnedIn /> : <Assignment />,
                color: getStageStatusColor(stage.status)
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 500, fontSize: '1rem' }}>{stage.name}</span>
                <Chip
                  size="small"
                  label={stage.status.toUpperCase()}
                  color={getStageStatusColor(stage.status)}
                />
                {((stage.skippable || isStageSkippableByName(stage.name)) && stage.status !== 'completed' && editable && propertyStatus !== 'archived') ? (
                  <Tooltip title="Skip this stage and mark all tasks as completed">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!workflowId) {
                            console.error('Cannot skip stage: workflowId is missing');
                            showError('Cannot skip stage: Workflow ID is missing. Please refresh the page.');
                            return;
                          }
                          console.log('Skip stage button clicked:', { workflowId, stageIndex, stageName: stage.name });
                          showConfirmDialog(
                            'Skip Stage',
                            `Are you sure you want to skip the "${stage.name}" stage? All tasks in this stage will be marked as completed and you will move to the next stage.`,
                            'Skip Stage',
                            'warning',
                            async () => {
                              try {
                                console.log('Skip stage confirmed, calling API:', { agencySlug, propertyId, workflowId, stageIndex });
                                await skipWorkflowStage(agencySlug, propertyId, workflowId, stageIndex);
                                console.log('Stage skipped successfully');
                                // Expand next stage and collapse the skipped one for a smooth jump
                                setExpandedStages(prev => ({ ...prev, [stageIndex]: false, [stageIndex + 1]: true }));
                                setCurrentStageIndex(stageIndex + 1);
                                if (onTaskPatched) {
                                  onTaskPatched();
                                }
                                // Scroll to next stage label
                                try {
                                  const nextHeader = document.querySelector(`[data-stage-header="${stageIndex + 1}"]`);
                                  if (nextHeader && (nextHeader as any).scrollIntoView) {
                                    (nextHeader as any).scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }
                                } catch {}
                                showSuccess('Stage skipped successfully');
                              } catch (error: any) {
                                console.error('Error skipping stage:', error);
                                showError(getErrorMessage(error));
                              }
                            }
                          );
                        }}
                        sx={{ ml: 1 }}
                      >
                        Skip Stage
                      </Button>
                    </span>
                  </Tooltip>
                ) : null}
                <IconButton
                  size="small"
                  onClick={() => handleStageExpand(stageIndex)}
                >
                  {expandedStages[stageIndex] ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </span>
            </StepLabel>

            {expandedStages[stageIndex] && (
              <Box style={{ marginBottom: '16px', paddingLeft: '32px' }}>
                <LinearProgress
                  variant="determinate"
                  value={calculateStageProgress(stage)}
                  color={getStageStatusColor(stage.status)}
                  sx={{ mb: 1 }}
                />
                {(() => {
                  // For Post-Auction stage, calculate visible tasks for accurate count
                  let visibleTasks = Array.isArray(stage.tasks) ? stage.tasks : [];
                  if (stage.name === 'Post-Auction' || stage.name?.toLowerCase().includes('post-auction')) {
                    const recordResultsTask = stage.tasks.find((t: any) => 
                      t.name === 'Record Results' || t.taskType === 'auction_results'
                    );
                    const auctionOutcome = recordResultsTask?.fieldValues?.auctionOutcome;
                    
                    visibleTasks = stage.tasks.filter((task: any) => {
                      // Always show "Record Results" and "Update Property Listing" tasks
                      if (task.name === 'Record Results' || task.name === 'Update Property Listing') {
                        return true;
                      }
                      
                      // Show "Follow Up (If Sold)" only if outcome is a sold type
                      if (task.name === 'Follow Up (If Sold)' || task.name?.includes('If Sold')) {
                        const isSoldOutcome = 
                          auctionOutcome === 'sold' || 
                          auctionOutcome === 'sold-prior-to-auction' || 
                          auctionOutcome === 'sold-at-auction' || 
                          auctionOutcome === 'sold-after-auction' ||
                          property?.auctionOutcome?.result === 'sold-prior-to-auction' ||
                          property?.auctionOutcome?.result === 'sold-at-auction' ||
                          property?.auctionOutcome?.result === 'sold-after-auction';
                        return isSoldOutcome;
                      }
                      
                      // Show tasks with "(If Passed In)" only if outcome is 'passed_in' or 'passed-in' or 'passed-in-vendor-bid'
                      if (task.name?.includes('If Passed In')) {
                        return auctionOutcome === 'passed_in' || 
                               auctionOutcome === 'passed-in' || 
                               auctionOutcome === 'passed-in-vendor-bid';
                      }
                      
                      // Show tasks with "(If Withdrawn)" only if outcome is 'withdrawn'
                      if (task.name?.includes('If Withdrawn')) {
                        return auctionOutcome === 'withdrawn';
                      }
                      
                      // If no outcome recorded yet, hide conditional tasks
                      if (!auctionOutcome && !property?.auctionOutcome?.result) {
                        return task.name === 'Record Results' || task.name === 'Update Property Listing';
                      }
                      
                      return true;
                    });
                  }
                  
                  const completedCount = visibleTasks.filter((t: any) => t.status === 'completed').length;
                  return (
                    <span style={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                      {completedCount} of {visibleTasks.length} tasks completed
                    </span>
                  );
                })()}
                <List dense>
                  {getVisibleTasksForStage(stage).map((task: any, taskIndex: number) => {
                    // Find original index in stage.tasks array for proper taskKey and task operations
                    const originalTaskIndex = Array.isArray(stage.tasks) 
                      ? stage.tasks.findIndex((t: any) => {
                          // Try to match by _id first (most reliable)
                          if (t._id && task._id && t._id.toString() === task._id.toString()) {
                            return true;
                          }
                          // Fall back to name and taskType match
                          if (t.name === task.name && t.taskType === task.taskType) {
                            return true;
                          }
                          // Fall back to just name match if taskType is not set
                          if (t.name === task.name && (!t.taskType || !task.taskType)) {
                            return true;
                          }
                          return false;
                        })
                      : taskIndex;
                    // Use originalTaskIndex for all task operations, fall back to taskIndex if not found
                    const actualTaskIndex = originalTaskIndex >= 0 ? originalTaskIndex : taskIndex;
                    const taskKey = `${stageIndex}-${actualTaskIndex}`;
                    const optimistic = optimisticTaskUpdates[taskKey];
                    

                    
                    return (
                      <React.Fragment key={taskKey}>
                        <ListItem
                          ref={(el) => {
                            taskRefs.current[taskKey] = el;
                          }}
                          sx={{
                            backgroundColor: highlightedTask === taskKey ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                            transition: 'background-color 0.3s ease',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: highlightedTask === taskKey ? 'primary.light' : 'Boxider',
                            mb: 1,
                            p: 1.5,
                            pl: 2,
                            position: 'relative',
                            '&:before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 4,
                              borderTopLeftRadius: 4,
                              borderBottomLeftRadius: 4,
                              bgcolor: (optimistic ? optimistic.status : task.status) === 'completed'
                                ? 'success.main'
                                : (optimistic ? optimistic.status : task.status) === 'in_progress'
                                ? 'primary.main'
                                : 'grey.400'
                            }
                          }}
                          secondaryAction={
                            <>
                              {onTaskStatusChange ? (
                              <Tooltip title={(() => {
                                if (propertyStatus === 'archived') {
                                  return 'Cannot update tasks for archived properties. Please restore the property first.';
                                }
                                const taskStatus = optimistic ? optimistic.status : task.status;
                                const isInfoSheetPrep = task.taskType === 'information_sheet_preparation' && taskStatus === 'in_progress';
                                const hasInfoSheet = isInfoSheetPrep ? 
                                  (Array.isArray((stage as any).files) && 
                                   (stage as any).files.some((f: any) => f?.type === 'information_sheet')) :
                                  true;
                                
                                if (taskStatus === 'pending') {
                                  return 'Start task';
                                }
                                if (taskStatus === 'in_progress') {
                                  if (isInfoSheetPrep && !hasInfoSheet) {
                                    return 'Complete task (Information sheet must be uploaded first)';
                                  }
                                  return 'Complete task';
                                }
                                return 'Reopen task';
                              })()}>
                                  <span> {/* Wrapper span for disabled IconButton */}
                                    {(() => {
                                      const taskStatus = optimistic ? optimistic.status : task.status;
                                      const isOpenScheduling = task.taskType === 'open_scheduling' && taskStatus === 'in_progress';
                                      const isInformationSheetPrep = task.taskType === 'information_sheet_preparation' && taskStatus === 'in_progress';
                                      
                                      // Check if schedule exists and has sessions
                                      const hasSchedule = openSchedule?._id && openSchedule.sessions && openSchedule.sessions.length > 0;
                                      
                                      // For Post-Auction stage, check auction outcome
                                      const isPostAuctionStage = stage.name === 'Post-Auction' || stage.name?.toLowerCase().includes('post-auction');
                                      let auctionOutcome: string | undefined;
                                      if (isPostAuctionStage && isOpenScheduling) {
                                        const recordResultsTask = stage.tasks.find((t: any) => 
                                          t.name === 'Record Results' || t.taskType === 'auction_results'
                                        );
                                        auctionOutcome = recordResultsTask?.fieldValues?.auctionOutcome;
                                      }
                                      
                                      const isNonSuccessfulAuction = 
                                        auctionOutcome === 'passed_in' || 
                                        auctionOutcome === 'passed-in' || 
                                        auctionOutcome === 'passed-in-vendor-bid' || 
                                        auctionOutcome === 'withdrawn';
                                      const isSoldAuction = 
                                        auctionOutcome === 'sold' || 
                                        auctionOutcome === 'sold-prior-to-auction' || 
                                        auctionOutcome === 'sold-at-auction' || 
                                        auctionOutcome === 'sold-after-auction';
                                      
                                      // For Post-Auction non-successful auctions, require open scheduling
                                      // For sold auctions, open scheduling is not required
                                      const requiresOpenSchedule = isPostAuctionStage && isOpenScheduling
                                        ? isNonSuccessfulAuction // Require only for non-successful auctions
                                        : isOpenScheduling; // For pre-auction, always require if in progress
                                      
                                      // For auction properties, check if auction is planned (unless it's a post-auction non-successful auction)
                                      const hasAuctionSchedule = (isPostAuctionStage && isNonSuccessfulAuction)
                                        ? true // For non-successful auctions, auction scheduling is not required
                                        : (propertySaleType !== 'auction' || 
                                          ((openSchedule?.auctionDate && 
                                           typeof openSchedule.auctionDate === 'string' && 
                                           openSchedule.auctionDate.trim() !== '' &&
                                           openSchedule?.auctionTime && 
                                           typeof openSchedule.auctionTime === 'string' && 
                                           openSchedule.auctionTime.trim() !== '') ||
                                          openSchedule?.sessions?.some((s: any) => s.isAuction === true)));
                                      
                                      // Check if information sheet file exists (only check when task is in progress)
                                      const hasInformationSheet = isInformationSheetPrep ? 
                                        (Array.isArray((stage as any).files) && 
                                         (stage as any).files.some((f: any) => f?.type === 'information_sheet')) :
                                        true; // If not information sheet prep task, consider it as having the file (no check needed)
                                      
                                      // Disable if: 
                                      // - property is archived, OR 
                                      // - (is open_scheduling AND required schedule missing), OR 
                                      // - (is information_sheet_preparation AND no file uploaded)
                                      const isDisabled = propertyStatus === 'archived' || 
                                        (isOpenScheduling && requiresOpenSchedule && (!hasSchedule || !hasAuctionSchedule)) ||
                                        (isInformationSheetPrep && !hasInformationSheet);
                                      
                                      return (
                                        <IconButton
                                          edge="end"
                                          onClick={() => handleTaskComplete(stageIndex, actualTaskIndex)}
                                          disabled={isDisabled}
                                          sx={{
                                            opacity: isDisabled ? 0.5 : 1,
                                            cursor: isDisabled ? 'not-allowed' : 'pointer'
                                          }}
                                        >
                                          {getTaskStatusIcon(taskStatus as Task['status'])}
                                        </IconButton>
                                      );
                                    })()}
                                  </span>
                                </Tooltip>
                              ) : (
                                <span>{getTaskStatusIcon((optimistic ? optimistic.status : task.status) as Task['status'])}</span>
                              )}
                              <Tooltip title={
                                propertyStatus === 'archived' 
                                  ? 'Cannot set reminders for archived properties. Please restore the property first.' 
                                  : getTaskReminder(stageIndex, actualTaskIndex) ? 'Edit reminder' : 'Set reminder'
                              }>
                                <span>
                                  <IconButton
                                    edge="end"
                                    onClick={() => handleReminderClick(stageIndex, actualTaskIndex, task.name, task.description)}
                                    size="small"
                                    sx={{ ml: 1 }}
                                    disabled={propertyStatus === 'archived'}
                                    style={{
                                      opacity: propertyStatus === 'archived' ? 0.5 : 1,
                                      cursor: propertyStatus === 'archived' ? 'not-allowed' : 'pointer'
                                    }}
                                  >
                                    <Schedule color={getTaskReminder(stageIndex, actualTaskIndex) ? 'primary' : 'disabled'} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={propertyStatus === 'archived' ? 'Cannot assign tasks for archived properties.' : 'Assign task'}>
                                <span>
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    sx={{ ml: 1 }}
                                    disabled={propertyStatus === 'archived'}
                                    onClick={() => {
                                      setAssignStageIdx(stageIndex);
                                      setAssignTaskIdx(actualTaskIndex);
                                      {
                                        const at = (task as any).assignedTo;
                                        const id = at && typeof at === 'object' ? (at._id || '') : (at || '');
                                        setAssignAssignee(id);
                                      }
                                      setAssignDueDate((task as any).dueDate ? new Date((task as any).dueDate).toISOString().slice(0,10) : '');
                                      setAssignTaskName(task.name || '');
                                      setAssignDialogOpen(true);
                                    }}
                                  >
                                    <SupervisorAccount />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={
                                propertyStatus === 'archived' 
                                  ? 'Cannot view comments for archived properties. Please restore the property first.' 
                                  : openTaskComments[taskKey] ? 'Hide comments' : 'Show comments'
                              }>
                                <span>
                                  <IconButton
                                    edge="end"
                                    onClick={() => setOpenTaskComments(prev => ({ ...prev, [taskKey]: !prev[taskKey] }))}
                                    size="small"
                                    sx={{ ml: 1 }}
                                    disabled={propertyStatus === 'archived'}
                                    style={{
                                      opacity: propertyStatus === 'archived' ? 0.5 : 1,
                                      cursor: propertyStatus === 'archived' ? 'not-allowed' : 'pointer'
                                    }}
                                  >
                                    <Assignment color={openTaskComments[taskKey] ? 'primary' : 'disabled'} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          }
                        >
                          <ListItemText
                            disableTypography
                            primary={
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <Chip size="small" label={taskIndex + 1} sx={{ minWidth: 28 }} />
                                <span style={{
                                  fontWeight: 600,
                                  color: (optimistic ? optimistic.status : task.status) === 'completed' ? '#999' : 'inherit',
                                  textDecoration: (optimistic ? optimistic.status : task.status) === 'completed' ? 'line-through' : 'none'
                                }}>
                                  {task.name}
                                </span>
                                <Chip
                                  size="small"
                                  label={(optimistic ? optimistic.status : task.status).replace('_', ' ')}
                                  color={(optimistic ? optimistic.status : task.status) === 'completed' ? 'success' : (optimistic ? optimistic.status : task.status) === 'in_progress' ? 'primary' : 'default'}
                                  variant={(optimistic ? optimistic.status : task.status) === 'pending' ? 'outlined' : 'filled'}
                                  component="span"
                                />
                              </span>
                            }
                            secondary={
                              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '0.75rem', color: '#666' }}>
                                    {task.description}
                                  </span>
                                  <Chip
                                    size="small"
                                    label={`${task.estimatedDuration}h`}
                                    variant="outlined"
                                    component="span"
                                  />
                                  {task.actualDuration && (
                                    <Chip
                                      size="small"
                                      label={`Actual: ${task.actualDuration}h`}
                                      color="primary"
                                      variant="outlined"
                                      component="span"
                                    />
                                  )}
                                </span>

                                {/* No assign icon when unassigned */}

                                {/* Assignee and Due info display (when assigned) */}
                                {((task as any).assignedTo || (task as any).dueDate) && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    {(task as any).assignedTo && (() => {
                                      const at = (task as any).assignedTo;
                                      let display = '';
                                      if (at && typeof at === 'object') {
                                        const fn = (at.firstName || '').trim();
                                        const ln = (at.lastName || '').trim();
                                        display = (fn || ln) ? `${fn} ${ln}`.trim() : (at.email || at._id || '');
                                      } else {
                                        const assignee = agencyUsers.find((u: any) => u._id === at);
                                        display = assignee ? `${assignee.firstName} ${assignee.lastName}` : at;
                                      }
                                      return <Chip size="small" color="default" variant="outlined" label={`Assignee: ${display}`} component="span" />;
                                    })()}
                                    {(task as any).dueDate && (
                                      <Chip
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        component="span"
                                        label={`Due: ${new Date((task as any).dueDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                                      />
                                    )}
                                    {editable && propertyStatus !== 'archived' && (optimistic ? optimistic.status : task.status) !== 'completed' && (
                                      <Tooltip title="Edit assignment">
                                        <span>
                                          <IconButton
                                            edge="end"
                                            size="small"
                                            onClick={() => {
                                              setAssignStageIdx(stageIndex);
                                              setAssignTaskIdx(actualTaskIndex);
                                              {
                                                const at = (task as any).assignedTo;
                                                const id = at && typeof at === 'object' ? (at._id || '') : (at || '');
                                                setAssignAssignee(id);
                                              }
                                              const existingDue = (task as any).dueDate ? new Date((task as any).dueDate) : null;
                                              setAssignDueDate(existingDue ? new Date(existingDue.getTime() - existingDue.getTimezoneOffset() * 60000).toISOString().slice(0,10) : '');
                                              setAssignDueTime(existingDue ? new Date(existingDue.getTime() - existingDue.getTimezoneOffset() * 60000).toISOString().slice(11,16) : '');
                                              setAssignTaskName(task.name || '');
                                              setAssignDialogOpen(true);
                                            }}
                                          >
                                            <SupervisorAccount fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                  </span>
                                )}

                                {/* Display completed task field values */}
                                {((optimistic ? optimistic.status : task.status) === 'completed' && task.fieldValues) && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    {task.fieldValues.saleType && (
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        component="span"
                                        label={`Sale Type: ${(() => {
                                          const saleTypeMap: Record<string, string> = {
                                            'off-market': 'Off-Market',
                                            'for sale': 'For Sale',
                                            'pre-auction': 'Pre-Auction',
                                            'auction': 'Auction',
                                            'rental': 'Rental'
                                          };
                                          return saleTypeMap[task.fieldValues.saleType] || task.fieldValues.saleType;
                                        })()}`}
                                      />
                                    )}
                                    {task.fieldValues.marketingFee && (
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        component="span"
                                        label={`Marketing Fee: $${Number(task.fieldValues.marketingFee).toLocaleString()}`}
                                      />
                                    )}
                                    {task.fieldValues.stagingFee && (
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        component="span"
                                        label={`Staging Fee: $${Number(task.fieldValues.stagingFee).toLocaleString()}`}
                                      />
                                    )}
                                    {task.fieldValues.commissionRate && (
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        component="span"
                                        label={`Commission: ${task.fieldValues.commissionRate}% ${task.fieldValues.gstInclusive ? '(GST incl.)' : '(plus GST)'}`}
                                      />
                                    )}
                                    {task.fieldValues.reservePrice && (
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        component="span"
                                        label={`Reserve Price: $${Number(task.fieldValues.reservePrice).toLocaleString()}`}
                                      />
                                    )}
                                    {(task.fieldValues.minWeeklyRent != null || task.fieldValues.maxWeeklyRent != null) && (
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        component="span"
                                        label={`Rent Range: $${Number(task.fieldValues.minWeeklyRent || 0).toLocaleString()}–$${Number(task.fieldValues.maxWeeklyRent || 0).toLocaleString()} /week`}
                                      />
                                    )}
                                    {(task.taskType === 'set_weekly_rent' || task.name === 'Set Weekly Rent') && task.fieldValues?.weeklyRent != null && (
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        component="span"
                                        label={`Weekly Rent: $${Number(task.fieldValues.weeklyRent).toLocaleString()}/week`}
                                      />
                                    )}
                                    {(task.fieldValues.managementFeePercent != null || task.fieldValues.lettingFeeWeeks != null || task.fieldValues.leaseRenewalFeePercent != null || task.fieldValues.inspectionFeeNote) && (
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        component="span"
                                        label={[
                                          task.fieldValues.managementFeePercent != null && `Mgmt ${task.fieldValues.managementFeePercent}%`,
                                          task.fieldValues.lettingFeeWeeks != null && `Letting ${task.fieldValues.lettingFeeWeeks}w`,
                                          task.fieldValues.leaseRenewalFeePercent != null && `Renewal ${task.fieldValues.leaseRenewalFeePercent}%`,
                                          task.fieldValues.inspectionFeeNote && `Inspection: ${task.fieldValues.inspectionFeeNote}`,
                                        ].filter(Boolean).join(' · ')}
                                      />
                                    )}
                                    {task.fieldValues.auctionOutcome && (
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        component="span"
                                        label={`Auction Outcome: ${(() => {
                                          const outcome = task.fieldValues.auctionOutcome;
                                          if (outcome === 'sold' || outcome === 'sold-prior-to-auction' || outcome === 'sold-at-auction' || outcome === 'sold-after-auction') {
                                            return outcome === 'sold-prior-to-auction' ? 'Sold Prior to Auction' :
                                                   outcome === 'sold-at-auction' ? 'Sold at Auction' :
                                                   outcome === 'sold-after-auction' ? 'Sold After Auction' : 'Sold';
                                          } else if (outcome === 'passed_in' || outcome === 'passed-in' || outcome === 'passed-in-vendor-bid') {
                                            return outcome === 'passed-in-vendor-bid' ? 'Passed In (Vendor Bid)' : 'Passed In';
                                          } else if (outcome === 'withdrawn') {
                                            return 'Withdrawn';
                                          }
                                          return outcome;
                                        })()}`}
                                      />
                                    )}
                                    {task.fieldValues.finalPrice && 
                                      (task.fieldValues.auctionOutcome === 'sold' || 
                                       task.fieldValues.auctionOutcome === 'sold-prior-to-auction' || 
                                       task.fieldValues.auctionOutcome === 'sold-at-auction' || 
                                       task.fieldValues.auctionOutcome === 'sold-after-auction' ||
                                       task.fieldValues.auctionOutcome === 'passed_in' || 
                                       task.fieldValues.auctionOutcome === 'passed-in' || 
                                       task.fieldValues.auctionOutcome === 'passed-in-vendor-bid') ? (
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        component="span"
                                        label={`Final Price: $${Number(task.fieldValues.finalPrice).toLocaleString()}`}
                                      />
                                    ) : null}
                                    {/* Display listing URLs for creating_listing task */}
                                    {task.taskType === 'creating_listing' && (
                                      <>
                                        {/* Show URLs from task fieldValues (saved manually or from posting results) */}
                                        {(task.fieldValues.realestateUrl || task.fieldValues.domainUrl || propertyListingUrls?.realestateUrl || propertyListingUrls?.domainUrl) && (
                                          <>
                                            {(task.fieldValues.realestateUrl || propertyListingUrls?.realestateUrl) && (
                                              <Chip
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                component="span"
                                                label={
                                                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <span>Realestate.com.au</span>
                                                    <a 
                                                      href={task.fieldValues.realestateUrl || propertyListingUrls?.realestateUrl} 
                                                      target="_blank" 
                                                      rel="noopener noreferrer"
                                                      onClick={(e) => e.stopPropagation()}
                                                      style={{ textDecoration: 'none', color: 'inherit' }}
                                                    >
                                                      <OpenInNewIcon fontSize="inherit" />
                                                    </a>
                                                  </Box>
                                                }
                                              />
                                            )}
                                            {(task.fieldValues.domainUrl || propertyListingUrls?.domainUrl) && (
                                              <Chip
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                component="span"
                                                label={
                                                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <span>Domain.com.au</span>
                                                    <a 
                                                      href={task.fieldValues.domainUrl || propertyListingUrls?.domainUrl} 
                                                      target="_blank" 
                                                      rel="noopener noreferrer"
                                                      onClick={(e) => e.stopPropagation()}
                                                      style={{ textDecoration: 'none', color: 'inherit' }}
                                                    >
                                                      <OpenInNewIcon fontSize="inherit" />
                                                    </a>
                                                  </Box>
                                                }
                                              />
                                            )}
                                          </>
                                        )}
                                      </>
                                    )}
                                    {/* Display posting status for creating_listing task - only show if progress > 0 or completed/failed */}
                                    {task.taskType === 'creating_listing' && task.fieldValues.postingStatus && 
                                     (task.fieldValues.postingStatus === 'completed' || 
                                      task.fieldValues.postingStatus === 'failed' ||
                                      (task.fieldValues.postingStatus === 'in_progress' && task.fieldValues.postingProgress !== undefined && task.fieldValues.postingProgress > 0)) && (
                                      <Chip
                                        size="small"
                                        color={
                                          task.fieldValues.postingStatus === 'completed' ? 'success' :
                                          task.fieldValues.postingStatus === 'failed' ? 'error' :
                                          task.fieldValues.postingStatus === 'in_progress' ? 'warning' :
                                          'default'
                                        }
                                        variant="outlined"
                                        component="span"
                                        label={
                                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <span>Posting: {task.fieldValues.postingStatus}</span>
                                            {task.fieldValues.postingProgress !== undefined && task.fieldValues.postingProgress > 0 && (
                                              <span style={{ fontSize: '0.75rem' }}>({task.fieldValues.postingProgress}%)</span>
                                            )}
                                          </Box>
                                        }
                                      />
                                    )}
                                  </span>
                                )}
                              </span>
                            }
                          />
                        </ListItem>

                        {/* Task comments panel – shown when user clicks "Show comments" */}
                        {openTaskComments[taskKey] && agencySlug && workflowId && propertyId && (
                          <Box sx={{ pl: 2, pr: 2, pb: 1 }}>
                            <WorkflowTaskComments
                              agencySlug={agencySlug}
                              propertyId={propertyId}
                              workflowId={workflowId}
                              stageIndex={stageIndex}
                              taskIndex={actualTaskIndex}
                              canEdit={editable && propertyStatus !== 'archived'}
                              author={user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.email}
                            />
                          </Box>
                        )}
                        
                        {/* Form components moved outside ListItemText to fix DOM nesting */}
                        {task.taskType === 'price_range_determination' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Enter the price range for this property:
                            </div>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                              <NumericFormat
                                label="Minimum Price"
                                value={dialogPriceRange.minPrice}
                                thousandSeparator
                                allowNegative={false}
                                onValueChange={(values: { value: string }) => {
                                  setDialogPriceRange(prev => ({ ...prev, minPrice: values.value }));
                                }}
                                customInput={TextField}
                                required
                                size="small"
                                sx={{ width: 150 }}
                                disabled={propertyStatus === 'archived'}
                              />
                              <span style={{ fontSize: '0.875rem' }}>to</span>
                              <NumericFormat
                                label="Maximum Price"
                                value={dialogPriceRange.maxPrice}
                                thousandSeparator
                                allowNegative={false}
                                onValueChange={(values: { value: string }) => {
                                  setDialogPriceRange(prev => ({ ...prev, maxPrice: values.value }));
                                }}
                                customInput={TextField}
                                required
                                size="small"
                                sx={{ width: 150 }}
                                disabled={propertyStatus === 'archived'}
                              />
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={
                                !dialogPriceRange.minPrice || 
                                !dialogPriceRange.maxPrice || 
                                Number(dialogPriceRange.minPrice) >= Number(dialogPriceRange.maxPrice) ||
                                propertyStatus === 'archived'
                              }
                              onClick={() => handlePriceRangeSubmit(stageIndex, actualTaskIndex)}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                            >
                              Set Price Range & Complete Task
                            </Button>
                          </Box>
                        )}

                        {task.taskType === 'rent_range_determination' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Enter the recommended weekly rent range for this property (AUD):
                            </div>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                              <NumericFormat
                                label="Minimum Weekly Rent"
                                value={dialogRentRange.minWeeklyRent}
                                thousandSeparator
                                allowNegative={false}
                                suffix=" /week"
                                onValueChange={(values: { value: string }) => {
                                  setDialogRentRange(prev => ({ ...prev, minWeeklyRent: values.value }));
                                }}
                                customInput={TextField}
                                required
                                size="small"
                                sx={{ width: 180 }}
                                disabled={propertyStatus === 'archived'}
                              />
                              <span style={{ fontSize: '0.875rem' }}>to</span>
                              <NumericFormat
                                label="Maximum Weekly Rent"
                                value={dialogRentRange.maxWeeklyRent}
                                thousandSeparator
                                allowNegative={false}
                                suffix=" /week"
                                onValueChange={(values: { value: string }) => {
                                  setDialogRentRange(prev => ({ ...prev, maxWeeklyRent: values.value }));
                                }}
                                customInput={TextField}
                                required
                                size="small"
                                sx={{ width: 180 }}
                                disabled={propertyStatus === 'archived'}
                              />
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={
                                !dialogRentRange.minWeeklyRent ||
                                !dialogRentRange.maxWeeklyRent ||
                                Number(dialogRentRange.minWeeklyRent) >= Number(dialogRentRange.maxWeeklyRent) ||
                                propertyStatus === 'archived'
                              }
                              onClick={() => handleRentRangeSubmit(stageIndex, actualTaskIndex)}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                            >
                              Set Rent Range & Complete Task
                            </Button>
                          </Box>
                        )}

                        {(task.taskType === 'set_weekly_rent' || (task.name === 'Set Weekly Rent' && task.requiredFields?.some((f: { name: string }) => f.name === 'weeklyRent'))) && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Set the advertised weekly rent for this rental property (AUD):
                            </div>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                              <NumericFormat
                                label="Weekly Rent (AUD)"
                                value={dialogWeeklyRent}
                                thousandSeparator
                                allowNegative={false}
                                prefix="$ "
                                suffix=" /week"
                                onValueChange={(values: { value: string }) => {
                                  setDialogWeeklyRent(values.value);
                                }}
                                customInput={TextField}
                                required
                                size="small"
                                sx={{ width: 200 }}
                                disabled={propertyStatus === 'archived'}
                              />
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={
                                !dialogWeeklyRent ||
                                Number(dialogWeeklyRent) <= 0 ||
                                propertyStatus === 'archived'
                              }
                              onClick={async () => {
                                const fieldValues = { weeklyRent: Number(dialogWeeklyRent) };
                                const token = localStorage.getItem('token');
                                try {
                                  const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                  const response = await fetch(url, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                                    },
                                    body: JSON.stringify({ status: 'completed', fieldValues }),
                                  });
                                  if (!response.ok) throw new Error('Failed to update task');
                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId, fieldValues);
                                  }
                                  setOptimisticTaskUpdates(prev => ({
                                    ...prev,
                                    [`${stageIndex}-${actualTaskIndex}`]: { status: 'completed', fieldValues },
                                  }));
                                  if (onTaskPatched) onTaskPatched();
                                  setDialogWeeklyRent('');
                                } catch (error) {
                                  console.error('Error updating task:', error);
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                            >
                              Set Weekly Rent & Complete Task
                            </Button>
                          </Box>
                        )}

                        {(task.taskType === 'set_date_available' || (task.name === 'Set Date Available' && task.requiredFields?.some((f: { name: string }) => f.name === 'dateAvailable'))) && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Set when the property is available for occupation (earliest start date for a new lease). This is shown on listings and the rental application form.
                            </div>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                              <TextField
                                label="Date Available"
                                type="date"
                                value={dialogDateAvailable}
                                onChange={(e) => setDialogDateAvailable(e.target.value)}
                                size="small"
                                sx={{ width: 200 }}
                                disabled={propertyStatus === 'archived'}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={!dialogDateAvailable || propertyStatus === 'archived'}
                              onClick={async () => {
                                const fieldValues = { dateAvailable: dialogDateAvailable };
                                const token = localStorage.getItem('token');
                                try {
                                  const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                  const response = await fetch(url, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                                    },
                                    body: JSON.stringify({ status: 'completed', fieldValues }),
                                  });
                                  if (!response.ok) throw new Error('Failed to update task');
                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId, fieldValues);
                                  }
                                  setOptimisticTaskUpdates(prev => ({
                                    ...prev,
                                    [`${stageIndex}-${actualTaskIndex}`]: { status: 'completed', fieldValues },
                                  }));
                                  if (onTaskPatched) onTaskPatched();
                                  setDialogDateAvailable('');
                                } catch (error) {
                                  console.error('Error updating task:', error);
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                            >
                              Set Date Available & Complete Task
                            </Button>
                          </Box>
                        )}
                        
                        {task.taskType === 'sale_type_determination' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Select the sale type for this property:
                            </div>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                              <InputLabel>Sale Type</InputLabel>
                              <Select
                                label="Sale Type"
                                value={saleTypeValue}
                                onChange={(e) => setSaleTypeValue(e.target.value)}
                                disabled={propertyStatus === 'archived'}
                              >
                                <MenuItem value="off-market">Off-Market</MenuItem>
                                <MenuItem value="for sale">For Sale</MenuItem>
                                <MenuItem value="pre-auction">Pre-Auction</MenuItem>
                                <MenuItem value="auction">Auction</MenuItem>
                                <MenuItem value="rental">Rental</MenuItem>
                              </Select>
                            </FormControl>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={!saleTypeValue || propertyStatus === 'archived'}
                              onClick={async () => {
                                try {
                                  const fieldValues = {
                                    saleType: saleTypeValue
                                  };
                                  
                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId, fieldValues);
                                  }
                                  setSaleTypeValue('');
                                  showSuccess('Sale type saved');
                                  setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues } }));
                                } catch (e) {
                                  console.error('Error saving sale type:', e);
                                  showError('Failed to save sale type');
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                              fullWidth
                            >
                              Save Sale Type & Complete Task
                            </Button>
                          </Box>
                        )}

                        {task.taskType === 'management_fees_determination' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Confirm property management fee rates and related settings:
                            </div>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                              <NumericFormat
                                label="Management Fee (% of rent)"
                                value={dialogManagementFees.managementFeePercent}
                                allowNegative={false}
                                suffix=" %"
                                onValueChange={(values: { value: string }) => {
                                  setDialogManagementFees(prev => ({ ...prev, managementFeePercent: values.value }));
                                }}
                                customInput={TextField}
                                required
                                size="small"
                                fullWidth
                                disabled={propertyStatus === 'archived'}
                              />
                              <NumericFormat
                                label="Letting Fee (weeks of rent)"
                                value={dialogManagementFees.lettingFeeWeeks}
                                allowNegative={false}
                                suffix=" weeks"
                                onValueChange={(values: { value: string }) => {
                                  setDialogManagementFees(prev => ({ ...prev, lettingFeeWeeks: values.value }));
                                }}
                                customInput={TextField}
                                size="small"
                                fullWidth
                                disabled={propertyStatus === 'archived'}
                              />
                              <NumericFormat
                                label="Lease renewal fee (% of rent)"
                                value={dialogManagementFees.leaseRenewalFeePercent}
                                allowNegative={false}
                                suffix=" %"
                                onValueChange={(values: { value: string }) => {
                                  setDialogManagementFees(prev => ({ ...prev, leaseRenewalFeePercent: values.value }));
                                }}
                                customInput={TextField}
                                size="small"
                                fullWidth
                                disabled={propertyStatus === 'archived'}
                              />
                              <TextField
                                label="Inspection fee (e.g. Included, or amount)"
                                value={dialogManagementFees.inspectionFeeNote}
                                onChange={(e) => setDialogManagementFees(prev => ({ ...prev, inspectionFeeNote: e.target.value }))}
                                size="small"
                                fullWidth
                                placeholder="e.g. Included, $50 per inspection"
                                disabled={propertyStatus === 'archived'}
                              />
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={!dialogManagementFees.managementFeePercent || propertyStatus === 'archived'}
                              onClick={() => handleManagementFeesSubmit(stageIndex, actualTaskIndex)}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                            >
                              Save Management Fees & Complete Task
                            </Button>
                          </Box>
                        )}
                        
                        {task.taskType === 'commission_rate' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Set the agent commission rate for this sale:
                            </div>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                              <TextField
                                label="Commission Rate (%)"
                                type="number"
                                value={commissionRateValue}
                                onChange={(e) => setCommissionRateValue(e.target.value)}
                                size="small"
                                required
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{ width: 180 }}
                                disabled={propertyStatus === 'archived'}
                              />
                              <FormControl size="small">
                                <InputLabel>GST</InputLabel>
                                <Select
                                  label="GST"
                                  value={commissionGstInclusive ? 'inclusive' : 'plus'}
                                  onChange={(e) => setCommissionGstInclusive(e.target.value === 'inclusive')}
                                  disabled={propertyStatus === 'archived'}
                                  sx={{ width: 160 }}
                                >
                                  <MenuItem value="inclusive">GST Inclusive</MenuItem>
                                  <MenuItem value="plus">Plus GST</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={
                                !commissionRateValue || isNaN(parseFloat(commissionRateValue)) || parseFloat(commissionRateValue) <= 0 || propertyStatus === 'archived'
                              }
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                  const response = await fetch(url, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                    },
                                    body: JSON.stringify({
                                      status: 'completed',
                                      fieldValues: {
                                        commissionRate: parseFloat(commissionRateValue),
                                        gstInclusive: commissionGstInclusive
                                      }
                                    })
                                  });
                                  if (!response.ok) throw new Error('Failed to update task');
                                  const updatedWorkflow = await response.json();
                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId);
                                  }
                                  setCommissionRateValue('');
                                  setCommissionGstInclusive(true);
                                  showSuccess('Commission rate saved');
                                  setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues: { commissionRate: parseFloat(commissionRateValue), gstInclusive: commissionGstInclusive } } }));
                                } catch (e) {
                                  console.error('Error saving commission rate:', e);
                                  showError('Failed to save commission rate');
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                            >
                              Save Commission & Complete Task
                            </Button>
                          </Box>
                        )}
                        
                        {((task.taskType === 'set_reserve_price') || (task.name && task.name.toLowerCase().includes('reserve price'))) && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Set the reserve price for this auction:
                            </div>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                              <NumericFormat
                                label="Reserve Price"
                                value={reservePriceValue}
                                thousandSeparator
                                allowNegative={false}
                                prefix="$"
                                onValueChange={(values: { value: string }) => {
                                  setReservePriceValue(values.value);
                                }}
                                customInput={TextField}
                                required
                                size="small"
                                sx={{ width: 200 }}
                                disabled={propertyStatus === 'archived'}
                              />
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={
                                !reservePriceValue || isNaN(parseFloat(reservePriceValue)) || parseFloat(reservePriceValue) <= 0 || propertyStatus === 'archived'
                              }
                              onClick={async () => {
                                try {
                                  const reservePriceNum = parseFloat(reservePriceValue);
                                  const token = localStorage.getItem('token');
                                  const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                  const response = await fetch(url, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                    },
                                    body: JSON.stringify({
                                      status: 'completed',
                                      fieldValues: {
                                        reservePrice: reservePriceNum
                                      }
                                    })
                                  });
                                  if (!response.ok) throw new Error('Failed to update task');
                                  const updatedWorkflow = await response.json();
                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId);
                                  }
                                  setReservePriceValue('');
                                  showSuccess('Reserve price saved');
                                  setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues: { reservePrice: reservePriceNum } } }));
                                } catch (e) {
                                  console.error('Error saving reserve price:', e);
                                  showError('Failed to save reserve price');
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                            >
                              Set Reserve Price & Complete Task
                            </Button>
                          </Box>
                        )}
                        
                        {task.taskType === 'creating_listing' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (() => {
                          const isFreePlan = ['Free', 'Agency Free', 'trial', 'Trial'].includes(currentPlan ?? '');
                          // Show URL inputs for:
                          // - Free plan: both URLs required
                          // - Subscribed plan: only for non-configured platforms (optional)
                          const requiresManualUrls = isFreePlan || hasRealestateConfig === false || hasDomainConfig === false;
                          const shouldAutoPost = !isFreePlan && (hasRealestateConfig || hasDomainConfig);
                          const showRealestateUrlField = isFreePlan || hasRealestateConfig === false;
                          const showDomainUrlField = isFreePlan || hasDomainConfig === false;
                          const taskFieldValues = task.fieldValues || {};
                          const taskKey = `${stageIndex}-${actualTaskIndex}`;
                          const listingStateKey = `listing_${taskKey}`;
                          
                          // Initialize state with URLs from task fieldValues, fallback to property.listingUrls if available
                          const initialRealestateUrl = taskFieldValues.realestateUrl || propertyListingUrls?.realestateUrl || '';
                          const initialDomainUrl = taskFieldValues.domainUrl || propertyListingUrls?.domainUrl || '';
                          
                          // Initialize price display options from property if available (generic for all platforms)
                          // Default to 'contact' (Hide price and display "Contact Agent")
                          const initialPriceDisplay = property?.listingPriceDisplay || 'contact';
                          const initialPriceDisplayText = property?.listingPriceDisplayText || '';
                          
                          // Initialize headline from property if available
                          const initialHeadline = property?.listingHeadline;
                          
                          // Initialize authority from property - default based on saleType
                          const initialAuthority =
                            property?.listingAuthority ||
                            (property?.saleType === 'auction'
                              ? 'auction'
                              : property?.saleType === 'for sale'
                              ? 'exclusive'
                              : undefined);
                          
                          // Initialize listing status - always 'active' for new listings
                          const initialListingStatus: 'active' | 'under_offer' | 'sold' | 'off_market' | 'deleted' = 'active';
                          
                          // Initialize setSaleDate from property if available
                          const initialSetSaleDate = property?.setSaleDate ? new Date(property.setSaleDate).toISOString().split('T')[0] : undefined;
                          
                          // Initialize videoLink from property if available
                          const initialVideoLink = property?.videoLink || '';
                          
                          const currentState = listingStates[listingStateKey] || {
                            realestateUrl: initialRealestateUrl,
                            domainUrl: initialDomainUrl,
                            isPosting: false,
                            listingPriceDisplay: initialPriceDisplay,
                            listingPriceDisplayText: initialPriceDisplayText,
                            listingAuthority: initialAuthority,
                            listingStatus: initialListingStatus,
                            listingHeadline: initialHeadline,
                            setSaleDate: initialSetSaleDate,
                            videoLink: initialVideoLink,
                            descriptionDraft: null as string | null,
                            descriptionSaving: false,
                            generatingAIDescription: false
                          };
                          
                          // Get the saved/original values (from task fieldValues or property listing URLs)
                          // These represent the last saved state
                          const savedRealestateUrl = taskFieldValues.realestateUrl || propertyListingUrls?.realestateUrl || '';
                          const savedDomainUrl = taskFieldValues.domainUrl || propertyListingUrls?.domainUrl || '';
                          
                          // Check if URLs have changed from the saved values
                          const hasChanges = (currentState.realestateUrl || '') !== savedRealestateUrl || 
                                            (currentState.domainUrl || '') !== savedDomainUrl;
                          
                          const isScheduleLoading = shouldAutoPost && scheduleLoading;
                          const listingValidationError = shouldAutoPost
                            ? getListingValidationErrorMessage(property || null, isScheduleLoading ? undefined : openSchedule)
                            : null;
                          
                          const updateListingState = (updates: Partial<typeof currentState>) => {
                            setListingStates(prev => ({
                              ...prev,
                              [listingStateKey]: { ...(prev[listingStateKey] || currentState), ...updates }
                            }));
                          };
                          
                          const canGenerateAIDescription = ['Professional', 'Agency Premium', 'Demo', 'professional', 'agency premium', 'demo'].includes(currentPlan ?? '');
                          
                          const completeDisabledReason = propertyStatus === 'archived'
                            ? 'Cannot update tasks for archived properties. Please restore the property first.'
                            : currentState.isPosting
                            ? 'Posting in progress...'
                            : isScheduleLoading
                            ? 'Loading inspection schedule...'
                            : listingValidationError
                            ? listingValidationError
                            : (!isFreePlan && hasRealestateConfig === null && hasDomainConfig === null)
                            ? 'Loading configuration...'
                            : (!isFreePlan && realestateConfigWarning && !hasRealestateConfig)
                            ? realestateConfigWarning
                            : (isFreePlan && (!currentState.realestateUrl || !currentState.domainUrl))
                            ? 'Both Realestate.com.au and Domain.com.au URLs are required'
                            : (!isFreePlan && !hasRealestateConfig && !hasDomainConfig && !currentState.realestateUrl && !currentState.domainUrl)
                            ? 'At least one listing URL is required, or configure at least one platform integration'
                            : '';
                          
                          return (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                                {isFreePlan ? (
                                  <>Add property listing URLs for Realestate.com.au and Domain.com.au. Both URLs are required to complete this task.</>
                                ) : (
                                  <>
                                    {hasRealestateConfig && hasDomainConfig ? (
                                      <>Post your property listing to Realestate.com.au and Domain.com.au. The listing URLs will be generated automatically after posting.</>
                                    ) : (
                                      <>
                                        {hasRealestateConfig ? (
                                          <>Realestate.com.au will be posted automatically. {showDomainUrlField && 'Add Domain.com.au URL if needed (optional).'}</>
                                        ) : hasDomainConfig ? (
                                          <>Domain.com.au will be posted automatically. {showRealestateUrlField && 'Add Realestate.com.au URL if needed (optional).'}</>
                                        ) : (
                                          <>Configure your integrations in <strong>3rd Party Integration</strong> settings to enable automatic posting, or add listing URLs manually (at least one required).</>
                                        )}
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                              
                              {/* URL input fields - show for free plan or non-configured platforms */}
                              {requiresManualUrls && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                                  {showRealestateUrlField && (
                                    <TextField
                                      label="Realestate.com.au URL"
                                      type="url"
                                      value={currentState.realestateUrl}
                                      onChange={(e) => updateListingState({ realestateUrl: e.target.value })}
                                      size="small"
                                      required={isFreePlan}
                                      fullWidth
                                      placeholder="https://www.realestate.com.au/property/..."
                                      disabled={propertyStatus === 'archived' || currentState.isPosting}
                                      helperText={
                                        isFreePlan 
                                          ? "Required for free plan" 
                                          : hasRealestateConfig 
                                            ? "Optional - will be posted automatically if configured"
                                            : "Optional - add URL if not configuring integration"
                                      }
                                    />
                                  )}
                                  {showDomainUrlField && (
                                    <TextField
                                      label="Domain.com.au URL"
                                      type="url"
                                      value={currentState.domainUrl}
                                      onChange={(e) => updateListingState({ domainUrl: e.target.value })}
                                      size="small"
                                      required={isFreePlan}
                                      fullWidth
                                      placeholder="https://www.domain.com.au/..."
                                      disabled={propertyStatus === 'archived' || currentState.isPosting}
                                      helperText={
                                        isFreePlan 
                                          ? "Required for free plan" 
                                          : hasDomainConfig 
                                            ? "Optional - will be posted automatically if configured"
                                            : "Optional - add URL if not configuring integration"
                                      }
                                    />
                                  )}
                                </Box>
                              )}
                              
                              {/* Description - required for listing; headline can be auto-generated from it */}
                              {(hasRealestateConfig || hasDomainConfig || showRealestateUrlField || showDomainUrlField) && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                    Description
                                  </Typography>
                                  {currentState.descriptionDraft === undefined || currentState.descriptionDraft === null ? (
                                    <>
                                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {(property?.description || '').trim() || 'No description yet. Add one so the headline can be auto-generated and the listing can be published.'}
                                      </Typography>
                                      {!property?.description?.trim() && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                          Required for publishing. If left empty, the listing cannot be posted.
                                        </Typography>
                                      )}
                                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => updateListingState({ descriptionDraft: property?.description || '' })}
                                          disabled={propertyStatus === 'archived' || currentState.isPosting}
                                        >
                                          Edit Description
                                        </Button>
                                        {canGenerateAIDescription && (
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            disabled={propertyStatus === 'archived' || currentState.isPosting || currentState.generatingAIDescription}
                                            onClick={() => {
                                              const hasExisting = (property?.description || '').trim().length > 0;
                                              const doGenerate = () => {
                                                updateListingState({ generatingAIDescription: true });
                                                api.post(`/agencies/${agencySlug}/properties/${propertyId}/generate-description`)
                                                  .then((response: { data?: { description?: string } }) => {
                                                    const generated = response.data?.description;
                                                    if (generated) {
                                                      const updates: Partial<typeof currentState> = {
                                                        descriptionDraft: generated,
                                                        generatingAIDescription: false,
                                                      };
                                                      // If no headline set yet, suggest one from the generated description
                                                      if (!currentState.listingHeadline || !currentState.listingHeadline.trim()) {
                                                        updates.listingHeadline = deriveHeadlineFromDescription(generated);
                                                      }
                                                      updateListingState(updates);
                                                      showSuccess('AI description generated. Review and save to apply.');
                                                    } else {
                                                      updateListingState({ generatingAIDescription: false });
                                                      showError('No description returned. Please try again.');
                                                    }
                                                  })
                                                  .catch((err: any) => {
                                                    updateListingState({ generatingAIDescription: false });
                                                    showError(err?.response?.data?.error || err?.message || 'Failed to generate AI description.');
                                                  });
                                              };
                                              if (hasExisting) {
                                                showConfirmDialog(
                                                  'Overwrite Existing Description? (may use AI credits)',
                                                  'This property already has a description. Generating a new AI description will replace it. Your agency has a limited number of free Description AI generations per user per day; once used, additional generations may be charged from agency AI credits according to the Service limits & fees settings. Continue?',
                                                  'Generate',
                                                  'primary',
                                                  doGenerate
                                                );
                                              } else {
                                                showConfirmDialog(
                                                  'Generate AI Description? (may use AI credits)',
                                                  'Your agency has a limited number of free Description AI generations per user per day; once used, additional generations may be charged from agency AI credits according to the Service limits & fees settings. Continue?',
                                                  'Generate',
                                                  'primary',
                                                  doGenerate
                                                );
                                              }
                                            }}
                                          >
                                            {currentState.generatingAIDescription ? 'Generating...' : 'AI Generate'}
                                          </Button>
                                        )}
                                      </Box>
                                    </>
                                  ) : (
                                    <>
                                      <TextField
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        value={currentState.descriptionDraft}
                                        onChange={(e) => updateListingState({ descriptionDraft: e.target.value })}
                                        placeholder="Describe the property..."
                                        disabled={currentState.descriptionSaving}
                                        sx={{ mb: 1 }}
                                      />
                                      <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          disabled={currentState.descriptionSaving}
                                          onClick={async () => {
                                            updateListingState({ descriptionSaving: true });
                                            try {
                                              await updateProperty(agencySlug, propertyId, {
                                                description: (currentState.descriptionDraft ?? property?.description ?? '').trim() || undefined
                                              });
                                              updateListingState({ descriptionDraft: null, descriptionSaving: false });
                                              onTaskPatched?.();
                                              showSuccess('Description saved');
                                            } catch (e: any) {
                                              showError(e?.response?.data?.error || e?.message || 'Failed to save description');
                                            } finally {
                                              updateListingState({ descriptionSaving: false });
                                            }
                                          }}
                                        >
                                          {currentState.descriptionSaving ? 'Saving...' : 'Save'}
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="text"
                                          disabled={currentState.descriptionSaving}
                                          onClick={() => updateListingState({ descriptionDraft: null })}
                                        >
                                          Cancel
                                        </Button>
                                        {canGenerateAIDescription && (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            disabled={currentState.descriptionSaving || currentState.generatingAIDescription}
                                            onClick={() => {
                                              const doGenerate = () => {
                                                updateListingState({ generatingAIDescription: true });
                                                api.post(`/agencies/${agencySlug}/properties/${propertyId}/generate-description`)
                                                  .then((response: { data?: { description?: string } }) => {
                                                    const generated = response.data?.description;
                                                    if (generated) {
                                                      const updates: Partial<typeof currentState> = {
                                                        descriptionDraft: generated,
                                                        generatingAIDescription: false,
                                                      };
                                                      if (!currentState.listingHeadline || !currentState.listingHeadline.trim()) {
                                                        updates.listingHeadline = deriveHeadlineFromDescription(generated);
                                                      }
                                                      updateListingState(updates);
                                                      showSuccess('AI description updated. Save to apply.');
                                                    } else {
                                                      updateListingState({ generatingAIDescription: false });
                                                    }
                                                  })
                                                  .catch((err: any) => {
                                                    updateListingState({ generatingAIDescription: false });
                                                    showError(err?.response?.data?.error || err?.message || 'Failed to generate.');
                                                  });
                                              };
                                              const hasContent = (currentState.descriptionDraft ?? '').trim().length > 0;
                                              if (hasContent) {
                                                showConfirmDialog(
                                                  'Replace current text? (may use AI credits)',
                                                  'Generate a new AI description and replace the current draft? Your agency has a limited number of free Description AI generations per user per day; once used, additional generations may be charged from agency AI credits according to the Service limits & fees settings.',
                                                  'Generate',
                                                  'primary',
                                                  doGenerate
                                                );
                                              } else {
                                                showConfirmDialog(
                                                  'Generate AI Description? (may use AI credits)',
                                                  'Your agency has a limited number of free Description AI generations per user per day; once used, additional generations may be charged from agency AI credits according to the Service limits & fees settings. Continue?',
                                                  'Generate',
                                                  'primary',
                                                  doGenerate
                                                );
                                              }
                                            }}
                                          >
                                            {currentState.generatingAIDescription ? 'Generating...' : 'AI Generate'}
                                          </Button>
                                        )}
                                      </Box>
                                    </>
                                  )}
                                </Box>
                              )}
                              
                              {/* Listing Headline - generic for all third-party platforms */}
                              {(hasRealestateConfig || hasDomainConfig || showRealestateUrlField || showDomainUrlField) && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                    Listing Headline
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Headline"
                                    placeholder="Enter a headline for the listing (optional - will be auto-generated from description if not provided)"
                                    value={currentState.listingHeadline || ''}
                                    onChange={(e) => updateListingState({ listingHeadline: e.target.value || undefined })}
                                    disabled={propertyStatus === 'archived' || currentState.isPosting}
                                    helperText="Maximum 200 characters. If left empty, the headline will be auto-generated from the description."
                                    inputProps={{ maxLength: 200 }}
                                  />
                                </Box>
                              )}
                              
                              {/* Price Display Options - generic for all third-party platforms */}
                              {(hasRealestateConfig || hasDomainConfig || showRealestateUrlField || showDomainUrlField) && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                    Price Display
                                  </Typography>
                                  <FormControl component="fieldset" fullWidth>
                                    <RadioGroup
                                      value={currentState.listingPriceDisplay || 'contact'}
                                      onChange={(e) => updateListingState({ listingPriceDisplay: e.target.value as 'exact' | 'range' | 'contact' })}
                                    >
                                      <FormControlLabel 
                                        value="exact" 
                                        control={<Radio size="small" />} 
                                        label="Show Actual price"
                                        disabled={propertyStatus === 'archived' || currentState.isPosting}
                                      />
                                      <FormControlLabel 
                                        value="range" 
                                        control={<Radio size="small" />} 
                                        label="Show text instead of price"
                                        disabled={propertyStatus === 'archived' || currentState.isPosting}
                                        sx={{ mb: currentState.listingPriceDisplay === 'range' ? 1 : 0 }}
                                      />
                                      {currentState.listingPriceDisplay === 'range' && (
                                        <TextField
                                          label="Custom text"
                                          value={currentState.listingPriceDisplayText || ''}
                                          onChange={(e) => updateListingState({ listingPriceDisplayText: e.target.value })}
                                          size="small"
                                          fullWidth
                                          disabled={propertyStatus === 'archived' || currentState.isPosting}
                                          sx={{ ml: 4, mt: 0.5, mb: 1 }}
                                          helperText="Text to display instead of the actual price (e.g., 'range', 'POA')"
                                        />
                                      )}
                                      <FormControlLabel 
                                        value="contact" 
                                        control={<Radio size="small" />} 
                                        label="Hide the price and display 'Contact Agent'"
                                        disabled={propertyStatus === 'archived' || currentState.isPosting}
                                      />
                                    </RadioGroup>
                                  </FormControl>
                                  <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
                                    <strong>Note:</strong> Listing prices need to be within 10% of the search price to display on listings.
                                  </Alert>
                                </Box>
                              )}
                              
                              {/* Authority - sale/auction only. REA: "The method of sale for this property" – not used for rental. */}
                              {(hasRealestateConfig || hasDomainConfig || showRealestateUrlField || showDomainUrlField) && property?.saleType !== 'rental' && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                    Authority
                                  </Typography>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Authority</InputLabel>
                                    <Select
                                      value={(() => {
                                        // Use currentState if set, otherwise derive from property
                                        if (currentState.listingAuthority) {
                                          return currentState.listingAuthority;
                                        }
                                        const derived =
                                          property?.listingAuthority ||
                                          (property?.saleType === 'auction'
                                            ? 'auction'
                                            : property?.saleType === 'for sale'
                                            ? 'exclusive'
                                            : undefined);
                                        return derived || '';
                                      })()}
                                      label="Authority"
                                      onChange={(e) => {
                                        const newAuthority = e.target.value;
                                        updateListingState({ 
                                          listingAuthority: (newAuthority ? newAuthority as 'auction' | 'sale' | 'setsale' | 'exclusive' | 'multilist' | 'conjunctional' | 'open' : undefined)
                                        });
                                      }}
                                      disabled={propertyStatus === 'archived' || currentState.isPosting}
                                      sx={{
                                        bgcolor: currentState.listingAuthority || (property?.saleType === 'auction' || property?.saleType === 'for sale') ? 'background.default' : 'warning.light',
                                        '& .MuiOutlinedInput-root': {
                                          borderColor: currentState.listingAuthority || (property?.saleType === 'auction' || property?.saleType === 'for sale') ? undefined : 'warning.main'
                                        }
                                      }}
                                    >
                                      <MenuItem value="">Select Authority</MenuItem>
                                      <MenuItem
                                        value="auction"
                                        title="The property is offered for sale via auction at a particular date and time."
                                      >
                                        Auction
                                      </MenuItem>
                                      <MenuItem
                                        value="exclusive"
                                        title="The property is “For Sale” via a single Agency."
                                      >
                                        Exclusive
                                      </MenuItem>
                                      <MenuItem
                                        value="multilist"
                                        title="The property is “For Sale” and listed with multiple agencies."
                                      >
                                        Multi List
                                      </MenuItem>
                                      <MenuItem
                                        value="conjunctional"
                                        title="The property is “For Sale” and listed with multiple agents working together - sales commission is shared amongst agents."
                                      >
                                        Conjunctional
                                      </MenuItem>
                                      <MenuItem
                                        value="open"
                                        title="More than one real estate agent may be employed to sell the property. The owner pays a commission only to the agent who finds the buyer."
                                      >
                                        Open
                                      </MenuItem>
                                      <MenuItem
                                        value="sale"
                                        title="Most common sale type - The property is offered for sale by negotiation."
                                      >
                                        Sale by Negotiation
                                      </MenuItem>
                                      <MenuItem
                                        value="setsale"
                                        title="The property is offered for sale up to the set sale date. (set sale date must be supplied where authority is setsale)."
                                      >
                                        Set Sale
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Method of sale for realestate.com.au (REAXML). Defaults to &quot;Auction&quot; or &quot;Exclusive&quot; for for-sale. Not used for rental listings.
                                  </Typography>
                                </Box>
                              )}

                              {/* Sold Details - portal-agnostic sale outcome */}
                              {propertyStatus === 'sold' && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                    Sold Details
                                  </Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label="Sold Date"
                                        type="date"
                                        defaultValue={
                                          property?.soldDetails?.soldDate
                                            ? new Date(property.soldDetails.soldDate).toISOString().split('T')[0]
                                            : property?.soldDate
                                            ? new Date(property.soldDate).toISOString().split('T')[0]
                                            : ''
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        disabled={propertyStatus === 'archived' || currentState.isPosting}
                                        onBlur={async (e) => {
                                          const value = e.target.value;
                                          try {
                                            await updateSoldDetails(agencySlug, property._id, {
                                              soldDate: value || undefined,
                                            });
                                          } catch (err) {
                                            console.error('Error updating sold date:', err);
                                          }
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label="Sold Price"
                                        type="number"
                                        defaultValue={
                                          property?.soldDetails?.soldPrice ?? property?.soldPrice ?? ''
                                        }
                                        InputProps={{
                                          startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                                        }}
                                        disabled={propertyStatus === 'archived' || currentState.isPosting}
                                        onBlur={async (e) => {
                                          const value = e.target.value;
                                          const numeric = value ? Number(value) : undefined;
                                          if (numeric !== undefined && isNaN(numeric)) {
                                            return;
                                          }
                                          try {
                                            await updateSoldDetails(agencySlug, property._id, {
                                              soldPrice: numeric,
                                            });
                                          } catch (err) {
                                            console.error('Error updating sold price:', err);
                                          }
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel id="sold-source-label">Sale Channel</InputLabel>
                                        <Select
                                          labelId="sold-source-label"
                                          label="Sale Channel"
                                          defaultValue={property?.soldDetails?.source || 'other'}
                                          disabled={propertyStatus === 'archived' || currentState.isPosting}
                                          onChange={async (e) => {
                                            try {
                                              await updateSoldDetails(agencySlug, property._id, {
                                                source: e.target.value as 'auction' | 'private_treaty' | 'other',
                                              });
                                            } catch (err) {
                                              console.error('Error updating sold source:', err);
                                            }
                                          }}
                                        >
                                          <MenuItem value="auction">Auction</MenuItem>
                                          <MenuItem value="private_treaty">Private treaty</MenuItem>
                                          <MenuItem value="other">Other / unknown</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            defaultChecked={property?.soldDetails?.showSoldPrice !== false}
                                            onChange={async (e) => {
                                              try {
                                                await updateSoldDetails(agencySlug, property._id, {
                                                  showSoldPrice: e.target.checked,
                                                });
                                              } catch (err) {
                                                console.error('Error updating sold price visibility:', err);
                                              }
                                            }}
                                            disabled={propertyStatus === 'archived' || currentState.isPosting}
                                            size="small"
                                          />
                                        }
                                        label="Show sold price on portals (where supported)"
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label="Internal notes (not sent to portals)"
                                        defaultValue={property?.soldDetails?.notes || ''}
                                        disabled={propertyStatus === 'archived' || currentState.isPosting}
                                        multiline
                                        rows={2}
                                        onBlur={async (e) => {
                                          try {
                                            await updateSoldDetails(agencySlug, property._id, {
                                              notes: e.target.value,
                                            });
                                          } catch (err) {
                                            console.error('Error updating sold notes:', err);
                                          }
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Sold details are stored for reporting even if realestate.com.au or Domain integrations
                                    are disabled.
                                  </Typography>
                                </Box>
                              )}

                              {/* Auction Date/Time - shown when authority is "auction" - fetched from auction scheduling */}
                              {(() => {
                                const currentAuthority =
                                  currentState.listingAuthority ||
                                  property?.listingAuthority ||
                                  (property?.saleType === 'auction'
                                    ? 'auction'
                                    : property?.saleType === 'for sale'
                                    ? 'exclusive'
                                    : '');
                                if (currentAuthority === 'auction') {
                                  // Format auction date - handle both string and Date object formats
                                  let auctionDate = '';
                                  if (property?.auctionDetails?.date) {
                                    const dateObj =
                                      typeof property.auctionDetails.date === 'string'
                                        ? new Date(property.auctionDetails.date)
                                        : new Date(property.auctionDetails.date);
                                    if (!isNaN(dateObj.getTime())) {
                                      auctionDate = dateObj.toISOString().split('T')[0];
                                    }
                                  }
                                  const auctionTime = property?.auctionDetails?.time || '';
                                  const hasAuctionDate = !!property?.auctionDetails?.date;

                                  return (
                                    <Box sx={{ mt: 2 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                          <TextField
                                            label="Auction Date"
                                            type="date"
                                            value={auctionDate}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            required
                                            disabled
                                            helperText={hasAuctionDate ? 'Set via Auction Scheduling' : undefined}
                                            error={!hasAuctionDate}
                                            sx={{
                                              '& .MuiInputBase-root': {
                                                bgcolor: hasAuctionDate ? 'background.default' : 'error.light',
                                              },
                                            }}
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <TextField
                                            label="Auction Time"
                                            type="time"
                                            value={auctionTime}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            required
                                            disabled
                                            helperText={hasAuctionDate ? 'Set via Auction Scheduling' : undefined}
                                            error={!hasAuctionDate}
                                            sx={{
                                              '& .MuiInputBase-root': {
                                                bgcolor: hasAuctionDate ? 'background.default' : 'error.light',
                                              },
                                            }}
                                          />
                                        </Grid>
                                      </Grid>
                                      {!hasAuctionDate && (
                                        <Typography
                                          variant="caption"
                                          color="error"
                                          sx={{ mt: 2, display: 'block' }}
                                        >
                                          Auction date and time are required. Set them via Auction Scheduling before the listing can be posted.
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                }
                                return null;
                              })()}

                              {/* Set Sale Date - shown when authority is "setsale" */}
                              {(() => {
                                const currentAuthority =
                                  currentState.listingAuthority ||
                                  property?.listingAuthority ||
                                  (property?.saleType === 'auction'
                                    ? 'auction'
                                    : property?.saleType === 'for sale'
                                    ? 'exclusive'
                                    : '');
                                if (currentAuthority === 'setsale') {
                                  const setSaleDate =
                                    currentState.setSaleDate ||
                                    (property?.setSaleDate
                                      ? new Date(property.setSaleDate).toISOString().split('T')[0]
                                      : '');

                                  return (
                                    <Box sx={{ mt: 2 }}>
                                      <TextField
                                        label="Set Sale Date"
                                        type="date"
                                        value={setSaleDate}
                                        onChange={(e) => {
                                          const newDate = e.target.value;
                                          updateListingState({
                                            setSaleDate: newDate || undefined,
                                          });
                                        }}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        disabled={propertyStatus === 'archived' || currentState.isPosting}
                                      />
                                    </Box>
                                  );
                                }
                                return null;
                              })()}
                              
                              {/* Video Link - for realestate.com.au */}
                              {(hasRealestateConfig || hasDomainConfig || showRealestateUrlField || showDomainUrlField) && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                    Video Link
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Video URL"
                                    placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
                                    value={currentState.videoLink || ''}
                                    onChange={(e) => updateListingState({ videoLink: e.target.value || undefined })}
                                    disabled={propertyStatus === 'archived' || currentState.isPosting}
                                    helperText="YouTube URLs will be embedded in the photo carousel on realestate.com.au. Leave empty to remove."
                                    type="url"
                                  />
                                </Box>
                              )}
                              
                              {/* Show posting error if available */}
                              {taskFieldValues.postingStatus === 'failed' && taskFieldValues.postingError && (
                                <Alert severity="error" sx={{ mb: 2, fontSize: '0.875rem' }}>
                                  Posting failed: {taskFieldValues.postingError}
                                </Alert>
                              )}
                              
                              {/* Show posting results if available */}
                              {taskFieldValues.postingResults && Array.isArray(taskFieldValues.postingResults) && (
                                <Box sx={{ mb: 2 }}>
                                  {taskFieldValues.postingResults.map((result: any, idx: number) => (
                                    <Box key={idx} sx={{ mb: 1 }}>
                                      {result.success ? (
                                        <Alert severity="success" sx={{ fontSize: '0.875rem' }}>
                                          Posted to {result.platform === 'realestate' ? 'Realestate.com.au' : 'Domain.com.au'} successfully
                                          {result.url && (
                                            <Button
                                              size="small"
                                              href={result.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              sx={{ ml: 1 }}
                                            >
                                              View Listing
                                            </Button>
                                          )}
                                        </Alert>
                                      ) : (
                                        <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
                                          Failed to post to {result.platform === 'realestate' ? 'Realestate.com.au' : 'Domain.com.au'}: {result.error || 'Unknown error'}
                                        </Alert>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                              
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                {/* Save URLs button for manual URL entry (free plan or subscribed without config) */}
                                {requiresManualUrls && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={
                                      propertyStatus === 'archived' || 
                                      currentState.isPosting || 
                                      !hasChanges
                                    }
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem('token');
                                        const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${taskIndex}`;
                                        const response = await fetch(url, {
                                          method: 'PATCH',
                                          headers: {
                                            'Content-Type': 'application/json',
                                            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                          },
                                          body: JSON.stringify({
                                            fieldValues: {
                                              realestateUrl: currentState.realestateUrl || undefined,
                                              domainUrl: currentState.domainUrl || undefined
                                            }
                                          })
                                        });
                                        if (!response.ok) {
                                          const errorData = await response.json();
                                          throw new Error(errorData.error || 'Failed to save URLs');
                                        }
                                        showSuccess('Listing URLs saved');
                                        // Refresh workflow data so task.fieldValues are updated
                                        // This will cause hasChanges to be recalculated correctly
                                        if (onTaskPatched) {
                                          onTaskPatched();
                                        }
                                      } catch (e: any) {
                                        console.error('Error saving listing URLs:', e);
                                        showError(e.message || 'Failed to save listing URLs');
                                      }
                                    }}
                                  >
                                    Save URLs
                                  </Button>
                                )}
                                <Tooltip title={completeDisabledReason}>
                                  <span>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      disabled={
                                        (isFreePlan && (!currentState.realestateUrl || !currentState.domainUrl)) || 
                                        (!isFreePlan && !hasRealestateConfig && !hasDomainConfig && !currentState.realestateUrl && !currentState.domainUrl) ||
                                        propertyStatus === 'archived' || 
                                        currentState.isPosting ||
                                    isScheduleLoading ||
                                        !!listingValidationError ||
                                        (!isFreePlan && hasRealestateConfig === null && hasDomainConfig === null) // Wait for config checks
                                      }
                                      onClick={async () => {
                                    try {
                                      // Validate required fields before completing the task
                                      if (listingValidationError) {
                                        showError(listingValidationError);
                                        return; // Stop here, don't complete the task
                                      }
                                      
                                      const proceedToComplete = async () => {
                                        updateListingState({ isPosting: true });
                                        const token = localStorage.getItem('token');
                                        const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${taskIndex}`;
                                        const response = await fetch(url, {
                                          method: 'PATCH',
                                          headers: {
                                            'Content-Type': 'application/json',
                                            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                          },
                                          body: JSON.stringify({
                                            status: 'completed',
                                            fieldValues: requiresManualUrls ? {
                                              realestateUrl: currentState.realestateUrl || undefined,
                                              domainUrl: currentState.domainUrl || undefined
                                            } : {},
                                            // Include generic price display options (will be saved to property, applies to all platforms)
                                            ...(currentState.listingPriceDisplay ? {
                                              listingPriceDisplay: currentState.listingPriceDisplay,
                                              ...(currentState.listingPriceDisplayText ? {
                                                listingPriceDisplayText: currentState.listingPriceDisplayText
                                              } : {})
                                            } : {}),
                                            // Include headline (will be saved to property)
                                            ...(currentState.listingHeadline !== undefined ? {
                                              listingHeadline: currentState.listingHeadline || undefined
                                            } : {}),
                                            // Include authority and setSaleDate (will be saved to property)
                                            // Listing status is always 'active' for new listings
                                            ...(currentState.listingAuthority ? {
                                              listingAuthority: currentState.listingAuthority
                                            } : {}),
                                            ...(currentState.setSaleDate ? {
                                              setSaleDate: currentState.setSaleDate ? new Date(currentState.setSaleDate).toISOString() : undefined
                                            } : {}),
                                            ...(currentState.videoLink !== undefined ? {
                                              videoLink: currentState.videoLink || undefined
                                            } : {}),
                                            listingStatus: 'active' // Always 'active' for new listings
                                          })
                                        });
                                        
                                        if (!response.ok) {
                                          const errorData = await response.json();
                                          throw new Error(errorData.error || 'Failed to complete task');
                                        }
                                        
                                        await response.json();
                                        if (onTaskStatusChange && workflowId) {
                                          onTaskStatusChange(stageIndex, taskIndex, 'completed', workflowId);
                                        }
                                        
                                        showSuccess(requiresManualUrls ? 'Listing URLs saved and task completed' : 'Listing posted and task completed');
                                        setOptimisticTaskUpdates(prev => ({ 
                                          ...prev, 
                                          [`${stageIndex}-${taskIndex}`]: { 
                                            ...(prev[`${stageIndex}-${taskIndex}`] || {}), 
                                            status: 'completed',
                                            fieldValues: requiresManualUrls ? {
                                              realestateUrl: currentState.realestateUrl || undefined,
                                              domainUrl: currentState.domainUrl || undefined
                                            } : {}
                                          } 
                                        }));
                                        if (requiresManualUrls) {
                                          updateListingState({ realestateUrl: '', domainUrl: '', isPosting: false });
                                        } else {
                                          updateListingState({ isPosting: false });
                                        }
                                      };
                                      
                                      if (!isFreePlan) {
                                        const hasRealestateProvided = !!currentState.realestateUrl?.trim();
                                        const hasDomainProvided = !!currentState.domainUrl?.trim();
                                        const hasRealestateListing = hasRealestateConfig || hasRealestateProvided;
                                        const hasDomainListing = hasDomainConfig || hasDomainProvided;
                                        const isOnlyOneListing = (hasRealestateListing ? 1 : 0) + (hasDomainListing ? 1 : 0) === 1;
                                        
                                        if (isOnlyOneListing) {
                                          const confirmMessage = hasRealestateListing
                                            ? 'Only Realestate.com.au will be posted or linked. You can add Domain.com.au later. Continue?'
                                            : 'Only Domain.com.au will be posted or linked. You can add Realestate.com.au later. Continue?';
                                          showConfirmDialog(
                                            'Confirm listing posting',
                                            confirmMessage,
                                            'Continue',
                                            'primary',
                                            () => {
                                              proceedToComplete().catch((err: any) => {
                                                console.error('Error completing listing task:', err);
                                                showError(err.message || 'Failed to complete task');
                                                updateListingState({ isPosting: false });
                                              });
                                            }
                                          );
                                          return;
                                        }
                                      }
                                      
                                      await proceedToComplete();
                                    } catch (e: any) {
                                      console.error('Error completing listing task:', e);
                                      showError(e.message || 'Failed to complete task');
                                      updateListingState({ isPosting: false });
                                    }
                                  }}
                                    >
                                      {currentState.isPosting ? 'Posting...' : (shouldAutoPost ? 'Post Listing & Complete Task' : 'Complete Task')}
                                    </Button>
                                  </span>
                                </Tooltip>
                              </Box>
                              {completeDisabledReason && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  {completeDisabledReason}
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                        
                        {task.taskType === 'information_sheet_preparation' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '8px' }}>
                              Upload Information Sheet Document:
                            </div>
                            <Button
                              variant="outlined"
                              startIcon={<Upload />}
                              disabled={informationSheetUploading || propertyStatus === 'archived'}
                              sx={{ mb: 1 }}
                              onClick={() => {
                                const input = document.getElementById(`information-sheet-upload-input-${taskKey}`) as HTMLInputElement;
                                if (input) input.click();
                              }}
                            >
                              {informationSheetUploading ? 'Uploading...' : informationSheetFile ? informationSheetFile.name : 'Select Information Sheet'}
                            </Button>
                            <input
                              type="file"
                              id={`information-sheet-upload-input-${taskKey}`}
                              accept=".pdf,.doc,.docx"
                              style={{ display: 'none' }}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  setInformationSheetUploading(true);
                                  setInformationSheetFile(file);
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  formData.append('type', 'information_sheet');
                                  const uploadResponse = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${stageIndex}/files`, formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  const { key, fileName } = uploadResponse.data;
                                  setInformationSheetFileId(key);
                                  if (!informationSheetFile) setInformationSheetFile(new File([file], fileName || file.name, { type: file.type }));
                                  showSuccess('Information sheet uploaded successfully');
                                } catch (err: any) {
                                  console.error('Error uploading information sheet:', err);
                                  showError(getErrorMessage(err));
                                  setInformationSheetFile(null);
                                } finally {
                                  setInformationSheetUploading(false);
                                  // Reset file input value
                                  e.target.value = '';
                                }
                              }}
                            />
                            {informationSheetFile && (
                              <Alert severity="success" sx={{ mt: 1 }}>
                                {informationSheetFile.name} uploaded successfully
                              </Alert>
                            )}
                            <Button
                              variant="contained"
                              size="small"
                              disabled={!informationSheetFileId || propertyStatus === 'archived'}
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                  
                                  const fieldValues: any = {};
                                  if (informationSheetFileId) {
                                    fieldValues.informationSheetFile = informationSheetFile?.name || 'information-sheet.pdf';
                                    fieldValues.informationSheetKey = informationSheetFileId;
                                  }
                                  
                                  const response = await fetch(url, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                    },
                                    body: JSON.stringify({
                                      status: 'completed',
                                      fieldValues
                                    })
                                  });
                                  
                                  if (!response.ok) {
                                    const errorData = await response.json();
                                    throw new Error(errorData.message || 'Failed to complete task');
                                  }
                                  
                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId);
                                  }
                                  setInformationSheetFile(null);
                                  setInformationSheetFileId(null);
                                  showSuccess('Information sheet task completed');
                                  setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues } }));
                                } catch (err: any) {
                                  console.error('Error completing information sheet task:', err);
                                  showError(err.message || 'Failed to complete task');
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                              sx={{ mt: 2 }}
                            >
                              Complete Task
                            </Button>
                          </Box>
                        )}

                        {/* Contractor Job: generic contractor job task type */}
                        {task.taskType === 'contractor_job' && task.contractorType && (() => {
                          const taskStatus = optimistic ? optimistic.status : task.status;
                          const isInProgress = taskStatus === 'in_progress';
                          const contractorTypeSlug = task.contractorType;
                          
                          // Find jobs for this specific contractor type
                          const relevantJobs = contractorJobs.filter(j => j.contractorType === contractorTypeSlug);
                          const hasJobs = relevantJobs.length > 0;
                          
                          return (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              {hasJobs ? (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                      {relevantJobs.length} {relevantJobs.length === 1 ? 'Contractor Job' : 'Contractor Jobs'}
                                    </Typography>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        const scrollKey = `property-scroll-${propertyId}`;
                                        sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                        navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
                                      }}
                                    >
                                      View All Jobs
                                    </Button>
                                  </Box>
                                  {relevantJobs.slice(0, 3).map((job) => {
                                    const displayStatus = getJobDisplayStatus(job);
                                    return (
                                      <Box key={job._id} sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                          <Typography variant="body2" fontWeight="bold">{job.contractorName}</Typography>
                                          <Chip size="small" label={displayStatus.status} color={displayStatus.color} sx={{ mt: 0.5 }} />
                                        </div>
                                      </Box>
                                    );
                                  })}
                                  {relevantJobs.length > 3 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                      +{relevantJobs.length - 3} more {relevantJobs.length - 3 === 1 ? 'job' : 'jobs'}
                                    </Typography>
                                  )}
                                </>
                              ) : isInProgress && (
                                <>
                                  <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '12px' }}>
                                    Create a contractor job for this task.
                                  </div>
                                  <Button
                                    variant="outlined"
                                    startIcon={<Assignment />}
                                    onClick={() => {
                                      const scrollKey = `property-scroll-${propertyId}`;
                                      sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                      navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs?newJob=${contractorTypeSlug}`);
                                    }}
                                    disabled={!editable || propertyStatus === 'archived'}
                                  >
                                    Create Contractor Job
                                  </Button>
                                </>
                              )}
                            </Box>
                          );
                        })()}

                        {/* Property Fixup: create and manage contractor jobs */}
                        {task.taskType === 'property_fixup' && (() => {
                          const taskStatus = optimistic ? optimistic.status : task.status;
                          const isInProgress = taskStatus === 'in_progress';
                          
                          // Get all contractor jobs - Property Fixup can have multiple jobs
                          const allJobs = contractorJobs.filter(j => j.contractorType !== 'photographer' && j.contractorType !== 'stylist' && j.contractorType !== 'property-sign-board-maker');
                          const hasJobs = allJobs.length > 0;
                          
                          return (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              {hasJobs ? (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                      {allJobs.length} {allJobs.length === 1 ? 'Contractor Job' : 'Contractor Jobs'}
                                    </Typography>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        const scrollKey = `property-scroll-${propertyId}`;
                                        sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                        navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
                                      }}
                                    >
                                      View All Jobs
                                    </Button>
                                  </Box>
                                  {allJobs.slice(0, 3).map((job) => {
                                    const displayStatus = getJobDisplayStatus(job);
                                    return (
                                      <Box key={job._id} sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                          <Typography variant="body2" fontWeight="bold">{job.contractorName}</Typography>
                                          <Chip size="small" label={displayStatus.status} color={displayStatus.color} sx={{ mt: 0.5 }} />
                                        </div>
                                      </Box>
                                    );
                                  })}
                                  {allJobs.length > 3 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                      +{allJobs.length - 3} more {allJobs.length - 3 === 1 ? 'job' : 'jobs'}
                                    </Typography>
                                  )}
                                </>
                              ) : isInProgress && (
                                <>
                                  <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '12px' }}>
                                    Manage repairs and improvements via Contractor Jobs.
                                  </div>
                                  <Button
                                    variant="outlined"
                                    startIcon={<Assignment />}
                                    onClick={() => {
                                      // Store current scroll position before navigating
                                      const scrollKey = `property-scroll-${propertyId}`;
                                      sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                      navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs?newJob=handyman`);
                                    }}
                                    disabled={!editable || propertyStatus === 'archived'}
                                  >
                                    Create Contractor Job
                                  </Button>
                                </>
                              )}
                            </Box>
                          );
                        })()}

                        {/* Property Photography: create or manage photography contractor job */}
                        {task.taskType === 'property_photography' && (() => {
                          const job = getJobForTask('property_photography');
                          const responses = (job as any)?.agencyQuote?.responses || [];
                          const hasResponses = responses.length > 0;
                          const displayStatus = job ? getJobDisplayStatus(job) : null;
                          const taskStatus = optimistic ? optimistic.status : task.status;
                          const isInProgress = taskStatus === 'in_progress';
                          
                          return (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              {job ? (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <div>
                                      <Typography variant="subtitle2" color="text.secondary">Job Status</Typography>
                                      {displayStatus && <Chip size="small" label={displayStatus.status} color={displayStatus.color} />}
                                    </div>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        const scrollKey = `property-scroll-${propertyId}`;
                                        sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                        navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
                                      }}
                                    >
                                      View Job Details
                                    </Button>
                                  </Box>
                                  
                                  {hasResponses && isInProgress && (
                                    <Box sx={{ mt: 2 }}>
                                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Quote Responses
                                      </Typography>
                                      {responses.map((response: any, idx: number) => (
                                        <Box key={idx} sx={{ p: 2, mb: 1, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <div>
                                            <Typography variant="body2" fontWeight="bold">{response.contractorName}</Typography>
                                            <Typography variant="body2" color="text.secondary">${response.amount} AUD</Typography>
                                            {response.notes && <Typography variant="caption">{response.notes}</Typography>}
                                          </div>
                                          {response.status === 'pending' && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                onClick={() => handleAcceptQuote(job._id, String(response.contractorId || response.contractorId?._id || ''))}
                                              >
                                                Approve
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                onClick={() => handleDeclineQuote(job._id, String(response.contractorId || response.contractorId?._id || ''))}
                                              >
                                                Decline
                                              </Button>
                                            </Box>
                                          )}
                                          {response.status === 'accepted' && (
                                            <Chip size="small" label="Approved" color="success" />
                                          )}
                                          {response.status === 'declined' && (
                                            <Chip size="small" label="Declined" color="error" />
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </>
                              ) : isInProgress && (
                                <>
                                  <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '12px' }}>
                                    Create a photography contractor job to schedule property photos and floor plans.
                                  </div>
                                  <Button
                                    variant="outlined"
                                    startIcon={<Assignment />}
                                    onClick={() => {
                                      const scrollKey = `property-scroll-${propertyId}`;
                                      sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                      navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs?newJob=photographer&sendQuotes=true`);
                                    }}
                                    disabled={!editable || propertyStatus === 'archived'}
                                  >
                                    Create Photography Job
                                  </Button>
                                </>
                              )}
                            </Box>
                          );
                        })()}

                        {/* Styling Arrangement: create or manage styling contractor job */}
                        {task.taskType === 'styling_arrangement' && (() => {
                          const job = getJobForTask('styling_arrangement');
                          const responses = (job as any)?.agencyQuote?.responses || [];
                          const hasResponses = responses.length > 0;
                          const displayStatus = job ? getJobDisplayStatus(job) : null;
                          const taskStatus = optimistic ? optimistic.status : task.status;
                          const isInProgress = taskStatus === 'in_progress';
                          
                          return (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              {job ? (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <div>
                                      <Typography variant="subtitle2" color="text.secondary">Job Status</Typography>
                                      {displayStatus && <Chip size="small" label={displayStatus.status} color={displayStatus.color} />}
                                    </div>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        const scrollKey = `property-scroll-${propertyId}`;
                                        sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                        navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
                                      }}
                                    >
                                      View Job Details
                                    </Button>
                                  </Box>
                                  
                                  {hasResponses && isInProgress && (
                                    <Box sx={{ mt: 2 }}>
                                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Quote Responses
                                      </Typography>
                                      {responses.map((response: any, idx: number) => (
                                        <Box key={idx} sx={{ p: 2, mb: 1, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <div>
                                            <Typography variant="body2" fontWeight="bold">{response.contractorName}</Typography>
                                            <Typography variant="body2" color="text.secondary">${response.amount} AUD</Typography>
                                            {response.notes && <Typography variant="caption">{response.notes}</Typography>}
                                          </div>
                                          {response.status === 'pending' && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                onClick={() => handleAcceptQuote(job._id, String(response.contractorId || response.contractorId?._id || ''))}
                                              >
                                                Approve
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                onClick={() => handleDeclineQuote(job._id, String(response.contractorId || response.contractorId?._id || ''))}
                                              >
                                                Decline
                                              </Button>
                                            </Box>
                                          )}
                                          {response.status === 'accepted' && (
                                            <Chip size="small" label="Approved" color="success" />
                                          )}
                                          {response.status === 'declined' && (
                                            <Chip size="small" label="Declined" color="error" />
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </>
                              ) : isInProgress && (
                                <>
                                  <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '12px' }}>
                                    Create a styling contractor job to arrange property styling.
                                  </div>
                                  <Button
                                    variant="outlined"
                                    startIcon={<Assignment />}
                                    onClick={() => {
                                      const scrollKey = `property-scroll-${propertyId}`;
                                      sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                      navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs?newJob=stylist&sendQuotes=true`);
                                    }}
                                    disabled={!editable || propertyStatus === 'archived'}
                                  >
                                    Create Styling Job
                                  </Button>
                                </>
                              )}
                            </Box>
                          );
                        })()}

                        {/* Order Sign Board: create or manage sign board maker contractor job */}
                        {task.taskType === 'sign_board_order' && (() => {
                          const job = getJobForTask('sign_board_order');
                          const responses = (job as any)?.agencyQuote?.responses || [];
                          const hasResponses = responses.length > 0;
                          const displayStatus = job ? getJobDisplayStatus(job) : null;
                          const taskStatus = optimistic ? optimistic.status : task.status;
                          const isInProgress = taskStatus === 'in_progress';
                          
                          return (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              {job ? (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <div>
                                      <Typography variant="subtitle2" color="text.secondary">Job Status</Typography>
                                      {displayStatus && <Chip size="small" label={displayStatus.status} color={displayStatus.color} />}
                                    </div>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        const scrollKey = `property-scroll-${propertyId}`;
                                        sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                        navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
                                      }}
                                    >
                                      View Job Details
                                    </Button>
                                  </Box>
                                  
                                  {hasResponses && isInProgress && (
                                    <Box sx={{ mt: 2 }}>
                                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Quote Responses
                                      </Typography>
                                      {responses.map((response: any, idx: number) => (
                                        <Box key={idx} sx={{ p: 2, mb: 1, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <div>
                                            <Typography variant="body2" fontWeight="bold">{response.contractorName}</Typography>
                                            <Typography variant="body2" color="text.secondary">${response.amount} AUD</Typography>
                                            {response.notes && <Typography variant="caption">{response.notes}</Typography>}
                                          </div>
                                          {response.status === 'pending' && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                onClick={() => handleAcceptQuote(job._id, String(response.contractorId || response.contractorId?._id || ''))}
                                              >
                                                Approve
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                onClick={() => handleDeclineQuote(job._id, String(response.contractorId || response.contractorId?._id || ''))}
                                              >
                                                Decline
                                              </Button>
                                            </Box>
                                          )}
                                          {response.status === 'accepted' && (
                                            <Chip size="small" label="Approved" color="success" />
                                          )}
                                          {response.status === 'declined' && (
                                            <Chip size="small" label="Declined" color="error" />
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </>
                              ) : isInProgress && (
                                <>
                                  <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '12px' }}>
                                    Create a sign board contractor job to order and install property sign board.
                                  </div>
                                  <Button
                                    variant="outlined"
                                    startIcon={<Assignment />}
                                    onClick={() => {
                                      const scrollKey = `property-scroll-${propertyId}`;
                                      sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                      navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs?newJob=property-sign-board-maker&sendQuotes=true`);
                                    }}
                                    disabled={!editable || propertyStatus === 'archived'}
                                  >
                                    Create Sign Board Job
                                  </Button>
                                </>
                              )}
                            </Box>
                          );
                        })()}

                        {/* Book with Auctioneer: create or manage auctioneer contractor job */}
                        {task.taskType === 'auction_booking' && (() => {
                          const job = getJobForTask('auction_booking');
                          const responses = (job as any)?.agencyQuote?.responses || [];
                          const hasResponses = responses.length > 0;
                          const displayStatus = job ? getJobDisplayStatus(job) : null;
                          const taskStatus = optimistic ? optimistic.status : task.status;
                          const isInProgress = taskStatus === 'in_progress';
                          
                          return (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              {job ? (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <div>
                                      <Typography variant="subtitle2" color="text.secondary">Job Status</Typography>
                                      {displayStatus && <Chip size="small" label={displayStatus.status} color={displayStatus.color} />}
                                    </div>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        const scrollKey = `property-scroll-${propertyId}`;
                                        sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                        navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs`);
                                      }}
                                    >
                                      View Job Details
                                    </Button>
                                  </Box>
                                  
                                  {hasResponses && isInProgress && (
                                    <Box sx={{ mt: 2 }}>
                                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Quote Responses
                                      </Typography>
                                      {responses.map((response: any, idx: number) => (
                                        <Box key={idx} sx={{ p: 2, mb: 1, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <div>
                                            <Typography variant="body2" fontWeight="bold">{response.contractorName}</Typography>
                                            <Typography variant="body2" color="text.secondary">${response.amount} AUD</Typography>
                                            {response.notes && <Typography variant="caption">{response.notes}</Typography>}
                                          </div>
                                          {response.status === 'pending' && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                onClick={() => handleAcceptQuote(job._id, String(response.contractorId || response.contractorId?._id || ''))}
                                              >
                                                Approve
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                onClick={() => handleDeclineQuote(job._id, String(response.contractorId || response.contractorId?._id || ''))}
                                              >
                                                Decline
                                              </Button>
                                            </Box>
                                          )}
                                          {response.status === 'accepted' && (
                                            <Chip size="small" label="Approved" color="success" />
                                          )}
                                          {response.status === 'declined' && (
                                            <Chip size="small" label="Declined" color="error" />
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </>
                              ) : isInProgress && (
                                <>
                                  <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '12px' }}>
                                    Create an auctioneer contractor job to book an auctioneer for the property auction.
                                  </div>
                                  <Button
                                    variant="outlined"
                                    startIcon={<Assignment />}
                                    onClick={() => {
                                      const scrollKey = `property-scroll-${propertyId}`;
                                      sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                      navigate(`/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs?newJob=auctioner&sendQuotes=true`);
                                    }}
                                    disabled={!editable || propertyStatus === 'archived'}
                                  >
                                    Create Auctioneer Job
                                  </Button>
                                </>
                              )}
                            </Box>
                          );
                        })()}

                        {task.taskType === 'marketing_fee' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Enter the agreed marketing fee (AUD):
                            </div>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                              <NumericFormat
                                customInput={TextField}
                                label="Marketing Fee (AUD)"
                                value={marketingFeeValue}
                                onValueChange={(values) => setMarketingFeeValue(values.value)}
                                thousandSeparator
                                prefix="$"
                                decimalScale={2}
                                size="small"
                                required
                                sx={{ width: 200 }}
                                disabled={propertyStatus === 'archived'}
                              />
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={!marketingFeeValue || isNaN(parseFloat(marketingFeeValue)) || parseFloat(marketingFeeValue) <= 0 || propertyStatus === 'archived'}
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                  const response = await fetch(url, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                    },
                                    body: JSON.stringify({
                                      status: 'completed',
                                      fieldValues: {
                                        marketingFee: parseFloat(marketingFeeValue)
                                      }
                                    })
                                  });
                                  if (!response.ok) throw new Error('Failed to update task');
                                  const updatedWorkflow = await response.json();
                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId);
                                  }
                                  setMarketingFeeValue('');
                                  showSuccess('Marketing fee saved');
                                  setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues: { marketingFee: parseFloat(marketingFeeValue) } } }));
                                } catch (e) {
                                  console.error('Error saving marketing fee:', e);
                                  showError('Failed to save marketing fee');
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                            >
                              Save Marketing Fee & Complete Task
                            </Button>
                          </Box>
                        )}

                        {task.taskType === 'staging_fee' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Enter the agreed staging/styling fee (AUD) - optional:
                            </div>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                              <NumericFormat
                                customInput={TextField}
                                label="Staging Fee (AUD) - Optional"
                                value={stagingFeeValue}
                                onValueChange={(values) => setStagingFeeValue(values.value)}
                                thousandSeparator
                                prefix="$"
                                decimalScale={2}
                                size="small"
                                sx={{ width: 200 }}
                                disabled={propertyStatus === 'archived'}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={!stagingFeeValue || isNaN(parseFloat(stagingFeeValue)) || parseFloat(stagingFeeValue) <= 0 || propertyStatus === 'archived'}
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                    const response = await fetch(url, {
                                      method: 'PATCH',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                      },
                                      body: JSON.stringify({
                                        status: 'completed',
                                        fieldValues: {
                                          stagingFee: parseFloat(stagingFeeValue)
                                        }
                                      })
                                    });
                                    if (!response.ok) throw new Error('Failed to update task');
                                    const updatedWorkflow = await response.json();
                                    if (onTaskStatusChange && workflowId) {
                                      onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId);
                                    }
                                    setStagingFeeValue('');
                                    showSuccess('Staging fee saved');
                                    setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues: { stagingFee: parseFloat(stagingFeeValue) } } }));
                                  } catch (e) {
                                    console.error('Error saving staging fee:', e);
                                    showError('Failed to save staging fee');
                                  }
                                }}
                                title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                              >
                                Save Staging Fee & Complete Task
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={propertyStatus === 'archived'}
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${taskIndex}`;
                                    const response = await fetch(url, {
                                      method: 'PATCH',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                      },
                                      body: JSON.stringify({
                                        status: 'completed',
                                        fieldValues: {}
                                      })
                                    });
                                    if (!response.ok) throw new Error('Failed to update task');
                                    const updatedWorkflow = await response.json();
                                    if (onTaskStatusChange && workflowId) {
                                      onTaskStatusChange(stageIndex, taskIndex, 'completed', workflowId);
                                    }
                                    setStagingFeeValue('');
                                    showSuccess('Task completed without staging fee');
                                    setOptimisticTaskUpdates(prev => ({ ...prev, [`${stageIndex}-${taskIndex}`]: { ...(prev[`${stageIndex}-${taskIndex}`] || {}), status: 'completed', fieldValues: {} } }));
                                  } catch (e) {
                                    console.error('Error completing task:', e);
                                    showError('Failed to complete task');
                                  }
                                }}
                                title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                              >
                                Complete Without Staging Fee
                              </Button>
                            </Box>
                          </Box>
                        )}
                        
                      {/* Present Findings task - Present marketing appraisal to vendor (email or in-person) */}
                        {task.taskType === 'present_findings' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Present marketing appraisal findings to property owner:
                            </div>
                            {(() => {
                              // Check if consultation report exists in property OR if the "Prepare Marketing Appraisal" task is completed
                              const hasPropertyReport = consultationReportExists;
                              let hasCompletedReportTask = false;
                              for (const stage of stages) {
                                const reportTask = stage.tasks.find((t: Task) => 
                                  (t.taskType === 'consultation_report' || t.name === 'Prepare Marketing Appraisal') && 
                                  t.status === 'completed'
                                );
                                if (reportTask) {
                                  hasCompletedReportTask = true;
                                  break;
                                }
                              }
                              const canSendReport = hasPropertyReport || hasCompletedReportTask;
                              return !canSendReport ? (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                  Please generate the marketing appraisal first before sending it to the vendor.
                                </Alert>
                              ) : (
                              <>
                                {(() => {
                                  // Extract all vendors from property
                                  const normalizeOwners = (owner: any): Array<{ name: string; email?: string }> => {
                                    if (!owner) return [];
                                    if (Array.isArray(owner)) {
                                      return owner.filter(o => o && o.name).map(o => ({ name: o.name, email: o.email }));
                                    }
                                    if (owner.name) {
                                      return [{ name: owner.name, email: owner.email }];
                                    }
                                    return [];
                                  };
                                  
                                  const allVendors = normalizeOwners(property?.owner);
                                  const vendorsWithEmail = allVendors.filter(v => v.email && v.email.includes('@'));
                                  const vendorsWithoutEmail = allVendors.filter(v => !v.email || !v.email.includes('@'));
                                  
                                  return (
                                    <>
                                      {vendorsWithEmail.length > 1 && (
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Select vendor(s) to send the marketing appraisal:
                                          </Typography>
                                          <FormGroup>
                                            {vendorsWithEmail.map((vendor, idx) => (
                                              <FormControlLabel
                                                key={idx}
                                                control={
                                                  <Checkbox
                                                    checked={selectedReportVendorEmails.has(vendor.email!)}
                                                    onChange={(e) => {
                                                      const newSelected = new Set(selectedReportVendorEmails);
                                                      if (e.target.checked) {
                                                        newSelected.add(vendor.email!);
                                                      } else {
                                                        newSelected.delete(vendor.email!);
                                                      }
                                                      setSelectedReportVendorEmails(newSelected);
                                                    }}
                                                  />
                                                }
                                                label={`${vendor.name} (${vendor.email})`}
                                              />
                                            ))}
                                          </FormGroup>
                                        </Box>
                                      )}
                                      
                                      {vendorsWithoutEmail.length > 0 && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                          The following vendor(s) don't have email addresses: {vendorsWithoutEmail.map(v => v.name).join(', ')}. 
                                          Please add email addresses to send them the report.
                                        </Alert>
                                      )}
                                      
                                      {vendorsWithEmail.length === 0 && (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                          No vendors with email addresses found. Please add vendor email addresses to the property to send the marketing appraisal.
                                        </Alert>
                                      )}
                                    </>
                                  );
                                })()}
                                
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<Email />}
                                    disabled={propertyStatus === 'archived' || sendingEmail || !canSendReport || selectedReportVendorEmails.size === 0}
                                    sx={{ textTransform: 'none' }}
                                  onClick={async () => {
                                    try {
                                      setSendingEmail(true);
                                      setEmailError(null);
                                      
                                      // Determine which emails to send to
                                      if (selectedReportVendorEmails.size === 0) {
                                        showError('Please select at least one vendor');
                                        setSendingEmail(false);
                                        return;
                                      }
                                      
                                      const emailsToSend = Array.from(selectedReportVendorEmails);
                                      
                                      // Validate emails before sending
                                      if (emailsToSend.length === 0) {
                                        showError('Please select at least one vendor email');
                                        setSendingEmail(false);
                                        return;
                                      }
                                      
                                      const validEmails = emailsToSend.filter(e => typeof e === 'string' && e.includes('@'));
                                      if (validEmails.length === 0) {
                                        showError('No valid email addresses selected');
                                        setSendingEmail(false);
                                        return;
                                      }
                                      
                                      // Send single POST request with all emails - backend will queue jobs
                                      const response = await api.post(
                                        `/agencies/${agencySlug}/properties/${propertyId}/consultation-report/send-email`,
                                        { emails: validEmails }
                                      );
                                      
                                      showSuccess(`Marketing appraisal email is being sent to ${validEmails.length} vendor(s). You will be notified when it completes.`);
                                      
                                      // Mark Present Findings as completed optimistically and via callback
                                      const taskKeyLocal = `${stageIndex}-${taskIndex}`;
                                      const now = new Date();
                                      setOptimisticTaskUpdates(prev => ({
                                        ...prev,
                                        [taskKeyLocal]: {
                                          ...(prev[taskKeyLocal] || {}),
                                          status: 'completed',
                                          fieldValues: {
                                            ...((prev[taskKeyLocal]?.fieldValues) || {}),
                                            emailSent: true,
                                            emailSentAt: now.toISOString(),
                                            vendorEmail: validEmails.join(', '), // Store all emails as comma-separated for backward compatibility
                                            vendorEmails: validEmails // Store as array for easier access
                                          }
                                        }
                                      }));
                                      if (onTaskStatusChange && workflowId) {
                                        onTaskStatusChange(stageIndex, taskIndex, 'completed', workflowId, {
                                          emailSent: true,
                                          emailSentAt: now.toISOString(),
                                          vendorEmails: validEmails,
                                          vendorEmail: validEmails.join(', ') // Backward compatibility
                                        });
                                      }
                                    } catch (e: any) {
                                      console.error('Error sending email:', e);
                                      const errorMessage = e.response?.data?.error || e.message || 'Failed to send email';
                                      setEmailError(errorMessage);
                                      showError(errorMessage);
                                    } finally {
                                      setSendingEmail(false);
                                    }
                                  }}
                                  title={propertyStatus === 'archived' ? 'Cannot send emails for archived properties. Please restore the property first.' : ''}
                                >
                                    {sendingEmail ? 'Sending email...' : `Send via Email${selectedReportVendorEmails.size > 1 ? ` (${selectedReportVendorEmails.size})` : ''}`}
                                </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={propertyStatus === 'archived' || !canSendReport}
                                    sx={{ textTransform: 'none' }}
                                    onClick={async () => {
                                      try {
                                        // Mark Present Findings as completed for an in-person presentation
                                        const taskKeyLocal = `${stageIndex}-${taskIndex}`;
                                        const now = new Date();
                                        const fieldValues = {
                                          presentedInPerson: true,
                                          presentedAt: now.toISOString()
                                        };
                                        
                                        // Update optimistically
                                        setOptimisticTaskUpdates(prev => ({
                                          ...prev,
                                          [taskKeyLocal]: {
                                            ...(prev[taskKeyLocal] || {}),
                                            status: 'completed',
                                            fieldValues: {
                                              ...((prev[taskKeyLocal]?.fieldValues) || {}),
                                              ...fieldValues
                                            }
                                          }
                                        }));
                                        
                                        // Update backend with status and fieldValues
                                        const token = localStorage.getItem('token');
                                        const response = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${taskIndex}`, {
                                          method: 'PATCH',
                                          headers: {
                                            'Content-Type': 'application/json',
                                            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                          },
                                          body: JSON.stringify({
                                            status: 'completed',
                                            fieldValues: fieldValues
                                          })
                                        });
                                        
                                        if (!response.ok) {
                                          const errorData = await response.json();
                                          throw new Error(errorData.error || 'Failed to update task');
                                        }
                                        
                                        // Also call onTaskStatusChange for consistency with other task updates
                                        if (onTaskStatusChange && workflowId) {
                                          onTaskStatusChange(stageIndex, taskIndex, 'completed', workflowId);
                                        }
                                        
                                        showSuccess('Marked as presented in person');
                                      } catch (e: any) {
                                        console.error('Error marking in-person presentation:', e);
                                        showError(e.message || 'Failed to update task');
                                        // Revert optimistic update on error
                                        const taskKeyLocal = `${stageIndex}-${taskIndex}`;
                                        setOptimisticTaskUpdates(prev => {
                                          const updated = { ...prev };
                                          delete updated[taskKeyLocal];
                                          return updated;
                                        });
                                      }
                                    }}
                                  >
                                    Mark as Presented In Person
                                  </Button>
                                </Box>
                                {emailError && (
                                  <Alert severity="error" sx={{ mt: 1, width: '100%' }}>
                                    {emailError}
                                  </Alert>
                                )}
                              </>
                              );
                            })()}
                            {task.fieldValues?.vendorResponse && (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                Vendor Response: <strong>{task.fieldValues.vendorResponse}</strong>
                                {task.fieldValues.vendorResponseDate && (
                                  <> - {new Date(task.fieldValues.vendorResponseDate).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                                )}
                              </Alert>
                            )}
                          </Box>
                        )}
                        
                        {/* Upload Auction Contract tasks */}
                        {(task.name === 'Upload Auction Contract' || task.name === 'Upload Pre-Auction Contract') && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            {contractFileByStage[stageIndex] ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Typography variant="body2">Contract uploaded: <strong>{contractFileByStage[stageIndex]?.name}</strong></Typography>
                                <Button size="small" startIcon={<Download />} onClick={async () => {
                                  const id = contractFileByStage[stageIndex]?.id;
                                  if (!id || !workflowId) return;
                                  const url = downloadStageFile(agencySlug, propertyId, stageIndex, id, workflowId);
                                  try { await downloadFileAsBlob(url, contractFileByStage[stageIndex]?.name || 'contract.pdf'); } catch {}
                                }}>Download</Button>
                              </Box>
                            ) : (
                              <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '8px' }}>
                                {task.name === 'Upload Pre-Auction Contract' ? 'Upload Pre-Auction Contract:' : 'Upload Auction Contract:'}
                              </div>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Max 100 MB. PDF or Word supported.
                            </Typography>
                            {!contractFileByStage[stageIndex] && (
                              <>
                                <Button
                                  variant="outlined"
                                  startIcon={<Upload />}
                                  disabled={auctionContractUploading || propertyStatus === 'archived'}
                                  sx={{ mb: 1 }}
                                  onClick={() => {
                                    const input = document.getElementById(`auction-contract-upload-input-${taskKey}`) as HTMLInputElement;
                                    if (input) input.click();
                                  }}
                                >
                                  {auctionContractUploading ? 'Uploading...' : auctionContractFile ? auctionContractFile.name : 'Select Contract'}
                                </Button>
                                <input
                                  type="file"
                                  id={`auction-contract-upload-input-${taskKey}`}
                                  accept=".pdf,.doc,.docx"
                                  style={{ display: 'none' }}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    try {
                                      setAuctionContractUploading(true);
                                      setAuctionContractFile(file);
                                      const formData = new FormData();
                                      formData.append('file', file);
                                      formData.append('type', task.name === 'Upload Pre-Auction Contract' ? 'pre_auction_contract' : 'auction_contract');
                                      const uploadResponse = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${stageIndex}/files`, formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                      });
                                      const { key, fileName } = uploadResponse.data;
                                      setAuctionContractFileId(key);
                                      if (!auctionContractFile) setAuctionContractFile(new File([file], fileName || file.name, { type: file.type }));
                                      // Immediately mark task as completed
                                      try {
                                        const token = localStorage.getItem('token');
                                        const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                        const resp = await fetch(url, {
                                          method: 'PATCH',
                                          headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                                          body: JSON.stringify({ status: 'completed', fieldValues: { contractFileId: key, contractFileName: fileName || file.name } })
                                        });
                                        if (resp.ok) {
                                          setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues: { contractFileId: key, contractFileName: fileName || file.name } } }));
                                          // Trigger parent refresh so StageFiles and aggregated Files update
                                          if (onTaskPatched) onTaskPatched();
                                          setAuctionContractFile(null);
                                        }
                                      } catch {}
                                      // Update local map to reflect uploaded file
                                      setContractFileByStage(prev => ({ ...prev, [stageIndex]: { id: key, name: fileName || file.name } }));
                                      showSuccess('Contract uploaded and task completed');
                                    } catch (err: any) {
                                      console.error('Error uploading contract:', err);
                                      showError(getErrorMessage(err));
                                      setAuctionContractFile(null);
                                    } finally {
                                      setAuctionContractUploading(false);
                                      // Reset file input value
                                      e.target.value = '';
                                    }
                                  }}
                                />
                              </>
                            )}
                            {auctionContractFile && (
                              <Alert severity="success" sx={{ mt: 1 }}>
                                {auctionContractFile.name} uploaded successfully
                              </Alert>
                            )}
                          </Box>
                        )}

                        {/* Contract Upload task */}
                        {task.taskType === 'contract_upload' && 
                         task.name !== 'Upload Auction Contract' && 
                         task.name !== 'Upload Pre-Auction Contract' && 
                         (optimistic ? optimistic.status : task.status) !== 'pending' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            {(task.fieldValues?.contractFile || contractUploadByTask[taskKey]?.fileName) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Typography variant="body2">
                                  Contract uploaded: <strong>{task.fieldValues?.contractFile || contractUploadByTask[taskKey]?.fileName}</strong>
                                </Typography>
                                {task.fieldValues?.contractKey && (
                                  <Button
                                    size="small"
                                    startIcon={<Download />}
                                    onClick={async () => {
                                      try {
                                        // Fetch stage files to find the contract file by key
                                        if (!workflowId) {
                                          showError('Workflow ID is missing');
                                          return;
                                        }
                                        const stageFiles = await listStageFiles(agencySlug, propertyId, stageIndex, workflowId);
                                        const contractFile = stageFiles.files?.find((f: any) => f.key === task.fieldValues?.contractKey || (f.type === 'sale_contract' || f.type === 'rental_contract'));
                                        if (contractFile?._id) {
                                          const downloadUrl = downloadStageFile(agencySlug, propertyId, stageIndex, contractFile._id, workflowId);
                                          await downloadFileAsBlob(downloadUrl, contractFile.fileName || task.fieldValues?.contractFile || 'contract.pdf');
                                        } else {
                                          showError('Contract file not found in stage files');
                                        }
                                      } catch (err: any) {
                                        console.error('Error downloading contract:', err);
                                        showError(getErrorMessage(err));
                                      }
                                    }}
                                  >
                                    Download
                                  </Button>
                                )}
                              </Box>
                            ) : (
                              <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '8px' }}>
                                Upload Contract:
                              </div>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Max 100 MB. PDF or Word supported.
                            </Typography>
                            {!task.fieldValues?.contractFile && !contractUploadByTask[taskKey]?.fileName && (
                              <>
                                <Button
                                  variant="outlined"
                                  startIcon={<Upload />}
                                  disabled={contractUploading || propertyStatus === 'archived'}
                                  sx={{ mb: 1 }}
                                  onClick={() => {
                                    const input = document.getElementById(`contract-upload-input-${taskKey}`) as HTMLInputElement;
                                    if (input) input.click();
                                  }}
                                >
                                  {contractUploading ? 'Uploading...' : contractUploadFile ? contractUploadFile.name : 'Select Contract'}
                                </Button>
                                <input
                                  type="file"
                                  id={`contract-upload-input-${taskKey}`}
                                  accept=".pdf,.doc,.docx"
                                  style={{ display: 'none' }}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    try {
                                      setContractUploading(true);
                                      setContractUploadFile(file);
                                      const formData = new FormData();
                                      formData.append('file', file);
                                      formData.append('taskType', 'contract_upload');
                                      const uploadResponse = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/upload`, formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                      });
                                      const { key, fileName } = uploadResponse.data;
                                      
                                      // Update task with contract file and key
                                      const token = localStorage.getItem('token');
                                      const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                      const resp = await fetch(url, {
                                        method: 'PATCH',
                                        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                                        body: JSON.stringify({ 
                                          status: 'completed', 
                                          fieldValues: { 
                                            contractFile: fileName || file.name, 
                                            contractKey: key 
                                          } 
                                        })
                                      });
                                      if (resp.ok) {
                                        setOptimisticTaskUpdates(prev => ({ 
                                          ...prev, 
                                          [taskKey]: { 
                                            ...(prev[taskKey] || {}), 
                                            status: 'completed', 
                                            fieldValues: { 
                                              contractFile: fileName || file.name, 
                                              contractKey: key 
                                            } 
                                          } 
                                        }));
                                        setContractUploadByTask(prev => ({ 
                                          ...prev, 
                                          [taskKey]: { 
                                            fileName: fileName || file.name, 
                                            key 
                                          } 
                                        }));
                                        if (onTaskPatched) onTaskPatched();
                                        setContractUploadFile(null);
                                      }
                                      showSuccess('Contract uploaded and task completed');
                                    } catch (err: any) {
                                      console.error('Error uploading contract:', err);
                                      showError(getErrorMessage(err));
                                      setContractUploadFile(null);
                                    } finally {
                                      setContractUploading(false);
                                      // Reset file input value so same file can be selected again if needed
                                      e.target.value = '';
                                    }
                                  }}
                                />
                              </>
                            )}
                            {contractUploadFile && !task.fieldValues?.contractFile && !contractUploadByTask[taskKey]?.fileName && (
                              <Alert severity="success" sx={{ mt: 1 }}>
                                {contractUploadFile.name} uploaded successfully
                              </Alert>
                            )}
                          </Box>
                        )}

                        {/* Open Scheduling task */}
                        {task.taskType === 'open_scheduling' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (() => {
                          // Check if schedule exists and has sessions
                          const hasSchedule = openSchedule?._id && openSchedule.sessions && openSchedule.sessions.length > 0;
                          
                          // For Post-Auction stage, check auction outcome
                          const isPostAuctionStage = stage.name === 'Post-Auction' || stage.name?.toLowerCase().includes('post-auction');
                          let auctionOutcome: string | undefined;
                          if (isPostAuctionStage) {
                            const recordResultsTask = stage.tasks.find((t: any) => 
                              t.name === 'Record Results' || t.taskType === 'auction_results'
                            );
                            auctionOutcome = recordResultsTask?.fieldValues?.auctionOutcome;
                          }
                          
                          // For Post-Auction stage with non-successful auction, require open scheduling
                          // For sold auctions or pre-auction open scheduling, auction scheduling is required
                          const isNonSuccessfulAuction = 
                            auctionOutcome === 'passed_in' || 
                            auctionOutcome === 'passed-in' || 
                            auctionOutcome === 'passed-in-vendor-bid' || 
                            auctionOutcome === 'withdrawn';
                          const isSoldAuction = 
                            auctionOutcome === 'sold' || 
                            auctionOutcome === 'sold-prior-to-auction' || 
                            auctionOutcome === 'sold-at-auction' || 
                            auctionOutcome === 'sold-after-auction';
                          
                          // Check if auction scheduling exists (if it's an auction property and not a post-auction non-successful auction)
                          const hasAuctionSchedule = (isPostAuctionStage && isNonSuccessfulAuction)
                            ? true // For non-successful auctions, auction scheduling is not required
                            : (propertySaleType === 'auction' 
                              ? (openSchedule?.auctionDate && openSchedule?.auctionTime) || 
                                (openSchedule?.sessions?.some((s: any) => s.isAuction === true))
                              : true); // Not an auction property, so auction scheduling is not required
                          
                          // For Post-Auction non-successful auctions, require open scheduling
                          // For sold auctions, open scheduling is not required
                          const requiresOpenSchedule = isPostAuctionStage 
                            ? isNonSuccessfulAuction // Require only for non-successful auctions
                            : true; // For pre-auction, always require
                          
                          // Check if we can complete the task
                          const canCompleteTask = (!requiresOpenSchedule || hasSchedule) && hasAuctionSchedule && propertyStatus !== 'archived';
                          
                          // Get validation messages
                          const validationMessages: string[] = [];
                          if (requiresOpenSchedule && !hasSchedule) {
                            if (isPostAuctionStage && isNonSuccessfulAuction) {
                              validationMessages.push('Open house schedule must be created for private treaty sale after non-successful auction.');
                            } else {
                              validationMessages.push('Open house schedule must be created with at least one session.');
                            }
                          }
                          if (!hasAuctionSchedule && propertySaleType === 'auction' && !(isPostAuctionStage && isNonSuccessfulAuction)) {
                            validationMessages.push('Auction date, time, and location must be scheduled.');
                          }
                          
                          return (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '8px' }}>
                                {isPostAuctionStage && isNonSuccessfulAuction 
                                  ? 'Create open house schedule for private treaty sale after non-successful auction.'
                                  : isPostAuctionStage && isSoldAuction
                                  ? 'Update property listing status. Open house schedule is not required for sold auctions.'
                                  : `Schedule property open house dates and times. ${propertySaleType === 'auction' ? 'For auction properties, also schedule the auction date, time, and location in the Open Schedule page.' : ''}`}
                              </div>
                              
                              {scheduleLoading && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                  Checking schedule status...
                                </Alert>
                              )}
                              
                              {!scheduleLoading && validationMessages.length > 0 && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                  <Typography variant="body2" component="div">
                                    <strong>Cannot complete task yet:</strong>
                                    <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                                      {validationMessages.map((msg, idx) => (
                                        <li key={idx}>{msg}</li>
                                      ))}
                                    </ul>
                                  </Typography>
                                </Alert>
                              )}
                              
                              {/* Only show "Go to Open Schedule" for non-sold cases (passed-in or withdrawn) */}
                              {isPostAuctionStage && isNonSuccessfulAuction && (
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                  <Button
                                    variant="outlined"
                                    onClick={() => {
                                      const scrollKey = `property-scroll-${propertyId}`;
                                      sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                      navigate(`/agencies/${agencySlug}/properties/${propertyId}/open-schedule`);
                                    }}
                                  >
                                    Go to Open Schedule
                                  </Button>
                                </Box>
                              )}
                              <Button
                                variant="contained"
                                size="small"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                    const response = await fetch(url, {
                                      method: 'PATCH',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                      },
                                      body: JSON.stringify({ status: 'completed', fieldValues: {} })
                                    });
                                    if (!response.ok) {
                                      const errorData = await response.json().catch(() => ({ error: 'Failed to update task' }));
                                      throw new Error(errorData.error || 'Failed to update task');
                                    }
                                    if (onTaskStatusChange && workflowId) onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId);
                                    showSuccess('Open scheduling marked complete');
                                    setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed' } }));
                                  } catch (e: any) {
                                    console.error('Error completing open scheduling:', e);
                                    showError(e.message || 'Failed to complete task');
                                  }
                                }}
                                title={
                                  propertyStatus === 'archived' 
                                    ? 'Cannot update tasks for archived properties. Please restore the property first.'
                                    : validationMessages.length > 0
                                    ? validationMessages.join(' ')
                                    : ''
                                }
                                disabled={!canCompleteTask || scheduleLoading}
                                fullWidth
                              >
                                Complete Task
                              </Button>
                            </Box>
                          );
                        })()}

                        {/* Auction Decision task */}
                        {task.taskType === 'auction_decision' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                              <InputLabel>Decision</InputLabel>
                              <Select label="Decision" value={auctionDecision} onChange={(e) => setAuctionDecision(e.target.value)} disabled={propertyStatus === 'archived'}>
                                <MenuItem value="proceed_to_auction">Proceed to Auction</MenuItem>
                                <MenuItem value="switch_to_for_sale">Switch to For Sale</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              label="Notes (optional)"
                              size="small"
                              fullWidth
                              value={auctionNotes}
                              onChange={(e) => setAuctionNotes(e.target.value)}
                              disabled={propertyStatus === 'archived'}
                              sx={{ mb: 2 }}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              disabled={!auctionDecision || propertyStatus === 'archived'}
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                  const response = await fetch(url, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                    },
                                    body: JSON.stringify({ status: 'completed', fieldValues: { decision: auctionDecision, auctionNotes } })
                                  });
                                  if (!response.ok) throw new Error('Failed to update task');
                                  if (onTaskStatusChange && workflowId) onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId);
                                  setAuctionDecision('');
                                  setAuctionNotes('');
                                  showSuccess('Decision saved');
                                  setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues: { decision: auctionDecision, auctionNotes } } }));
                                } catch (e) {
                                  console.error('Error saving decision:', e);
                                  showError('Failed to save decision');
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                              fullWidth
                            >
                              Save Decision & Complete Task
                            </Button>
                          </Box>
                        )}

                        {/* Rental Application Management helpers */}
                        {propertySaleType === 'rental' &&
                          stage.name === 'Application Management' &&
                          ['Review Applications', 'Shortlist Applicants', 'Select Preferred Applicant', 'Landlord Approval & Handover to Lease'].includes(task.name) &&
                          (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {task.name === 'Review Applications'
                                ? 'Open the Rental applications screen to review each submitted application and supporting documents.'
                                : task.name === 'Shortlist Applicants'
                                ? 'Use the Rental applications screen to mark suitable applicants as "shortlisted".'
                                : task.name === 'Select Preferred Applicant'
                                ? 'Use the Rental applications screen to approve the preferred applicant (agent decision).'
                                : 'Use the Rental applications screen to record the landlord’s decision and then continue to Lease Preparation.'}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                const scrollKey = `property-scroll-${propertyId}`;
                                sessionStorage.setItem(scrollKey, window.scrollY.toString());
                                navigate(`/agencies/${agencySlug}/properties/${propertyId}/rental-applications`);
                              }}
                              disabled={propertyStatus === 'archived'}
                            >
                              Open rental applications
                            </Button>
                          </Box>
                        )}

                        {/* Auction Results task */}
                        {task.taskType === 'auction_results' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              Record Auction Results
                            </Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                              <InputLabel>Auction Outcome</InputLabel>
                              <Select 
                                label="Auction Outcome" 
                                value={auctionOutcome} 
                                onChange={(e) => {
                                  setAuctionOutcome(e.target.value);
                                  const newValue = e.target.value;
                                  // Clear fields based on outcome type
                                  if (newValue === 'withdrawn') {
                                    setAuctionFinalPrice('');
                                    setAuctionMaxBid('');
                                    setShowSoldPrice(true);
                                  } else if (newValue === 'passed-in' || newValue === 'passed-in-vendor-bid') {
                                    // Clear sold price for passed-in cases
                                    setAuctionFinalPrice('');
                                    setShowSoldPrice(true);
                                  } else if (newValue === 'sold-prior-to-auction' || newValue === 'sold-at-auction' || newValue === 'sold-after-auction') {
                                    // Clear max bid for sold cases
                                    setAuctionMaxBid('');
                                  }
                                }} 
                                disabled={propertyStatus === 'archived'}
                              >
                                <MenuItem value="sold-prior-to-auction">Sold Prior to Auction</MenuItem>
                                <MenuItem value="sold-at-auction">Sold at Auction</MenuItem>
                                <MenuItem value="sold-after-auction">Sold After Auction</MenuItem>
                                <MenuItem value="passed-in">Passed In</MenuItem>
                                <MenuItem value="passed-in-vendor-bid">Passed In (Vendor Bid)</MenuItem>
                                <MenuItem value="withdrawn">Withdrawn</MenuItem>
                              </Select>
                            </FormControl>
                            
                            <TextField
                              label="Outcome Date"
                              type="date"
                              value={auctionOutcomeDate || new Date().toISOString().split('T')[0]}
                              onChange={(e) => setAuctionOutcomeDate(e.target.value)}
                              fullWidth
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              disabled={propertyStatus === 'archived'}
                              sx={{ mb: 2 }}
                            />

                            {/* Show max bid field for all passed-in cases */}
                            {(auctionOutcome === 'passed-in' || auctionOutcome === 'passed-in-vendor-bid') && (
                              <NumericFormat
                                label={auctionOutcome === 'passed-in-vendor-bid' ? 'Maximum Bid (required)' : 'Maximum Bid (optional)'}
                                value={auctionMaxBid}
                                thousandSeparator
                                allowNegative={false}
                                prefix="$"
                                onValueChange={(values: { value: string }) => {
                                  setAuctionMaxBid(values.value);
                                }}
                                customInput={TextField}
                                size="small"
                                fullWidth
                                disabled={propertyStatus === 'archived'}
                                sx={{ mb: 2 }}
                                helperText={auctionOutcome === 'passed-in-vendor-bid' ? 'Required for vendor bid cases' : 'Optional for passed-in auctions'}
                              />
                            )}

                            {/* Show final price only for sold outcomes */}
                            {(auctionOutcome === 'sold-prior-to-auction' || 
                              auctionOutcome === 'sold-at-auction' || 
                              auctionOutcome === 'sold-after-auction') && (
                              <>
                                <NumericFormat
                                  label="Sold Price"
                                  value={auctionFinalPrice}
                                  thousandSeparator
                                  allowNegative={false}
                                  prefix="$"
                                  onValueChange={(values: { value: string }) => {
                                    setAuctionFinalPrice(values.value);
                                  }}
                                  customInput={TextField}
                                  size="small"
                                  fullWidth
                                  disabled={propertyStatus === 'archived'}
                                  sx={{ mb: 2 }}
                                />
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={showSoldPrice}
                                      onChange={(e) => setShowSoldPrice(e.target.checked)}
                                      disabled={propertyStatus === 'archived'}
                                    />
                                  }
                                  label="Display sold price on portals"
                                  sx={{ mb: 2 }}
                                />
                              </>
                            )}

                            <TextField
                              label="Notes (optional, internal only)"
                              size="small"
                              fullWidth
                              multiline
                              rows={2}
                              value={auctionNotes}
                              onChange={(e) => setAuctionNotes(e.target.value)}
                              disabled={propertyStatus === 'archived'}
                              sx={{ mb: 2 }}
                              helperText="These notes are for internal use only and will not be sent to portals"
                            />
                            
                            <Button
                              variant="contained"
                              size="small"
                              disabled={(() => {
                                if (!auctionOutcome || propertyStatus === 'archived') return true;
                                if (auctionOutcome === 'passed-in-vendor-bid' && !auctionMaxBid) return true;
                                const isSold = auctionOutcome === 'sold-prior-to-auction' || 
                                               auctionOutcome === 'sold-at-auction' || 
                                               auctionOutcome === 'sold-after-auction';
                                if (isSold && !auctionFinalPrice) return true;
                                return false;
                              })()}
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                  
                                  // Prepare task fieldValues (for backward compatibility)
                                  const fieldValues: any = { 
                                    auctionOutcome, 
                                    notes: auctionNotes 
                                  };
                                  
                                  // Map REAXML values to legacy values for task fieldValues
                                  const legacyOutcome = 
                                    auctionOutcome === 'sold-prior-to-auction' || 
                                    auctionOutcome === 'sold-at-auction' || 
                                    auctionOutcome === 'sold-after-auction' 
                                      ? 'sold' 
                                      : auctionOutcome === 'passed-in' || auctionOutcome === 'passed-in-vendor-bid'
                                      ? 'passed_in'
                                      : auctionOutcome;
                                  
                                  fieldValues.auctionOutcome = legacyOutcome;
                                  
                                  // Only include finalPrice if outcome is sold (not passed_in or withdrawn)
                                  if (legacyOutcome === 'sold' && auctionFinalPrice) {
                                    fieldValues.finalPrice = parseFloat(auctionFinalPrice);
                                  }

                                  // Update task first
                                  const response = await fetch(url, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                    },
                                    body: JSON.stringify({ status: 'completed', fieldValues })
                                  });
                                  if (!response.ok) throw new Error('Failed to update task');

                                  // Update property.auctionOutcome
                                  const outcomePayload: {
                                    result:
                                      | 'sold-prior-to-auction'
                                      | 'sold-at-auction'
                                      | 'sold-after-auction'
                                      | 'passed-in'
                                      | 'passed-in-vendor-bid'
                                      | 'withdrawn';
                                    date?: string;
                                    maxBid?: number;
                                    notes?: string;
                                  } = {
                                    result: auctionOutcome as
                                      | 'sold-prior-to-auction'
                                      | 'sold-at-auction'
                                      | 'sold-after-auction'
                                      | 'passed-in'
                                      | 'passed-in-vendor-bid'
                                      | 'withdrawn',
                                    notes: auctionNotes || undefined,
                                  };

                                  if (auctionOutcomeDate) {
                                    outcomePayload.date = auctionOutcomeDate;
                                  }

                                  // Include maxBid for all passed-in cases if provided
                                  if ((auctionOutcome === 'passed-in' || auctionOutcome === 'passed-in-vendor-bid') && auctionMaxBid) {
                                    outcomePayload.maxBid = parseFloat(auctionMaxBid);
                                  }

                                  await updateAuctionOutcome(agencySlug, propertyId, outcomePayload);

                                  // If outcome is a "sold" type, also update soldDetails
                                  const isSoldOutcome = 
                                    auctionOutcome === 'sold-prior-to-auction' || 
                                    auctionOutcome === 'sold-at-auction' || 
                                    auctionOutcome === 'sold-after-auction';
                                  
                                  if (isSoldOutcome && auctionFinalPrice) {
                                    await updateSoldDetails(agencySlug, propertyId, {
                                      soldDate: auctionOutcomeDate || undefined,
                                      soldPrice: parseFloat(auctionFinalPrice),
                                      showSoldPrice: showSoldPrice,
                                      source: 'auction',
                                      notes: auctionNotes || undefined,
                                    });
                                  }

                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId);
                                  }
                                  
                                  // Clear form
                                  setAuctionOutcome('');
                                  setAuctionFinalPrice('');
                                  setAuctionNotes('');
                                  setAuctionMaxBid('');
                                  setAuctionOutcomeDate('');
                                  setShowSoldPrice(true);
                                  
                                  showSuccess('Auction results saved');
                                  setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues } }));
                                } catch (e) {
                                  console.error('Error saving results:', e);
                                  showError('Failed to save results');
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                              fullWidth
                            >
                              Save Results & Complete Task
                            </Button>
                          </Box>
                        )}
                        
                        {task.taskType === 'receive_offers' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Record buyer offers for this property:
                            </div>
                            
                            {/* Existing Offers Table */}
                            {offers.length > 0 && (
                              <TableContainer component={Paper} sx={{ mb: 2 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Buyer Name</TableCell>
                                      <TableCell align="right">Offer Amount</TableCell>
                                      <TableCell align="right">Deposit</TableCell>
                                      <TableCell>Settlement</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Notes</TableCell>
                                      <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {offers.map((offer, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>{offer.buyerName}</TableCell>
                                        <TableCell align="right">
                                          <NumericFormat
                                            value={offer.offerAmount}
                                            displayType="text"
                                            thousandSeparator
                                            prefix="$"
                                          />
                                        </TableCell>
                                        <TableCell align="right">
                                          {offer.deposit ? (
                                            <NumericFormat
                                              value={offer.deposit}
                                              displayType="text"
                                              thousandSeparator
                                              prefix="$"
                                            />
                                          ) : '-'}
                                        </TableCell>
                                        <TableCell>{offer.settlement || '-'}</TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            label={offer.status.replace('_', ' ')}
                                            color={
                                              offer.status === 'accepted' ? 'success' :
                                              offer.status === 'declined' ? 'error' :
                                              offer.status === 'under_review' ? 'warning' : 'default'
                                            }
                                          />
                                        </TableCell>
                                        <TableCell>{offer.notes || '-'}</TableCell>
                                        <TableCell align="center">
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setNewOffer({ 
                                                buyerName: offer.buyerName,
                                                offerAmount: offer.offerAmount.toString(), 
                                                deposit: offer.deposit?.toString() || '',
                                                settlement: offer.settlement || '',
                                                notes: offer.notes || '',
                                                status: offer.status
                                              });
                                              setOffers(offers.filter((_, i) => i !== idx));
                                            }}
                                            disabled={propertyStatus === 'archived'}
                                          >
                                            <Edit fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() => setOffers(offers.filter((_, i) => i !== idx))}
                                            disabled={propertyStatus === 'archived'}
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                            
                            {/* Add New Offer Form */}
                            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {newOffer.buyerName ? 'Edit Offer' : 'Add New Offer'}
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                  label="Buyer Name"
                                  value={newOffer.buyerName}
                                  onChange={(e) => setNewOffer({ ...newOffer, buyerName: e.target.value })}
                                  size="small"
                                  required
                                  disabled={propertyStatus === 'archived'}
                                />
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                  <NumericFormat
                                    label="Offer Amount"
                                    value={newOffer.offerAmount}
                                    thousandSeparator
                                    allowNegative={false}
                                    onValueChange={(values: { value: string }) => {
                                      setNewOffer({ ...newOffer, offerAmount: values.value });
                                    }}
                                    customInput={TextField}
                                    required
                                    size="small"
                                    sx={{ flex: 1 }}
                                    InputProps={{
                                      startAdornment: <AttachMoney />,
                                    }}
                                    disabled={propertyStatus === 'archived'}
                                  />
                                  <NumericFormat
                                    label="Deposit (Optional)"
                                    value={newOffer.deposit}
                                    thousandSeparator
                                    allowNegative={false}
                                    onValueChange={(values: { value: string }) => {
                                      setNewOffer({ ...newOffer, deposit: values.value });
                                    }}
                                    customInput={TextField}
                                    size="small"
                                    sx={{ flex: 1 }}
                                    InputProps={{
                                      startAdornment: <AttachMoney />,
                                    }}
                                    disabled={propertyStatus === 'archived'}
                                  />
                                </Box>
                                <TextField
                                  label="Settlement Period (Optional)"
                                  value={newOffer.settlement}
                                  onChange={(e) => setNewOffer({ ...newOffer, settlement: e.target.value })}
                                  size="small"
                                  placeholder="e.g., 30 days"
                                  disabled={propertyStatus === 'archived'}
                                />
                                <TextField
                                  label="Notes (Optional)"
                                  value={newOffer.notes}
                                  onChange={(e) => setNewOffer({ ...newOffer, notes: e.target.value })}
                                  multiline
                                  rows={2}
                                  size="small"
                                  disabled={propertyStatus === 'archived'}
                                />
                                <FormControl size="small">
                                  <InputLabel>Status</InputLabel>
                                  <Select
                                    label="Status"
                                    value={newOffer.status}
                                    onChange={(e) => setNewOffer({ ...newOffer, status: e.target.value as any })}
                                    disabled={propertyStatus === 'archived'}
                                  >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="under_review">Under Review</MenuItem>
                                    <MenuItem value="accepted">Accepted</MenuItem>
                                    <MenuItem value="declined">Declined</MenuItem>
                                  </Select>
                                </FormControl>
                                <Button
                                  variant="outlined"
                                  startIcon={<Add />}
                                  onClick={() => {
                                    if (newOffer.buyerName && newOffer.offerAmount) {
                                      setOffers([...offers, {
                                        ...newOffer,
                                        offerAmount: parseFloat(newOffer.offerAmount),
                                        deposit: newOffer.deposit ? parseFloat(newOffer.deposit) : undefined
                                      }]);
                                      setNewOffer({
                                        buyerName: '',
                                        offerAmount: '',
                                        deposit: '',
                                        settlement: '',
                                        notes: '',
                                        status: 'pending'
                                      });
                                    }
                                  }}
                                  disabled={!newOffer.buyerName || !newOffer.offerAmount || propertyStatus === 'archived'}
                                  fullWidth
                                >
                                  {newOffer.buyerName ? 'Update Offer' : 'Add Offer'}
                                </Button>
                              </Box>
                            </Box>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Button
                              variant="contained"
                              size="small"
                              disabled={offers.length === 0 || propertyStatus === 'archived'}
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const url = `/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`;
                                  const response = await fetch(url, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                    },
                                    body: JSON.stringify({
                                      status: 'completed',
                                      fieldValues: { offers }
                                    })
                                  });
                                  if (!response.ok) throw new Error('Failed to update task');
                                  const updatedWorkflow = await response.json();
                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId);
                                  }
                                  setOffers([]);
                                  showSuccess(`Saved ${offers.length} offer(s)`);
                                  setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues: { offers } } }));
                                } catch (e) {
                                  console.error('Error saving offers:', e);
                                  showError('Failed to save offers');
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                              fullWidth
                            >
                              Save Offers & Complete Task
                            </Button>
                          </Box>
                        )}
                        
                        {/* Always show vendor response on Determine Next Steps */}
                        {task.taskType === 'next_steps_determination' && (() => {
                          let vendorResponse: string | null = null;
                          let vendorResponseDate: any = null;
                          for (const st of stages) {
                            for (const t of st.tasks) {
                              if (t.name === 'Present Findings' && t.fieldValues?.vendorResponse) {
                                vendorResponse = t.fieldValues.vendorResponse;
                                vendorResponseDate = t.fieldValues.vendorResponseDate;
                                break;
                              }
                            }
                            if (vendorResponse) break;
                          }
                          // Fallback from property.consultationReport via props
                          if (!vendorResponse && consultationVendorResponse) {
                            vendorResponse = consultationVendorResponse;
                            vendorResponseDate = consultationVendorResponseDate || null;
                          }
                          return vendorResponse ? (
                            <Alert severity={vendorResponse === 'Accept' ? 'success' : vendorResponse === 'Decline' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                              <strong>Vendor Response:</strong> {vendorResponse}
                              {vendorResponseDate && (<>
                                {' '}– {new Date(vendorResponseDate).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </>)}
                            </Alert>
                          ) : null;
                        })()}

                        {/* Show vendor signatures for Prepare Agency Agreement task */}
                        {(task.taskType === 'agent_agreement' || task.name === 'Prepare Agency Agreement') && (() => {
                          const vendorSignatures = property?.agencyAgreement?.vendorSignatures || [];
                          const normalizeOwners = (owner: any): Array<{ name: string; email?: string }> => {
                            if (!owner) return [];
                            if (Array.isArray(owner)) {
                              return owner.filter(o => o && o.name).map(o => ({ name: o.name, email: o.email }));
                            }
                            if (owner.name) {
                              return [{ name: owner.name, email: owner.email }];
                            }
                            return [];
                          };
                          const allVendors = normalizeOwners(property?.owner);
                          
                          if (allVendors.length === 0) return null;
                          
                          const signedVendorEmails = new Set(
                            vendorSignatures
                              .filter(sig => sig.vendorEmail || sig.vendorName)
                              .map(sig => sig.vendorEmail?.toLowerCase() || sig.vendorName?.toLowerCase())
                          );
                          
                          const allVendorsSigned = allVendors.every(vendor => {
                            const vendorKey = vendor.email?.toLowerCase() || vendor.name?.toLowerCase();
                            return vendorKey && signedVendorEmails.has(vendorKey);
                          });
                          
                          // Check if final version has been generated
                          // Final version exists if agreement was uploaded/updated after all signatures were collected
                          const agreement = property?.agencyAgreement;
                          const agreementUploadedAt = agreement?.uploadedAt ? new Date(agreement.uploadedAt).getTime() : 0;
                          const lastSignatureTime = vendorSignatures.length > 0
                            ? Math.max(...vendorSignatures.map(sig => sig.signedAt ? new Date(sig.signedAt).getTime() : 0))
                            : 0;
                          const finalVersionGenerated = allVendorsSigned && agreementUploadedAt > 0 && agreementUploadedAt >= lastSignatureTime;
                          
                          return (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                                Agency Agreement Signatures:
                              </Typography>
                              
                              {finalVersionGenerated && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                  <strong>Final Agreement Generated</strong>
                                  {agreementUploadedAt > 0 && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                      Generated on {new Date(agreementUploadedAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                  )}
                                </Alert>
                              )}
                              
                              <Box sx={{ mb: 2 }}>
                                {allVendors.map((vendor, idx) => {
                                  const vendorKey = vendor.email?.toLowerCase() || vendor.name?.toLowerCase();
                                  const isSigned = vendorKey && signedVendorEmails.has(vendorKey);
                                  const signature = vendorSignatures.find(sig => {
                                    const sigKey = sig.vendorEmail?.toLowerCase() || sig.vendorName?.toLowerCase();
                                    return sigKey === vendorKey;
                                  });
                                  
                                  return (
                                    <Box key={idx} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {isSigned ? (
                                        <CheckCircle color="success" fontSize="small" />
                                      ) : (
                                        <RadioButtonUnchecked color="disabled" fontSize="small" />
                                      )}
                                      <Typography variant="body2" sx={{ flex: 1 }}>
                                        {vendor.name}
                                        {vendor.email && (
                                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            ({vendor.email})
                                          </Typography>
                                        )}
                                      </Typography>
                                      {signature?.signedAt && (
                                        <Chip 
                                          label={new Date(signature.signedAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} 
                                          size="small" 
                                          color="success"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                              
                              {allVendorsSigned && property?.agencyAgreement?.key && (
                                <Button
                                  variant={finalVersionGenerated ? "outlined" : "contained"}
                                  color={finalVersionGenerated ? "success" : "primary"}
                                  startIcon={<CheckCircle />}
                                  onClick={async () => {
                                    try {
                                      // Check eligibility first
                                      await checkAgreementEligibility();
                                      
                                      if (agreementEligibility && !agreementEligibility.canGenerate) {
                                        showError(agreementEligibility.message || 'Insufficient credits to generate final agreement');
                                        return;
                                      }
                                      
                                      // Show confirmation if credits will be charged (only if there's an actual cost)
                                      if (agreementEligibility && !agreementEligibility.hasFreeGeneration && agreementEligibility.canGenerate && agreementEligibility.costCents > 0) {
                                        setPendingAgreementAction(() => async () => {
                                          try {
                                            setGeneratingFinalAgreement(true);
                                            setShowCreditConfirmationDialog(false);
                                            const response = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/agency-agreement/generate-final`);
                                            showSuccess(response.data?.message || 'Final agreement generation queued. You will be notified when it is complete.');
                                            await checkAgreementEligibility();
                                            setTimeout(() => {
                                              if (onTaskPatched) {
                                                onTaskPatched();
                                              }
                                              setTimeout(() => {
                                                if (onTaskPatched) {
                                                  onTaskPatched();
                                                }
                                              }, 5000);
                                            }, 2000);
                                          } catch (err: any) {
                                            if (err?.response?.status === 402) {
                                              showError(getOperationErrorMessage(err, 'generateAgreement'));
                                            } else {
                                              showError(getOperationErrorMessage(err, 'generateAgreement'));
                                            }
                                          } finally {
                                            setGeneratingFinalAgreement(false);
                                            setPendingAgreementAction(null);
                                          }
                                        });
                                        setShowCreditConfirmationDialog(true);
                                        return;
                                      }
                                      
                                      setGeneratingFinalAgreement(true);
                                      // Call API to generate final agreement
                                      const response = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/agency-agreement/generate-final`);
                                      showSuccess(response.data?.message || 'Final agreement generation queued. You will be notified when it is complete.');
                                      await checkAgreementEligibility();
                                      // Wait a bit and refresh property data to pick up the updated agreement
                                      setTimeout(() => {
                                        if (onTaskPatched) {
                                          onTaskPatched();
                                        }
                                        // Also trigger a page refresh after a longer delay to ensure the job has completed
                                        setTimeout(() => {
                                          if (onTaskPatched) {
                                            onTaskPatched();
                                          }
                                        }, 5000);
                                      }, 2000);
                                    } catch (err: any) {
                                      console.error('Error generating final agreement:', err);
                                      if (err?.response?.status === 402) {
                                        showError(getOperationErrorMessage(err, 'generateAgreement'));
                                      } else {
                                        showError(getOperationErrorMessage(err, 'generateAgreement'));
                                      }
                                    } finally {
                                      setGeneratingFinalAgreement(false);
                                    }
                                  }}
                                  disabled={propertyStatus === 'archived' || generatingFinalAgreement}
                                  fullWidth
                                >
                                  {generatingFinalAgreement ? 'Generating...' : finalVersionGenerated ? 'Regenerate Final Agreement' : 'Generate Final Agreement'}
                                </Button>
                              )}
                            </Box>
                          );
                        })()}

                        {/* Show principal signatures for Prepare Rental Management Agreement task */}
                        {(task.taskType === 'rental_management_agreement' || task.name === 'Prepare Rental Management Agreement') && (() => {
                          const principalSignatures = property?.rentalManagementAgreement?.principalSignatures || [];
                          const normalizeOwners = (owner: any): Array<{ name: string; email?: string }> => {
                            if (!owner) return [];
                            if (Array.isArray(owner)) {
                              return owner.filter(o => o && o.name).map(o => ({ name: o.name, email: o.email }));
                            }
                            if (owner.name) {
                              return [{ name: owner.name, email: owner.email }];
                            }
                            return [];
                          };
                          const allPrincipals = normalizeOwners(property?.owner);
                          
                          if (allPrincipals.length === 0) return null;
                          
                          const signedPrincipalEmails = new Set(
                            principalSignatures
                              .filter((sig: any) => sig.principalEmail || sig.principalName)
                              .map((sig: any) => sig.principalEmail?.toLowerCase() || sig.principalName?.toLowerCase())
                          );
                          
                          const allPrincipalsSigned = allPrincipals.every(principal => {
                            const principalKey = principal.email?.toLowerCase() || principal.name?.toLowerCase();
                            return principalKey && signedPrincipalEmails.has(principalKey);
                          });
                          
                          // Check if final version has been generated
                          const agreement = property?.rentalManagementAgreement;
                          const agreementUploadedAt = agreement?.uploadedAt ? new Date(agreement.uploadedAt).getTime() : 0;
                          const lastSignatureTime = principalSignatures.length > 0
                            ? Math.max(...principalSignatures.map((sig: any) => sig.signedAt ? new Date(sig.signedAt).getTime() : 0))
                            : 0;
                          const finalVersionGenerated = allPrincipalsSigned && agreementUploadedAt > 0 && agreementUploadedAt >= lastSignatureTime;
                          
                          return (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                                Rental Management Agreement Signatures:
                              </Typography>
                              
                              {finalVersionGenerated && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                  <strong>Final Agreement Generated</strong>
                                  {agreementUploadedAt > 0 && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                      Generated on {new Date(agreementUploadedAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                  )}
                                </Alert>
                              )}
                              
                              <Box sx={{ mb: 2 }}>
                                {allPrincipals.map((principal, idx) => {
                                  const principalKey = principal.email?.toLowerCase() || principal.name?.toLowerCase();
                                  const isSigned = principalKey && signedPrincipalEmails.has(principalKey);
                                  const signature = principalSignatures.find((sig: any) => {
                                    const sigKey = sig.principalEmail?.toLowerCase() || sig.principalName?.toLowerCase();
                                    return sigKey === principalKey;
                                  });
                                  
                                  return (
                                    <Box key={idx} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {isSigned ? (
                                        <CheckCircle color="success" fontSize="small" />
                                      ) : (
                                        <RadioButtonUnchecked color="disabled" fontSize="small" />
                                      )}
                                      <Typography variant="body2" sx={{ flex: 1 }}>
                                        {principal.name}
                                        {principal.email && (
                                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            ({principal.email})
                                          </Typography>
                                        )}
                                      </Typography>
                                      {signature?.signedAt && (
                                        <Chip 
                                          label={new Date(signature.signedAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} 
                                          size="small" 
                                          color="success"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                              {allPrincipalsSigned && property?.rentalManagementAgreement?.key && (
                                <Button
                                  variant={finalVersionGenerated ? 'outlined' : 'contained'}
                                  color={finalVersionGenerated ? 'success' : 'primary'}
                                  startIcon={<CheckCircle />}
                                  onClick={async () => {
                                    try {
                                      setGeneratingFinalRentalAgreement(true);
                                      const response = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/rental-management-agreement/generate-final`);
                                      showSuccess(response.data?.message || 'Final agreement generated successfully.');
                                      setTimeout(() => {
                                        if (onTaskPatched) onTaskPatched();
                                      }, 1000);
                                      setTimeout(() => {
                                        if (onTaskPatched) onTaskPatched();
                                      }, 5000);
                                    } catch (err: any) {
                                      console.error('Error generating final rental agreement:', err);
                                      showError(err?.response?.data?.error || 'Failed to generate final rental agreement');
                                    } finally {
                                      setGeneratingFinalRentalAgreement(false);
                                    }
                                  }}
                                  disabled={propertyStatus === 'archived' || generatingFinalRentalAgreement}
                                  fullWidth
                                >
                                  {generatingFinalRentalAgreement ? 'Generating...' : finalVersionGenerated ? 'Regenerate Final Agreement' : 'Generate Final Agreement'}
                                </Button>
                              )}
                            </Box>
                          );
                        })()}

                        {/* Prepare Rental Management Agreement task */}
                        {(task.taskType === 'rental_management_agreement' || task.name === 'Prepare Rental Management Agreement') && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Prepare Rental Management Agreement:
                            </div>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                              <InputLabel>Agreement Method</InputLabel>
                              <Select
                                value={rentalAgreementMethod}
                                label="Agreement Method"
                                onChange={(e) => {
                                  setRentalAgreementMethod(e.target.value as 'generate' | 'upload');
                                  // Reset file states when switching methods
                                  if (e.target.value === 'generate') {
                                    setRentalAgreementFile(null);
                                    setRentalAgreementFileId(null);
                                  } else {
                                    setGeneratedRentalAgreementFileId(null);
                                  }
                                }}
                                disabled={propertyStatus === 'archived'}
                              >
                                <MenuItem value="generate">Generate Agreement (Platform)</MenuItem>
                                <MenuItem value="upload">Upload Signed Agreement</MenuItem>
                              </Select>
                            </FormControl>

                            {rentalAgreementMethod === 'generate' && (
                              <Box>
                                <Button
                                  variant="outlined"
                                  startIcon={<Visibility />}
                                  onClick={() => {
                                    // Load current management fees from property to pre-populate config
                                    if (property?.rentalManagementFees) {
                                      setRentalAgreementConfig(prev => ({
                                        ...prev,
                                        advertisingOther: property.rentalManagementFees?.inspectionFeeNote || '',
                                      }));
                                    }
                                    setShowRentalAgreementConfigDialog(true);
                                  }}
                                  disabled={generatingRentalAgreementPdf || propertyStatus === 'archived'}
                                  sx={{ mb: 1 }}
                                >
                                  {generatingRentalAgreementPdf 
                                    ? 'Generating...' 
                                    : (rentalAgreementFileId || generatedRentalAgreementFileId) 
                                      ? 'Regenerate & Review Agreement' 
                                      : 'Generate & Review Agreement'}
                                </Button>
                                {generatedRentalAgreementFileId && (
                                  <Alert 
                                    severity={property?.rentalManagementAgreement?.sentAt ? "info" : "success"} 
                                    sx={{ mt: 1 }}
                                  >
                                    {property?.rentalManagementAgreement?.sentAt 
                                      ? `Agreement sent to principal${property?.rentalManagementAgreement?.principalEmail ? '' : 's'} on ${new Date(property.rentalManagementAgreement.sentAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                                      : 'Agreement generated and ready to send to principal'}
                                  </Alert>
                                )}
                              </Box>
                            )}

                            {rentalAgreementMethod === 'upload' && (
                              <Box>
                                <Button
                                  variant="outlined"
                                  startIcon={<Upload />}
                                  disabled={rentalAgreementUploading || propertyStatus === 'archived'}
                                  sx={{ mb: 1 }}
                                  onClick={() => {
                                    const input = document.getElementById(`rental-agreement-upload-input-${taskKey}`) as HTMLInputElement;
                                    if (input) input.click();
                                  }}
                                >
                                  {rentalAgreementUploading ? 'Uploading...' : rentalAgreementFile ? rentalAgreementFile.name : 'Select Rental Management Agreement'}
                                </Button>
                                <input
                                  type="file"
                                  id={`rental-agreement-upload-input-${taskKey}`}
                                  accept=".pdf,.doc,.docx"
                                  style={{ display: 'none' }}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    
                                    try {
                                      setRentalAgreementUploading(true);
                                      setRentalAgreementFile(file);
                                      
                                      await uploadRentalManagementAgreement(agencySlug, propertyId, file);
                                      // Refresh property data to get the updated key
                                      if (onTaskPatched) {
                                        await onTaskPatched();
                                      }
                                      showSuccess('Rental management agreement uploaded successfully');
                                    } catch (err: any) {
                                      console.error('Error uploading rental management agreement:', err);
                                      showError(err?.response?.data?.error || 'Failed to upload rental management agreement');
                                      setRentalAgreementFile(null);
                                    } finally {
                                      setRentalAgreementUploading(false);
                                      // Reset file input value
                                      e.target.value = '';
                                    }
                                  }}
                                />
                                {rentalAgreementFile && (
                                  <Alert severity="success" sx={{ mt: 1 }}>
                                    {rentalAgreementFile.name} uploaded successfully
                                  </Alert>
                                )}
                              </Box>
                            )}

                            {/* View and Send Agreement Buttons - shown when agreement is ready */}
                            {(rentalAgreementFileId || generatedRentalAgreementFileId || property?.rentalManagementAgreement?.key) && (
                              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<Visibility />}
                                    onClick={() => {
                                      const fileKey = property?.rentalManagementAgreement?.key || rentalAgreementFileId || generatedRentalAgreementFileId;
                                      if (fileKey) {
                                        // Use property.rentalManagementAgreement.url if available (presigned URL)
                                        // Otherwise construct URL from fileKey using the file endpoint
                                        const agreementUrl = property?.rentalManagementAgreement?.url || 
                                          `${window.location.origin}/api/agencies/${agencySlug}/files/${fileKey}`;
                                        window.open(agreementUrl, '_blank', 'noopener,noreferrer');
                                      }
                                    }}
                                    disabled={propertyStatus === 'archived'}
                                    sx={{ flex: 1 }}
                                  >
                                    View Agreement
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Email />}
                                    onClick={() => {
                                      const ownerEmail = Array.isArray(property?.owner) 
                                        ? property.owner[0]?.email || property.owner.map((o: any) => o.email).filter(Boolean).join(',')
                                        : property?.owner?.email || '';
                                      setAgreementEmail(ownerEmail);
                                      setSendAgreementType('rental_management');
                                      setShowSendAgreementDialog(true);
                                    }}
                                    disabled={sendingAgreementEmail || propertyStatus === 'archived'}
                                    sx={{ flex: 1 }}
                                  >
                                    {sendingAgreementEmail ? 'Sending...' : 'Send to Principal'}
                                  </Button>
                                </Box>
                              </Box>
                            )}

                            <Button
                              variant="contained"
                              size="small"
                              disabled={(!rentalAgreementFileId && !generatedRentalAgreementFileId && !property?.rentalManagementAgreement?.key) || propertyStatus === 'archived'}
                              onClick={async () => {
                                try {
                                  const fileKey = property?.rentalManagementAgreement?.key || rentalAgreementFileId || generatedRentalAgreementFileId;
                                  const fieldValues: any = {
                                    rentalAgreementFileId: fileKey,
                                    rentalAgreementFileName: rentalAgreementFile?.name || 'rental-management-agreement.pdf',
                                    rentalAgreementMethod: rentalAgreementMethod,
                                  };
                                  
                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId, fieldValues);
                                  }
                                  setRentalAgreementFile(null);
                                  setRentalAgreementFileId(null);
                                  showSuccess('Rental management agreement prepared');
                                  setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues } }));
                                } catch (e) {
                                  console.error('Error saving rental management agreement:', e);
                                  showError('Failed to save rental management agreement');
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ((!rentalAgreementFileId && !generatedRentalAgreementFileId && !property?.rentalManagementAgreement?.key) ? 'Please generate or upload a rental management agreement first' : '')}
                              fullWidth
                            >
                              Complete Task
                            </Button>
                          </Box>
                        )}

                        {(task.taskType === 'next_steps_determination' || task.name === 'Agree Next Steps') && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            {/* Show vendor response if available */}
                            {(() => {
                              // Find vendor response from "Present Findings" task
                              let vendorResponse = null;
                              let vendorResponseDate = null;
                              for (const stage of stages) {
                                for (const t of stage.tasks) {
                                  if (t.taskType === 'present_findings' && t.fieldValues?.vendorResponse) {
                                    vendorResponse = t.fieldValues.vendorResponse;
                                    vendorResponseDate = t.fieldValues.vendorResponseDate;
                                    break;
                                  }
                                }
                                if (vendorResponse) break;
                              }
                              return vendorResponse ? (
                                <Alert severity={vendorResponse === "I'd like to proceed" ? 'success' : vendorResponse === 'Not interested' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                                  <strong>Vendor Response:</strong> {vendorResponse}
                                  {vendorResponseDate && (
                                    <> - {new Date(vendorResponseDate).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                                  )}
                                </Alert>
                              ) : null;
                            })()}
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Determine the next steps for this property:
                            </div>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                              <InputLabel>Next Step</InputLabel>
                              <Select
                                label="Next Step"
                                value={nextStepValue}
                                onChange={(e) => setNextStepValue(e.target.value)}
                                disabled={propertyStatus === 'archived'}
                              >
                                <MenuItem value="Proceed">
                                  Proceed
                                </MenuItem>
                                <MenuItem value="Abandoned">Abandoned</MenuItem>
                              </Select>
                            </FormControl>
                            
                            <Button
                              variant="contained"
                              size="small"
                              disabled={!nextStepValue || propertyStatus === 'archived'}
                              onClick={async () => {
                                // Check if vendor has positively responded when proceeding
                                if (nextStepValue === 'Proceed') {
                                  let resp: string | null = null;
                                  for (const st of stages) {
                                    for (const t of st.tasks) {
                                      if (t.name === 'Present Findings' && t.fieldValues?.vendorResponse) {
                                        resp = t.fieldValues.vendorResponse;
                                        break;
                                      }
                                    }
                                    if (resp) break;
                                  }
                                  if (!resp && consultationVendorResponse) resp = consultationVendorResponse;
                                  const hasRecordedResponse = resp === 'I\'d like to proceed' || resp === 'Accept';

                                  // Also treat "all vendors have signed the agency agreement" as positive (no need to show "no response" dialog)
                                  let allVendorsSignedAgreement = false;
                                  const vendorSigs = property?.agencyAgreement?.vendorSignatures || [];
                                  const owners = !property?.owner ? [] : Array.isArray(property.owner)
                                    ? property.owner.filter((o: any) => o && o.name).map((o: any) => ({ name: o.name, email: o.email }))
                                    : [{ name: (property.owner as any).name, email: (property.owner as any).email }];
                                  if (owners.length > 0 && vendorSigs.length > 0) {
                                    const signedSet = new Set(
                                      vendorSigs
                                        .filter((s: any) => s.vendorEmail || s.vendorName)
                                        .map((s: any) => (s.vendorEmail || s.vendorName || '').toString().trim().toLowerCase())
                                    );
                                    allVendorsSignedAgreement = owners.every((o: any) => {
                                      const key = (o.email || o.name || '').toString().trim().toLowerCase();
                                      return key && signedSet.has(key);
                                    });
                                  }
                                  // For rental: treat "all principals have signed the rental management agreement" as positive
                                  let allPrincipalsSignedRentalAgreement = false;
                                  const principalSigs = property?.rentalManagementAgreement?.principalSignatures || [];
                                  const rentalOwners = !property?.owner ? [] : Array.isArray(property.owner)
                                    ? property.owner.filter((o: any) => o && o.name).map((o: any) => ({ name: o.name, email: o.email }))
                                    : [{ name: (property.owner as any).name, email: (property.owner as any).email }];
                                  if (rentalOwners.length > 0 && principalSigs.length > 0) {
                                    const principalSignedSet = new Set(
                                      principalSigs
                                        .filter((s: any) => s.principalEmail || s.principalName)
                                        .map((s: any) => (s.principalEmail || s.principalName || '').toString().trim().toLowerCase())
                                    );
                                    allPrincipalsSignedRentalAgreement = rentalOwners.every((o: any) => {
                                      const key = (o.email || o.name || '').toString().trim().toLowerCase();
                                      return key && principalSignedSet.has(key);
                                    });
                                  }
                                  const hasPositiveResponse = hasRecordedResponse || allVendorsSignedAgreement || allPrincipalsSignedRentalAgreement;

                                  if (!hasPositiveResponse) {
                                    setPendingNextStepTask({ stageIndex, taskIndex });
                                    setShowNoResponseConfirmationDialog(true);
                                    return;
                                  }
                                }

                                try {
                                  const fieldValues: any = { nextStep: nextStepValue };
                                  
                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, taskIndex, 'completed', workflowId, fieldValues);
                                  }
                                  setNextStepValue('');
                                  showSuccess('Next step saved');
                                  setOptimisticTaskUpdates(prev => ({ ...prev, [`${stageIndex}-${taskIndex}`]: { ...(prev[`${stageIndex}-${taskIndex}`] || {}), status: 'completed', fieldValues } }));
                                } catch (e) {
                                  console.error('Error saving next step:', e);
                                  showError('Failed to save next step');
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : ''}
                              fullWidth
                            >
                              Save Next Step & Complete Task
                            </Button>
                          </Box>
                        )}

                        {/* Prepare Agency Agreement task */}
                        {(task.taskType === 'agent_agreement' || task.name === 'Prepare Agency Agreement') && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                              Prepare Agency Agreement:
                            </div>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                              <InputLabel>Agreement Method</InputLabel>
                              <Select
                                value={agentAgreementMethod}
                                label="Agreement Method"
                                onChange={(e) => {
                                  setAgentAgreementMethod(e.target.value as 'generate' | 'upload');
                                  // Reset file states when switching methods
                                  if (e.target.value === 'generate') {
                                    setAgentAgreementFile(null);
                                    setAgentAgreementFileId(null);
                                  } else {
                                    setGeneratedAgreementFileId(null);
                                  }
                                }}
                                disabled={propertyStatus === 'archived'}
                              >
                                {isAgentAgreementSupported && (
                                  <MenuItem value="generate">Generate Agreement (Platform)</MenuItem>
                                )}
                                <MenuItem value="upload">Upload Signed Agreement</MenuItem>
                              </Select>
                            </FormControl>

                            {agentAgreementMethod === 'generate' && (
                              <Box>
                                <Button
                                  variant="outlined"
                                  startIcon={<Visibility />}
                                  onClick={async () => {
                                    try {
                                      const eligibility = await checkAgreementEligibility();
                                      const missing = eligibility?.missingPrerequisites || [];
                                      if (missing.length > 0) {
                                        setMissingAgreementPrerequisites(missing);
                                        setShowLicenseNumberDialog(true);
                                        return;
                                      }
                                    } catch (err) {
                                      console.warn('Failed to check agreement prerequisites:', err);
                                    }

                                    let initialMarketingCosts: Array<{ item: string; supplier?: string; cost: number; payable: 'upfront' | 'settlement' }> = [];
                                    let saleMethod = '';
                                    try {
                                      // First, check the current stages prop (which might already have the data)
                                      for (const stage of stages) {
                                        for (const task of stage.tasks || []) {
                                          // Marketing fee
                                          if (task.taskType === 'marketing_fee' && task.fieldValues?.marketingFee) {
                                            initialMarketingCosts.push({
                                              item: 'Marketing Package',
                                              cost: Number(task.fieldValues.marketingFee),
                                              payable: 'settlement' as const,
                                            });
                                          }
                                          // Staging fee
                                          if (task.taskType === 'staging_fee' && task.fieldValues?.stagingFee) {
                                            initialMarketingCosts.push({
                                              item: 'Staging Package',
                                              cost: Number(task.fieldValues.stagingFee),
                                              payable: 'settlement' as const,
                                            });
                                          }
                                          // Sale type (to determine if auction)
                                          if (task.taskType === 'sale_type_determination' && task.fieldValues?.saleType) {
                                            saleMethod = task.fieldValues.saleType;
                                          }
                                        }
                                      }
                                    } catch (err) {
                                      console.warn('Failed to load marketing costs from workflow:', err);
                                    }
                                    
                                    // Pre-populate form data
                                    setAgreementFormData({
                                      marketingCosts: initialMarketingCosts.length > 0 ? initialMarketingCosts : [],
                                      introducedPurchasersListDays: 14, // Default value
                                    });
                                    
                                    setAgreementStageIndex(stageIndex);
                                    agreementStageIndexRef.current = stageIndex;
                                    setShowAgreementGenerationDialog(true);
                                  }}
                                  disabled={generatingAgreement || propertyStatus === 'archived'}
                                  sx={{ mb: 1 }}
                                >
                                  {generatingAgreement 
                                    ? 'Generating...' 
                                    : (agentAgreementFileId || generatedAgreementFileId) 
                                      ? 'Regenerate & Review Agreement' 
                                      : 'Generate & Review Agreement'}
                                </Button>
                                {generatedAgreementFileId && (
                                  <Alert 
                                    severity={property?.agencyAgreement?.sentAt ? "info" : "success"} 
                                    sx={{ mt: 1 }}
                                  >
                                    {property?.agencyAgreement?.sentAt 
                                      ? `Agreement sent to vendor${property?.agencyAgreement?.vendorEmails && property.agencyAgreement.vendorEmails.length > 1 ? 's' : ''} on ${new Date(property.agencyAgreement.sentAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                                      : 'Agreement generated and ready to send to vendor'}
                                  </Alert>
                                )}
                              </Box>
                            )}

                            {agentAgreementMethod === 'upload' && (
                              <Box>
                                <Button
                                  variant="outlined"
                                  startIcon={<Upload />}
                                  disabled={agentAgreementUploading || propertyStatus === 'archived'}
                                  sx={{ mb: 1 }}
                                  onClick={() => {
                                    const input = document.getElementById(`agent-agreement-upload-input-${taskKey}`) as HTMLInputElement;
                                    if (input) input.click();
                                  }}
                                >
                                  {agentAgreementUploading ? 'Uploading...' : agentAgreementFile ? agentAgreementFile.name : 'Select Agency Agreement'}
                                </Button>
                                <input
                                  type="file"
                                  id={`agent-agreement-upload-input-${taskKey}`}
                                  accept=".pdf,.doc,.docx"
                                  style={{ display: 'none' }}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    
                                    try {
                                      setAgentAgreementUploading(true);
                                      setAgentAgreementFile(file);
                                      
                                      const formData = new FormData();
                                      formData.append('file', file);
                                      formData.append('type', 'agent_agreement');
                                      
                                      const uploadResponse = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${stageIndex}/files`, formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                      });
                                      
                                      const { key, fileName } = uploadResponse.data;
                                      setAgentAgreementFileId(key);
                                      if (!agentAgreementFile) setAgentAgreementFile(new File([file], fileName || file.name, { type: file.type }));
                                      showSuccess('Agency agreement uploaded successfully');
                                    } catch (err: any) {
                                      console.error('Error uploading agency agreement:', err);
                                      showError(err?.response?.data?.error || 'Failed to upload agency agreement');
                                      setAgentAgreementFile(null);
                                    } finally {
                                      setAgentAgreementUploading(false);
                                      // Reset file input value
                                      e.target.value = '';
                                    }
                                  }}
                                />
                                {agentAgreementFile && (
                                  <Alert severity="success" sx={{ mt: 1 }}>
                                    {agentAgreementFile.name} uploaded successfully
                                  </Alert>
                                )}
                              </Box>
                            )}

                            {/* View and Send Agreement Buttons - shown when agreement is ready */}
                            {(agentAgreementFileId || generatedAgreementFileId) && (
                              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<Visibility />}
                                    onClick={() => {
                                      const fileId = agentAgreementFileId || generatedAgreementFileId;
                                      if (fileId) {
                                        // Use property.agencyAgreement.url if available (presigned URL)
                                        // Otherwise construct URL from fileId using the file endpoint
                                        const agreementUrl = property?.agencyAgreement?.url || 
                                          `${window.location.origin}/api/agencies/${agencySlug}/files/${fileId}`;
                                        window.open(agreementUrl, '_blank', 'noopener,noreferrer');
                                      }
                                    }}
                                    disabled={propertyStatus === 'archived'}
                                    sx={{ flex: 1 }}
                                  >
                                    View Agreement
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Email />}
                                    onClick={() => {
                                      setAgreementEmail(ownerEmail || '');
                                      setSendAgreementType('agency');
                                      setShowSendAgreementDialog(true);
                                    }}
                                    disabled={sendingAgreementEmail || propertyStatus === 'archived'}
                                    sx={{ flex: 1 }}
                                  >
                                    {sendingAgreementEmail ? 'Sending...' : 'Send to Vendor'}
                                  </Button>
                                </Box>
                              </Box>
                            )}

                            <Button
                              variant="contained"
                              size="small"
                              disabled={!agentAgreementFileId && !generatedAgreementFileId || propertyStatus === 'archived'}
                              onClick={async () => {
                                try {
                                  const fileId = agentAgreementFileId || generatedAgreementFileId;
                                  const fieldValues: any = {
                                    agentAgreementFileId: fileId,
                                    agentAgreementFileName: agentAgreementFile?.name || 'agent-agreement.pdf',
                                    agentAgreementMethod: agentAgreementMethod,
                                  };
                                  
                                  if (onTaskStatusChange && workflowId) {
                                    onTaskStatusChange(stageIndex, actualTaskIndex, 'completed', workflowId, fieldValues);
                                  }
                                  setAgentAgreementFile(null);
                                  setAgentAgreementFileId(null);
                                  showSuccess('Agency agreement prepared');
                                  setOptimisticTaskUpdates(prev => ({ ...prev, [taskKey]: { ...(prev[taskKey] || {}), status: 'completed', fieldValues } }));
                                } catch (e) {
                                  console.error('Error saving agency agreement:', e);
                                  showError('Failed to save agency agreement');
                                }
                              }}
                              title={propertyStatus === 'archived' ? 'Cannot update tasks for archived properties. Please restore the property first.' : (!agentAgreementFileId && !generatedAgreementFileId ? 'Please generate or upload an agency agreement first' : '')}
                              fullWidth
                            >
                              Complete Task
                            </Button>
                          </Box>
                        )}
                        
                        {/* Show existing report link if available, regardless of task status */}
                        {task.taskType === 'consultation_report' && consultationReportExists && consultationReportUrl && (
                          <Box sx={{ mt: 2, mb: 2 }}>
                            <Alert severity="success">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                <span>Marketing appraisal available</span>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={reportPdfLoading ? <CircularProgress size={18} /> : <Download />}
                                  disabled={reportPdfLoading}
                                  onClick={async () => {
                                    setReportPdfLoading(true);
                                    try {
                                      const { data } = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report`);
                                      const url = data?.url;
                                      if (!url) {
                                        showError('Report URL not available. Please try again.');
                                        return;
                                      }
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.target = '_blank';
                                      link.rel = 'noopener noreferrer';
                                      if (data?.fileName) link.download = data.fileName;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    } catch (err: any) {
                                      showError(err.response?.data?.error || err.message || 'Failed to open PDF');
                                    } finally {
                                      setReportPdfLoading(false);
                                    }
                                  }}
                                >
                                    {reportPdfLoading ? 'Opening...' : 'View PDF'}
                                </Button>
                                </Box>
                              </Box>
                            </Alert>
                          </Box>
                        )}
                        
                        {/* Show generate/upload button only when task is in_progress */}
                        {task.taskType === 'consultation_report' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (() => {
                          const isFreePlan = ['Free', 'Agency Free', 'trial', 'Trial'].includes(currentPlan ?? '');
                          
                          // Free plan: Show upload option
                          if (isFreePlan) {
                            return (
                              <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                                  Upload Consultation Report (Free Plan)
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2, color: '#666', fontSize: '0.875rem' }}>
                                  Upload a PDF consultation report file to complete this task.
                                </Typography>
                                <input
                                  type="file"
                                  accept=".pdf"
                                  style={{ display: 'none' }}
                                  id={`consultation-report-upload-${stageIndex}-${actualTaskIndex}`}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      try {
                                        setReportLoading(true);
                                        // Upload to workflow stage files with type 'consultation_report'
                                        const formData = new FormData();
                                        formData.append('file', file);
                                        formData.append('type', 'consultation_report');
                                        const token = localStorage.getItem('token');
                                        const uploadResp = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${stageIndex}/files`, {
                                          method: 'POST',
                                          headers: {
                                            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                          },
                                          body: formData
                                        });
                                        if (!uploadResp.ok) {
                                          const err = await uploadResp.json().catch(() => ({}));
                                          showError(err.error || 'Failed to upload consultation report');
                                          return;
                                        }
                                        const uploadData = await uploadResp.json();
                                        
                                        // Also save to property.consultationReport for consistency via API
                                        try {
                                          const saveResp = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/consultation-report/upload`, {
                                            method: 'POST',
                                            headers: {
                                              'Content-Type': 'application/json',
                                              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                            },
                                            body: JSON.stringify({
                                              fileName: uploadData.fileName || file.name,
                                              key: uploadData.key,
                                              url: uploadData.url
                                            })
                                          });
                                          if (!saveResp.ok) {
                                            console.warn('Failed to save report to property, but file was uploaded to stage');
                                          }
                                        } catch (propErr) {
                                          console.warn('Failed to save report to property:', propErr);
                                        }
                                        
                                        showSuccess('Consultation report uploaded successfully');
                                        if (onTaskPatched) onTaskPatched();
                                      } catch (e: any) {
                                        console.error('Error uploading report:', e);
                                        showError(e.message || 'Failed to upload consultation report');
                                      } finally {
                                        setReportLoading(false);
                                        // Reset file input
                                        const input = document.getElementById(`consultation-report-upload-${stageIndex}-${actualTaskIndex}`) as HTMLInputElement;
                                        if (input) input.value = '';
                                      }
                                    }
                                  }}
                                />
                                <label htmlFor={`consultation-report-upload-${stageIndex}-${actualTaskIndex}`}>
                                  <Button
                                    variant="contained"
                                    component="span"
                                    size="small"
                                    disabled={reportLoading || propertyStatus === 'archived'}
                                    startIcon={<Upload />}
                                    title={propertyStatus === 'archived' ? 'Cannot upload files for archived properties. Please restore the property first.' : ''}
                                    fullWidth
                                    sx={{ mb: 1 }}
                                  >
                                    {reportLoading ? 'Uploading...' : '📄 Upload Consultation Report PDF'}
                                  </Button>
                                </label>
                                <Alert severity="info" sx={{ mt: 1 }}>
                                  Free plan users can upload their own consultation report. Upgrade to the Agency Premium plan for AI-generated reports.
                                </Alert>
                              </Box>
                            );
                          }
                          
                          // Professional: Show direct input editing (no AI generation needed)
                          // Button label changes based on whether structured inputs exist
                          return (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '16px' }}>
                                {hasStructuredInputs
                                  ? 'Edit the marketing appraisal report by reviewing and confirming structured inputs:'
                                  : 'Prepare the marketing appraisal report by reviewing and confirming structured inputs:'}
                              </div>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={loadingAppraisalInputs || propertyStatus === 'archived' || (!!consultationReportSentAt && consultationReportPresentFindingsCompleted)}
                                startIcon={<Edit />}
                                onClick={async () => {
                                  // Load structured inputs and open dialog directly
                                  setLoadingAppraisalInputs(true);
                                  try {
                                    const inputsResponse = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/inputs`);
                                    const inputsData = inputsResponse.data;
                                    
                                    // Use saved structured inputs if available, otherwise prepare from data
                                    let initialInputs: MarketAppraisalInputs;
                                    if (inputsData.savedStructuredInputs) {
                                      initialInputs = inputsData.savedStructuredInputs;
                                      setHasStructuredInputs(true);
                                      } else {
                                      initialInputs = prepareAppraisalInputs(
                                        inputsData.property,
                                        inputsData.agency,
                                        inputsData.agent,
                                        inputsData.extractedData,
                                        { content: inputsData.reportContent }
                                      );
                                      setHasStructuredInputs(false);
                                    }
                                    
                                    setAppraisalInputs(initialInputs);
                                    setShowAppraisalConfirmDialog(true);
                                  } catch (err: any) {
                                    console.error('Error loading appraisal inputs:', err);
                                    showError('Failed to load report data. Please try again.');
                                } finally {
                                    setLoadingAppraisalInputs(false);
                                  }
                                }}
                                title={
                                  propertyStatus === 'archived' 
                                    ? 'Cannot update tasks for archived properties. Please restore the property first.'
                                    : (!!consultationReportSentAt && consultationReportPresentFindingsCompleted)
                                    ? 'The marketing appraisal has been sent and presented, and cannot be edited'
                                    : ''
                                }
                              fullWidth
                              sx={{ mb: 1 }}
                            >
                                {loadingAppraisalInputs 
                                  ? 'Loading...' 
                                  : (hasStructuredInputs ? 'Edit Report' : 'Prepare Marketing Appraisal')}
                            </Button>
                          </Box>
                          );
                        })()}

                        {/* Property Valuation task with RP data fields */}
                        {task.taskType === 'property_valuation' && (optimistic ? optimistic.status : task.status) === 'in_progress' && (
                          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                              Property Valuation
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <label htmlFor={`rp-data-upload-${stageIndex}-${actualTaskIndex}`} style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem' }}>
                                Upload RP Data (Valuation and CMA):
                              </label>
                              <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                  <InputLabel>File Type</InputLabel>
                                  <Select
                                    value={rpDataFileTypes[taskKey] || 'valuation'}
                                    label="File Type"
                                    onChange={(e) => {
                                      setRpDataFileTypes(prev => ({
                                        ...prev,
                                        [taskKey]: e.target.value
                                      }));
                                    }}
                                  >
                                    <MenuItem value="valuation">Valuation</MenuItem>
                                    <MenuItem value="comparative_market_analysis">CMA (Comparative Market Analysis)</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                              <input
                                type="file"
                                id={`rp-data-upload-${stageIndex}-${actualTaskIndex}`}
                                accept=".pdf"
                                style={{ display: 'none' }}
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                    try {
                                    const token = localStorage.getItem('token');
                                      const formData = new FormData();
                                      formData.append('file', file);
                                    // Use selected file type, or auto-detect if not selected
                                    const selectedFileType = rpDataFileTypes[taskKey];
                                    let fileType: string;
                                    if (selectedFileType) {
                                      fileType = selectedFileType;
                                    } else {
                                      // Fallback: Detect if it's a CMA file by filename
                                      const isCMA = file.name.toLowerCase().includes('cma') || file.name.toLowerCase().includes('comparative') || file.name.toLowerCase().includes('market-analysis');
                                      fileType = isCMA ? 'comparative_market_analysis' : 'valuation';
                                    }
                                    formData.append('fileType', fileType);
                                      formData.append('type', 'rp_data');
                                    
                                    const response = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${stageIndex}/files`, {
                                        method: 'POST',
                                        headers: {
                                          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                        },
                                        body: formData
                                      });
                                    
                                    if (!response.ok) {
                                      throw new Error('Upload failed');
                                    }
                                    
                                    const data = await response.json();
                                    const fileMeta = data.file || data;
                                    const currentFiles = Array.isArray((task.fieldValues as any)?.rpDataFiles) ? (task.fieldValues as any).rpDataFiles : [];
                                    const updatedFiles = [...currentFiles, { fileId: fileMeta._id || fileMeta.fileId, fileName: fileMeta.fileName, fileType }];
                                    
                                    const patchResponse = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`, {
                                        method: 'PATCH',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                        },
                                      body: JSON.stringify({ fieldValues: { ...(task.fieldValues || {}), rpDataFiles: updatedFiles } })
                                      });
                                    
                                    if (patchResponse.ok) {
                                      showSuccess('RP data file uploaded');
                                        if (onTaskPatched) onTaskPatched();
                                      } else {
                                      showError('Failed to update file list');
                                      }
                                  } catch (err) {
                                      showError('Failed to upload RP data file');
                                    }
                                  // Reset input
                                  e.target.value = '';
                                }}
                              />
                                <Button
                                  variant="outlined"
                                  size="small"
                                onClick={() => {
                                  const input = document.getElementById(`rp-data-upload-${stageIndex}-${actualTaskIndex}`) as HTMLInputElement;
                                  input?.click();
                                }}
                              >
                                Upload File
                                </Button>
                            </Box>
                              {Array.isArray((task.fieldValues as any)?.rpDataFiles) && (task.fieldValues as any).rpDataFiles.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  {(task.fieldValues as any).rpDataFiles.map((f: any, idx: number) => (
                                    <Box key={f.fileId || idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <Button size="small" variant="text"
                                        onClick={async () => {
                                          const url = `/api/agencies/${agencySlug}/files/${f.fileId}`;
                                          try { await downloadFileAsBlob(url, f.fileName); } catch {}
                                        }}
                                    >
                                      {f.fileName}
                                      {f.fileType && (
                                        <Chip 
                                          label={f.fileType === 'valuation' ? 'Valuation' : 'CMA'} 
                                          size="small" 
                                          color={f.fileType === 'valuation' ? 'primary' : 'secondary'}
                                          sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                                        />
                                      )}
                                    </Button>
                                      <Button size="small" color="error" variant="text"
                                        onClick={async () => {
                                          try {
                                            // Delete stage file by id, then update fieldValues list
                                            const token = localStorage.getItem('token');
                                            await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${stageIndex}/files/${f.fileId}`, { method: 'DELETE', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } });
                                            const next = (task.fieldValues as any).rpDataFiles.filter((x: any) => x.fileId !== f.fileId);
                                            const response = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIndex}/${actualTaskIndex}`, {
                                              method: 'PATCH',
                                              headers: {
                                                'Content-Type': 'application/json',
                                                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                              },
                                              body: JSON.stringify({ fieldValues: { ...(task.fieldValues || {}), rpDataFiles: next } })
                                            });
                                            if (response.ok) { showSuccess('RP data file deleted'); if (onTaskPatched) onTaskPatched(); }
                                            else { showError('Failed to update list after delete'); }
                                          } catch {
                                            showError('Failed to delete RP data file');
                                          }
                                        }}
                                      >Delete</Button>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                          </Box>
                        )}
                        
                        {/* Continue with other task types... */}
                      </React.Fragment>
                    );
                  })}
                </List>
              </Box>
            )}
          </Step>
  ))}</Stepper>

      {/* Missing Files Warning Dialog */}
      <Dialog 
        open={showMissingFilesDialog}
        onClose={() => setShowMissingFilesDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Missing Required Files</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The following required files are missing:
          </Alert>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            {missingFilesList.map((file, idx) => (
              <Typography key={idx} component="li" variant="body2" sx={{ mb: 1 }}>
                {file}
              </Typography>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            The report generation will proceed, but the quality of the report may be reduced without these files.
            <br />
            <strong>Valuation files</strong> provide market statistics data.
            <br />
            <strong>CMA files</strong> provide comparable sales data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMissingFilesDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              setShowMissingFilesDialog(false);
              try {
                setReportLoading(true);
                setReportError(null);
                const token = localStorage.getItem('token');
                // Async trigger: do not block UI
                const response = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/consultation-report/async`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                  }
                });
                if (!response.ok) {
                  const errorData = await response.json();
                  throw new Error(errorData.error || 'Failed to start report generation');
                }
                showSuccess('Report generation started. You can continue working.');
                // Poll status until ready (fallback if socket not used)
                let attempts = 0;
                const maxAttempts = 40; // ~3.3 minutes at 5s interval
                const interval = setInterval(async () => {
                  try {
                    const st = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/consultation-report/status`, {
                      headers: {
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                      }
                    });
                    const js = await st.json();
                    if (js.status === 'ready' || js.status === 'draft' || js.status === 'finalized') {
                      clearInterval(interval);
                      showSuccess('Marketing appraisal is ready. Please review and confirm the data...');
                      
                      // Load structured data for confirmation dialog
                      setLoadingAppraisalInputs(true);
                      try {
                        const inputsResponse = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/inputs`);
                        const inputsData = inputsResponse.data;
                        
                        // Prepare initial inputs using the helper function
                        const initialInputs = prepareAppraisalInputs(
                          inputsData.property,
                          inputsData.agency,
                          inputsData.agent,
                          inputsData.extractedData,
                          { content: inputsData.reportContent }
                        );
                        
                        setAppraisalInputs(initialInputs);
                        setShowAppraisalConfirmDialog(true);
                      } catch (err: any) {
                        console.error('Error loading appraisal inputs:', err);
                        // If loading inputs fails, navigate directly to edit page
                        navigate(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/edit`);
                        if (onTaskPatched) onTaskPatched();
                      } finally {
                        setLoadingAppraisalInputs(false);
                      }
                    } else if (js.status === 'failed') {
                      clearInterval(interval);
                      showError(js.error || 'Report generation failed');
                    } else {
                      attempts += 1;
                      if (attempts >= maxAttempts) {
                        clearInterval(interval);
                      }
                  }
                } catch {}
                }, 5000);
              } catch (e: any) {
                console.error('Error generating report:', e);
                showError(e.message || 'Failed to generate report');
              } finally {
                setReportLoading(false);
              }
            }}
            variant="contained"
            color="primary"
          >
            Continue Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Agreement Generation Dialog */}
      <Dialog
        open={showAgreementGenerationDialog}
        onClose={() => setShowAgreementGenerationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generate Agency Agreement</DialogTitle>
        <DialogContent>
          {agreementEligibility && (
            <Alert 
              severity={agreementEligibility.canGenerate ? 'info' : 'warning'} 
              sx={{ mb: 2 }}
            >
              {agreementEligibility.message}
              {agreementEligibility.hasFreeGeneration && agreementEligibility.canGenerate && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Free generation available this month.
                </Typography>
              )}
              {!agreementEligibility.hasFreeGeneration && agreementEligibility.canGenerate && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Cost: ${(agreementEligibility.costCents / 100).toFixed(2)}
                </Typography>
              )}
              {(agreementEligibility.missingPrerequisites?.length ?? 0) > 0 && (
                <Box sx={{ mt: 1 }}>
                  {hasAdminAccess(user) && agreementEligibility.missingPrerequisites!.some((item) => item.location === 'settings') && (
                    <Button size="small" onClick={() => navigate(`/agencies/${agencySlug}/settings`)}>
                      Go to Settings
                    </Button>
                  )}
                  {agreementEligibility.missingPrerequisites!.some((item) => item.location === 'profile') && (
                    <Button size="small" onClick={() => navigate(`/agencies/${agencySlug}/profile`)}>
                      Go to Profile
                    </Button>
                  )}
                </Box>
              )}
            </Alert>
          )}
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Marketing Costs (pre-populated from workflow):
            </Typography>
            {agreementFormData.marketingCosts.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Cost</TableCell>
                      <TableCell>Payable</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agreementFormData.marketingCosts.map((cost, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{cost.item}</TableCell>
                        <TableCell align="right">
                          <NumericFormat
                            value={cost.cost}
                            displayType="text"
                            thousandSeparator
                            prefix="$"
                            decimalScale={2}
                            fixedDecimalScale
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={cost.payable || 'settlement'}
                            onChange={(e) => {
                              const value = e.target.value as 'upfront' | 'settlement';
                              const updated = [...agreementFormData.marketingCosts];
                              updated[idx] = { ...cost, payable: value };
                              setAgreementFormData({ ...agreementFormData, marketingCosts: updated });
                            }}
                            sx={{ minWidth: 120 }}
                          >
                            <MenuItem value="upfront">Upfront</MenuItem>
                            <MenuItem value="settlement">Settlement/Termination of Agency Agreement</MenuItem>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No marketing costs found in workflow.
              </Typography>
            )}
          </Box>

          <TextField
            label="Introduced Purchasers List Days"
            type="number"
            fullWidth
            value={agreementFormData.introducedPurchasersListDays}
            onChange={(e) => setAgreementFormData({
              ...agreementFormData,
              introducedPurchasersListDays: parseInt(e.target.value) || 14
            })}
            sx={{ mb: 2 }}
            helperText="Number of days after exclusive agency period ends to provide list of introduced purchasers"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAgreementGenerationDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const eligibility = agreementEligibility;
              if (!eligibility?.canGenerate) {
                showError(eligibility?.message || 'Cannot generate agreement. Please check your credits or eligibility.');
                return;
              }

              pendingAfterTermsAcceptRef.current = () => {
                if (!eligibility.hasFreeGeneration && eligibility.costCents > 0) {
                  setShowCreditConfirmationDialog(true);
                } else {
                  void generateAgreementRef.current();
                }
              };
              setShowAgreementGenerationDialog(false);
              setShowAgencyAgreementTermsDialog(true);
            }}
            variant="contained"
            disabled={!agreementEligibility?.canGenerate || generatingAgreement}
          >
            {generatingAgreement ? 'Generating...' : 'Generate Agreement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Agency Agreement Generation — Terms of Service */}
      <AgencyAgreementTermsDialog
        open={showAgencyAgreementTermsDialog}
        onClose={() => {
          setShowAgencyAgreementTermsDialog(false);
          pendingAfterTermsAcceptRef.current = null;
        }}
        onAccept={() => {
          const next = pendingAfterTermsAcceptRef.current;
          pendingAfterTermsAcceptRef.current = null;
          next?.();
        }}
      />

      {/* Credit Confirmation Dialog */}
      <Dialog
        open={showCreditConfirmationDialog}
        onClose={() => {
          setShowCreditConfirmationDialog(false);
          setPendingAgreementAction(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Credit Usage</DialogTitle>
        <DialogContent>
          <Typography>
            This will use ${agreementEligibility ? (agreementEligibility.costCents / 100).toFixed(2) : '0.00'} from your agency credits.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowCreditConfirmationDialog(false);
            setPendingAgreementAction(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowCreditConfirmationDialog(false);
              void generateAgreementRef.current();
            }}
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* No Vendor Response Confirmation Dialog */}
      <Dialog
        open={showNoResponseConfirmationDialog}
        onClose={() => {
          setShowNoResponseConfirmationDialog(false);
          setPendingNextStepTask(null);
        }}
      >
        <DialogTitle>Proceed Without Vendor Response?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            We haven't recorded a positive response from the vendor yet. 
            If you've already presented the findings in person and they've agreed to proceed, 
            you can continue to the next step.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowNoResponseConfirmationDialog(false);
              setPendingNextStepTask(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmNextStep}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm & Proceed'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success / Error snackbars (generation pattern: show prompt after generate/regenerate) */}
      <Snackbar
        open={successSnackbar.open}
        autoHideDuration={6000}
        onClose={handleSuccessSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSuccessSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {successSnackbar.message}
        </Alert>
      </Snackbar>
      <Snackbar
        open={errorSnackbar.open}
        autoHideDuration={6000}
        onClose={handleErrorSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleErrorSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {errorSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Agreement Review Dialog */}
      <Dialog
        open={showAgreementReviewDialog}
        onClose={() => {
          setShowAgreementReviewDialog(false);
          if (agreementReviewUrl) {
            URL.revokeObjectURL(agreementReviewUrl);
            setAgreementReviewUrl(null);
          }
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>Agency Agreement Preview</DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {agreementReviewUrl && (
            <iframe
              src={agreementReviewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                flex: 1
              }}
              title="Agency Agreement PDF"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (agreementReviewUrl) {
                const a = document.createElement('a');
                a.href = agreementReviewUrl;
                a.download = `agent-agreement-${propertyId}-${Date.now()}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
              }
            }}
            variant="outlined"
            startIcon={<Download />}
          >
            Download Again
          </Button>
          <Button
            onClick={() => {
              setShowAgreementReviewDialog(false);
              if (agreementReviewUrl) {
                URL.revokeObjectURL(agreementReviewUrl);
                setAgreementReviewUrl(null);
              }
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rental Management Agreement: HTML Preview */}
      <Dialog
        open={showRentalAgreementPreviewDialog}
        onClose={() => {
          setShowRentalAgreementPreviewDialog(false);
          setRentalAgreementPreviewHtml(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle>Rental Management Agreement (NSW) – Preview</DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {rentalAgreementPreviewHtml && (
            <iframe
              srcDoc={rentalAgreementPreviewHtml}
              style={{ width: '100%', height: '100%', border: 'none', flex: 1 }}
              title="Rental Management Agreement Preview"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowRentalAgreementPreviewDialog(false);
              setRentalAgreementPreviewHtml(null);
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rental Management Agreement: PDF */}
      <Dialog
        open={showRentalAgreementPdfDialog}
        onClose={() => {
          setShowRentalAgreementPdfDialog(false);
          if (rentalAgreementPdfUrl) {
            URL.revokeObjectURL(rentalAgreementPdfUrl);
            setRentalAgreementPdfUrl(null);
          }
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle>Rental Management Agreement (NSW) – PDF</DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {rentalAgreementPdfUrl && (
            <iframe
              src={rentalAgreementPdfUrl}
              style={{ width: '100%', height: '100%', border: 'none', flex: 1 }}
              title="Rental Management Agreement PDF"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (rentalAgreementPdfUrl) {
                const a = document.createElement('a');
                a.href = rentalAgreementPdfUrl;
                a.download = `rental-management-agreement-${propertyId}-${Date.now()}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
              }
            }}
            variant="outlined"
            startIcon={<Download />}
          >
            Download PDF
          </Button>
          <Button
            onClick={() => {
              setShowRentalAgreementPdfDialog(false);
              if (rentalAgreementPdfUrl) {
                URL.revokeObjectURL(rentalAgreementPdfUrl);
                setRentalAgreementPdfUrl(null);
              }
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rental Management Agreement Configuration Dialog */}
      <Dialog
        open={showRentalAgreementConfigDialog}
        onClose={() => setShowRentalAgreementConfigDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Configure Rental Management Agreement</DialogTitle>
        <DialogContent>
          {rentalAgreementEligibility && (
            <Alert
              severity={rentalAgreementEligibility.canGenerate ? 'info' : 'warning'}
              sx={{ mb: 2 }}
            >
              {rentalAgreementEligibility.message}
              {rentalAgreementEligibility.hasFreeGeneration && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Free generation available this month (1 per user per month).
                </Typography>
              )}
              {!rentalAgreementEligibility.hasFreeGeneration && rentalAgreementEligibility.canGenerate && rentalAgreementEligibility.costCents > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Cost: ${(rentalAgreementEligibility.costCents / 100).toFixed(2)}
                </Typography>
              )}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review and configure agreement options before generating the preview.
          </Typography>

          {/* Advertising Options */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Advertising & Marketing</Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rentalAgreementConfig.advertisingPortals}
                    onChange={(e) => setRentalAgreementConfig(prev => ({ ...prev, advertisingPortals: e.target.checked }))}
                  />
                }
                label="Online portals"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rentalAgreementConfig.advertisingPhotography}
                    onChange={(e) => setRentalAgreementConfig(prev => ({ ...prev, advertisingPhotography: e.target.checked }))}
                  />
                }
                label="Photography"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rentalAgreementConfig.advertisingSignboard}
                    onChange={(e) => setRentalAgreementConfig(prev => ({ ...prev, advertisingSignboard: e.target.checked }))}
                  />
                }
                label="Signboard"
              />
              <TextField
                label="Other advertising (e.g., inspection fees)"
                value={rentalAgreementConfig.advertisingOther || ''}
                onChange={(e) => setRentalAgreementConfig(prev => ({ ...prev, advertisingOther: e.target.value }))}
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                placeholder="Optional"
              />
            </FormGroup>
          </Box>

          {/* Repair Authorization */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Repairs & Maintenance</Typography>
            <NumericFormat
              label="Repair Authorization Limit ($)"
              value={rentalAgreementConfig.repairAuthLimit}
              allowNegative={false}
              prefix="$"
              thousandSeparator
              onValueChange={(values: { value: string }) => {
                setRentalAgreementConfig(prev => ({ ...prev, repairAuthLimit: Number(values.value) || 500 }));
              }}
              customInput={TextField}
              size="small"
              fullWidth
              helperText="Maximum amount per repair item without prior approval"
            />
          </Box>

          {/* Statements Frequency */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Statements</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Statement Frequency</InputLabel>
              <Select
                value={rentalAgreementConfig.statementsFrequency}
                onChange={(e) => setRentalAgreementConfig(prev => ({ ...prev, statementsFrequency: e.target.value as 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'other' }))}
                label="Statement Frequency"
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="biweekly">Biweekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="other">Other agreed intervals</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Disbursements */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Authorized Disbursements</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The agent is authorised to pay from rent collected:
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rentalAgreementConfig.disbursementsCouncil}
                    onChange={(e) => setRentalAgreementConfig(prev => ({ ...prev, disbursementsCouncil: e.target.checked }))}
                  />
                }
                label="Council rates"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rentalAgreementConfig.disbursementsWater}
                    onChange={(e) => setRentalAgreementConfig(prev => ({ ...prev, disbursementsWater: e.target.checked }))}
                  />
                }
                label="Water usage charges"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rentalAgreementConfig.disbursementsStrata}
                    onChange={(e) => setRentalAgreementConfig(prev => ({ ...prev, disbursementsStrata: e.target.checked }))}
                  />
                }
                label="Strata levies"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rentalAgreementConfig.disbursementsMaintenance}
                    onChange={(e) => setRentalAgreementConfig(prev => ({ ...prev, disbursementsMaintenance: e.target.checked }))}
                  />
                }
                label="Maintenance invoices"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rentalAgreementConfig.disbursementsOther}
                    onChange={(e) => setRentalAgreementConfig(prev => ({ ...prev, disbursementsOther: e.target.checked }))}
                  />
                }
                label="Other authorised expenses"
              />
            </FormGroup>
          </Box>

          {/* Termination Notice */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Termination</Typography>
            <NumericFormat
              label="Termination Notice (days)"
              value={rentalAgreementConfig.terminationNoticeDays}
              allowNegative={false}
              suffix=" days"
              onValueChange={(values: { value: string }) => {
                setRentalAgreementConfig(prev => ({ ...prev, terminationNoticeDays: Number(values.value) || 30 }));
              }}
              customInput={TextField}
              size="small"
              fullWidth
              helperText="Number of days notice required to terminate the agreement"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRentalAgreementConfigDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setShowRentalAgreementConfigDialog(false);
              try {
                setLoadingRentalAgreementPreview(true);
                // Pass config as query params
                const params = new URLSearchParams();
                Object.entries(rentalAgreementConfig).forEach(([key, value]) => {
                  if (value !== undefined && value !== null && value !== '') {
                    params.append(key, String(value));
                  }
                });
                const res = await api.get(
                  `/agencies/${agencySlug}/properties/${propertyId}/rental-management-agreement/preview?${params.toString()}`,
                  { responseType: 'text' }
                );
                setRentalAgreementPreviewHtml(res.data);
                setShowRentalAgreementPreviewDialog(true);
              } catch (err: any) {
                showError(err?.response?.data?.error || 'Failed to load preview');
              } finally {
                setLoadingRentalAgreementPreview(false);
              }
            }}
            sx={{ mr: 1 }}
          >
            Preview Agreement
          </Button>
          <Button
            variant="outlined"
            onClick={async () => {
              setShowRentalAgreementConfigDialog(false);
              const eligibility = await checkRentalAgreementEligibility();

              if (eligibility && !eligibility.canGenerate) {
                showError(eligibility.message || 'Insufficient credits to generate agreement');
                return;
              }

              pendingRentalAfterTermsAcceptRef.current = () => {
                if (eligibility && !eligibility.hasFreeGeneration && eligibility.costCents > 0) {
                  setShowRentalAgreementCreditDialog(true);
                } else {
                  void generateRentalAgreementRef.current();
                }
              };
              setShowRentalAgreementTermsDialog(true);
            }}
            disabled={generatingRentalAgreementPdf}
          >
            {generatingRentalAgreementPdf ? 'Generating...' : 'Generate & Save Agreement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rental Management Agreement — Terms of Service */}
      <AgencyAgreementTermsDialog
        open={showRentalAgreementTermsDialog}
        onClose={() => {
          setShowRentalAgreementTermsDialog(false);
          pendingRentalAfterTermsAcceptRef.current = null;
        }}
        onAccept={() => {
          const next = pendingRentalAfterTermsAcceptRef.current;
          pendingRentalAfterTermsAcceptRef.current = null;
          next?.();
        }}
      />

      {/* Rental Agreement Credit Confirmation Dialog */}
      <Dialog
        open={showRentalAgreementCreditDialog}
        onClose={() => {
          setShowRentalAgreementCreditDialog(false);
          setPendingRentalAgreementAction(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Credit Usage</DialogTitle>
        <DialogContent>
          <Typography>
            This will use ${rentalAgreementEligibility ? (rentalAgreementEligibility.costCents / 100).toFixed(2) : '0.00'} from your agency credits.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowRentalAgreementCreditDialog(false);
            setPendingRentalAgreementAction(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowRentalAgreementCreditDialog(false);
              void generateRentalAgreementRef.current();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Required information before generating agency agreement */}
      <Dialog
        open={showLicenseNumberDialog}
        onClose={() => setShowLicenseNumberDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agency agreement cannot be generated</DialogTitle>
        <DialogContent>
          {missingAgreementPrerequisites.length > 0 ? (
            <>
              <Typography sx={{ mb: 1.5 }}>
                The following information is required before an agency agreement can be generated:
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2.5 }}>
                {missingAgreementPrerequisites.map((item) => (
                  <li key={item.field}>
                    <Typography component="span">
                      {item.label}
                      {item.location === 'settings'
                        ? hasAdminAccess(user)
                          ? ' — add this in Settings'
                          : ' — ask an agency admin to add this in Settings'
                        : ' — add this in Profile'}
                    </Typography>
                  </li>
                ))}
              </Box>
            </>
          ) : (
            <Typography>
              Required agency or agent information is missing. Add the agency ABN and license number in Settings,
              and your license number and digital signature in Profile, then try again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLicenseNumberDialog(false)}>Cancel</Button>
          {missingAgreementPrerequisites.some((item) => item.location === 'settings') && hasAdminAccess(user) && (
            <Button
              variant={missingAgreementPrerequisites.every((item) => item.location === 'settings') ? 'contained' : 'outlined'}
              onClick={() => {
                setShowLicenseNumberDialog(false);
                navigate(`/agencies/${agencySlug}/settings`);
              }}
            >
              Go to Settings
            </Button>
          )}
          {(missingAgreementPrerequisites.some((item) => item.location === 'profile') ||
            missingAgreementPrerequisites.length === 0) && (
            <Button
              variant="contained"
              onClick={() => {
                setShowLicenseNumberDialog(false);
                navigate(`/agencies/${agencySlug}/profile`);
              }}
            >
              Go to Profile
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Send Agreement Email Dialog (agency = vendor, rental_management = principal) */}
      <Dialog
        open={showSendAgreementDialog}
        onClose={() => {
          setShowSendAgreementDialog(false);
          setSendAgreementType(null);
          setAgreementEmail(ownerEmail || '');
          setSelectedVendorEmails(new Set());
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {sendAgreementType === 'rental_management' ? 'Send Agreement to Principal' : 'Send Agreement to Vendor'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {sendAgreementType === 'rental_management'
              ? 'Select the principal(s) to send the rental management agreement for review and signing.'
              : 'Select the vendor(s) to send the agency agreement for review and signing.'}
          </Typography>
          
          {(() => {
            // Extract all vendors from property
            const normalizeOwners = (owner: any): Array<{ name: string; email?: string }> => {
              if (!owner) return [];
              if (Array.isArray(owner)) {
                return owner.filter(o => o && o.name).map(o => ({ name: o.name, email: o.email }));
              }
              if (owner.name) {
                return [{ name: owner.name, email: owner.email }];
              }
              return [];
            };
            
            const allVendors = normalizeOwners(property?.owner);
            
            if (allVendors.length === 0) {
              const isRental = sendAgreementType === 'rental_management';
              return (
                <>
                  <TextField
                    label={isRental ? 'Principal Email' : 'Vendor Email'}
                    type="email"
                    fullWidth
                    value={agreementEmail}
                    onChange={(e) => setAgreementEmail(e.target.value)}
                    placeholder={isRental ? 'principal@example.com' : 'vendor@example.com'}
                    sx={{ mb: 2 }}
                    helperText={ownerEmail ? 'Pre-filled from property owner email' : (isRental ? 'Enter principal email address' : 'Enter vendor email address')}
                  />
                </>
              );
            }
            
            const vendorsWithEmail = allVendors.filter(v => v.email && v.email.includes('@'));
            const vendorsWithoutEmail = allVendors.filter(v => !v.email || !v.email.includes('@'));
            
            return (
              <>
                {vendorsWithEmail.length > 0 && (
                  <FormGroup sx={{ mb: 2 }}>
                    {vendorsWithEmail.map((vendor, idx) => (
                      <FormControlLabel
                        key={idx}
                        control={
                          <Checkbox
                            checked={selectedVendorEmails.has(vendor.email!)}
                            onChange={(e) => {
                              const newSelected = new Set(selectedVendorEmails);
                              if (e.target.checked) {
                                newSelected.add(vendor.email!);
                              } else {
                                newSelected.delete(vendor.email!);
                              }
                              setSelectedVendorEmails(newSelected);
                            }}
                          />
                        }
                        label={`${vendor.name} (${vendor.email})`}
                      />
                    ))}
                  </FormGroup>
                )}
                
                {vendorsWithoutEmail.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {sendAgreementType === 'rental_management'
                      ? `The following principal(s) don't have email addresses: ${vendorsWithoutEmail.map(v => v.name).join(', ')}. Please add email addresses to send them the agreement.`
                      : `The following vendor(s) don't have email addresses: ${vendorsWithoutEmail.map(v => v.name).join(', ')}. Please add email addresses to send them the agreement.`}
                  </Alert>
                )}
                
                {vendorsWithEmail.length === 0 && (
                  <TextField
                    label={sendAgreementType === 'rental_management' ? 'Principal Email' : 'Vendor Email'}
                    type="email"
                    fullWidth
                    value={agreementEmail}
                    onChange={(e) => setAgreementEmail(e.target.value)}
                    placeholder={sendAgreementType === 'rental_management' ? 'principal@example.com' : 'vendor@example.com'}
                    sx={{ mb: 2 }}
                    helperText={sendAgreementType === 'rental_management' ? 'Enter principal email address' : 'Enter vendor email address'}
                  />
                )}
              </>
            );
          })()}
          
          <Alert severity="info" sx={{ mt: 2 }}>
            {sendAgreementType === 'rental_management'
              ? 'Selected principal(s) will receive an email with a link to view, download, and sign the agreement.'
              : 'Selected vendor(s) will receive an email with a link to view, download, and sign the agreement.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowSendAgreementDialog(false);
            setSendAgreementType(null);
            setAgreementEmail(ownerEmail || '');
            setSelectedVendorEmails(new Set());
          }}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              // Determine which emails to send to
              let emailsToSend: string[] = [];
              
              if (selectedVendorEmails.size > 0) {
                emailsToSend = Array.from(selectedVendorEmails);
              } else if (agreementEmail && agreementEmail.trim()) {
                emailsToSend = [agreementEmail.trim()];
              } else {
                showError('Please select at least one vendor or enter an email address');
                return;
              }

              try {
                setSendingAgreementEmail(true);
                
                // Rental management agreement uses a different endpoint than sales agency agreement
                const sendEndpoint = sendAgreementType === 'rental_management'
                  ? `/agencies/${agencySlug}/properties/${propertyId}/rental-management-agreement/send-email`
                  : `/agencies/${agencySlug}/properties/${propertyId}/agency-agreement/send-email`;
                const sendPromises = emailsToSend.map(email =>
                  api.post(sendEndpoint, { email }).catch(err => {
                    console.error(`Failed to send to ${email}:`, err);
                    return { error: err.response?.data?.error || 'Failed to send', email };
                  })
                );
                
                const results = await Promise.allSettled(sendPromises);
                const successful = results.filter(r => r.status === 'fulfilled' && !(r.value as { error?: string })?.error).length;
                const failed = results.length - successful;
                
                if (successful > 0) {
                  const recipientLabel = sendAgreementType === 'rental_management' ? 'principal(s)' : 'vendor(s)';
                  showSuccess(`Agreement sent successfully to ${successful} ${recipientLabel}${failed > 0 ? ` (${failed} failed)` : ''}`);
                  setShowSendAgreementDialog(false);
                  setSendAgreementType(null);
                  setAgreementEmail(ownerEmail || '');
                  setSelectedVendorEmails(new Set());
                } else {
                  showError(`Failed to send agreement. Please try again.`);
                }
              } catch (err: any) {
                console.error('Error sending agreement email:', err);
                showError(err.response?.data?.error || 'Failed to send agreement email');
              } finally {
                setSendingAgreementEmail(false);
              }
            }}
            variant="contained"
            disabled={(selectedVendorEmails.size === 0 && (!agreementEmail || !agreementEmail.trim())) || sendingAgreementEmail}
          >
            {sendingAgreementEmail ? 'Sending...' : `Send Agreement${selectedVendorEmails.size > 1 ? ` (${selectedVendorEmails.size})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Skip Stage and other actions */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmDialogConfig?.title || 'Confirm Action'}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {confirmDialogConfig?.message || 'Are you sure you want to proceed?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDialogConfirm}
            variant="contained"
            color={confirmDialogConfig?.confirmColor || 'primary'}
          >
            {confirmDialogConfig?.confirmText || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Task Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Task</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {assignTaskName ? `Task: ${assignTaskName}` : 'Set assignee and due date for this task.'}
          </Typography>
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Assignee</InputLabel>
            <Select
              value={assignAssignee}
              label="Assignee"
              onChange={(e) => setAssignAssignee(e.target.value)}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {agencyUsers.map((u: any) => (
                <MenuItem key={u._id} value={u._id}>
                  {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u._id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Due date"
            type="date"
            value={assignDueDate}
            onChange={(e) => setAssignDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Due time"
            type="time"
            value={assignDueTime}
            onChange={(e) => setAssignDueTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (assignStageIdx == null || assignTaskIdx == null) return;
              const dueDate = assignDueDate
                ? `${assignDueDate}T${(assignDueTime || '00:00').slice(0, 5)}:00.000Z`
                : undefined;
              await patchTaskAssignment(assignStageIdx, assignTaskIdx, {
                assignedTo: assignAssignee || undefined,
                dueDate,
                name: assignTaskName || undefined,
              });
              setAssignDialogOpen(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set reminder dialog */}
      {currentTaskForReminder != null && agencySlug && workflowId && propertyId && (
        <TaskReminderDialog
          open={reminderDialogOpen}
          onClose={() => {
            setReminderDialogOpen(false);
            setCurrentTaskForReminder(null);
          }}
          agencySlug={agencySlug}
          workflowId={workflowId}
          propertyId={propertyId}
          stageIndex={currentTaskForReminder.stageIndex}
          taskIndex={currentTaskForReminder.taskIndex}
          taskName={currentTaskForReminder.taskName}
          taskDescription={currentTaskForReminder.taskDescription}
          existingReminder={getTaskReminder(currentTaskForReminder.stageIndex, currentTaskForReminder.taskIndex)}
          onSuccess={() => {
            handleReminderSuccess();
            setReminderDialogOpen(false);
            setCurrentTaskForReminder(null);
          }}
        />
      )}

      {/* Market Appraisal Confirmation Dialog */}
      {appraisalInputs && (
        <MarketAppraisalConfirmDialog
          open={showAppraisalConfirmDialog}
          onClose={() => {
            setShowAppraisalConfirmDialog(false);
            setAppraisalInputs(null);
          }}
          onSave={async (confirmedInputs: MarketAppraisalInputs) => {
            try {
              await api.put(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report`, {
                structuredInputs: confirmedInputs
              });
              showSuccess('Market appraisal inputs saved successfully.');
            } catch (err: any) {
              console.error('Error saving structured inputs:', err);
              showError('Failed to save structured inputs');
              throw err;
            }
          }}
          onConfirm={async (confirmedInputs: MarketAppraisalInputs) => {
            setShowAppraisalConfirmDialog(false);
            setAppraisalInputs(null);
            
            try {
              await api.post(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/generate-pdf`, {
                structuredInputs: confirmedInputs
              });
              showSuccess('PDF generated successfully.');
              // Mark "Prepare Marketing Appraisal" task as completed
              if (workflowId && Array.isArray(stages)) {
                let stageIdx = -1;
                let taskIdx = -1;
                for (let s = 0; s < stages.length; s++) {
                  const tasks = stages[s]?.tasks || [];
                  const found = tasks.findIndex((t: any) =>
                    t?.taskType === 'consultation_report' || t?.name === 'Prepare Marketing Appraisal' || t?.name === 'Consultation Report'
                  );
                  if (found >= 0) {
                    stageIdx = s;
                    taskIdx = found;
                    break;
                  }
                }
                if (stageIdx >= 0 && taskIdx >= 0) {
                  try {
                    await api.patch(
                      `/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/tasks/${stageIdx}/${taskIdx}`,
                      { status: 'completed' }
                    );
                  } catch (patchErr: any) {
                    console.warn('Could not mark Prepare Marketing Appraisal task completed:', patchErr?.message || patchErr);
                  }
                }
              }
              if (onTaskPatched) onTaskPatched();
            } catch (err: any) {
              console.error('Error generating PDF:', err);
              showError(err.response?.data?.error || 'Failed to generate PDF');
              if (onTaskPatched) onTaskPatched();
            }
          }}
          initialInputs={appraisalInputs}
          loading={loadingAppraisalInputs}
          agencySlug={agencySlug}
          propertyId={propertyId}
        />
      )}
    </Paper>
  );
};

export default PropertyWorkflow; 